import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Link, useParams, useLocation } from "wouter";
import { dateMaisDias } from "../components/form/DueDateField.js";
import { CreditNoteModal } from "../components/domain/CreditNoteModal.js";
import { CreditNoteDocButtons } from "../components/domain/CreditNoteDocButtons.js";
import { InvoiceDraftModal } from "../components/domain/InvoiceDraftModal.js";
import { VehicleHistoryModal } from "../components/domain/VehicleHistoryModal.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { CarLogo } from "../components/common/CarLogo.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { useT, type MessageKey } from "../i18n/index.js";
import { trDepose, trMethod, trNote, trPanel } from "../lib/devis-i18n.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { abrirArquivo, useAbaPendente } from "../lib/open-file.js";
import { hasOnlyMenuGrantAccess } from "@eurostorm/shared";
import { useGrantOnly } from "../hooks/useGrantOnly.js";

/** dd/mm/aaaa (mesmo formato em PT/FR). */
function dataCurta(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

/**
 * F-ficha-carro (auditoria UX 23/10/2026, item 4; CEO 24/10 "pode seguir"):
 * 8 abas viraram 4, por pergunta do usuário — Resumo · Serviços e técnicos ·
 * Faturação e pagamentos (faturas do cliente, recebimentos, faturas e
 * pagamentos do técnico, intercompany, empilhados) · Histórico e documentos.
 * Em cima, a linha do tempo do carro com UMA ação principal (o próximo passo).
 */
type TabKey = "resumo" | "servicos" | "financeiro" | "historico";

/** Labels das abas — função porque depende de `t` (i18n). */
function buildTabs(tr: ReturnType<typeof useT>): { key: TabKey; label: string }[] {
  return [
    { key: "resumo", label: tr("dossierDetail.tabResumo") },
    { key: "servicos", label: tr("dossierDetail.tabServicos") },
    { key: "financeiro", label: tr("dossierDetail.tabFinanceiro") },
    { key: "historico", label: tr("dossierDetail.tabHistorico") },
  ];
}

const CUSTOMER_INVOICE_TONE: Record<string, string> = {
  pending: "bg-neutral-700/40 text-neutral-300",
  issued: "bg-sky-500/15 text-sky-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
  void: "bg-neutral-700/40 text-neutral-500",
};
function customerInvoiceLabels(tr: ReturnType<typeof useT>): Record<string, string> {
  return {
    pending: tr("dossierDetail.statusPending"),
    issued: tr("dossierDetail.statusIssued"),
    // CEO 2026-09-14: fatura de CLIENTE quitada = "recebida" (cliente nos
    // pagou); "paga" fica para técnico/fornecedor/intercompany.
    paid: tr("dossierDetail.statusCustomerReceived"),
    cancelled: tr("dossierDetail.statusCancelled"),
    void: tr("dossierDetail.statusVoid"),
  };
}

const TECHNICIAN_INVOICE_TONE: Record<string, string> = {
  draft: "bg-neutral-700/40 text-neutral-300",
  received: "bg-sky-500/15 text-sky-300",
  approved: "bg-cyan-500/15 text-cyan-300",
  rejected: "bg-amber-500/15 text-amber-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
};
function technicianInvoiceLabels(tr: ReturnType<typeof useT>): Record<string, string> {
  return {
    draft: tr("dossierDetail.statusDraft"),
    received: tr("dossierDetail.statusReceived"),
    approved: tr("dossierDetail.statusApproved"),
    rejected: tr("dossierDetail.statusRejected"),
    paid: tr("dossierDetail.statusPaid"),
    cancelled: tr("dossierDetail.statusCancelled"),
  };
}

const IC_INVOICE_TONE: Record<string, string> = {
  draft: "bg-neutral-700/40 text-neutral-300",
  issued: "bg-sky-500/15 text-sky-300",
  approved: "bg-cyan-500/15 text-cyan-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
  reversed: "bg-amber-500/15 text-amber-300",
};
function icInvoiceLabels(tr: ReturnType<typeof useT>): Record<string, string> {
  return {
    draft: tr("dossierDetail.statusDraft"),
    issued: tr("dossierDetail.statusIssued"),
    approved: tr("dossierDetail.statusApproved"),
    paid: tr("dossierDetail.statusPaid"),
    cancelled: tr("dossierDetail.statusCancelled"),
    reversed: tr("dossierDetail.statusReversed"),
  };
}

export default function DossierDetail() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const tr = useT();
  const dossierId = Number(params.id);

  const [tab, setTab] = useState<TabKey>("resumo");
  // F-ficha-carro — ação principal da linha do tempo leva à aba/seção certa;
  // "Trocar cliente" sai do Resumo e vai para o menu "⋯ Mais" do cabeçalho.
  const canEditPage = useCanEdit();
  const [trocarCliente, setTrocarCliente] = useState(false);
  // F-troca-cliente-devis (CEO 2026-10-30) — proposta vinda do devis reenviado.
  const [trocarInicial, setTrocarInicial] = useState<number | null>(null);
  const utils = trpc.useUtils();
  const dismissPendingMut = trpc.dossiers.dismissPendingCustomer.useMutation({
    onSuccess: () => void utils.dossiers.invalidate(),
  });
  // CEO 2026-10-27 — "👷 Lancer le dégarnissage" no aviso amarelo e na aba.
  const [lancarDegarn, setLancarDegarn] = useState(false);
  const [autoIssue, setAutoIssue] = useState(false);
  const irPara = (alvo: TabKey, anchor?: string, abrirFatura = false) => {
    setTab(alvo);
    setAutoIssue(abrirFatura);
    if (anchor) {
      setTimeout(() => {
        document.getElementById(anchor)?.scrollIntoView({ block: "start", behavior: "smooth" });
      }, 80);
    }
  };

  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  // Tenta inferir companyId: primeiro através do dossier (mas precisa companyId para query).
  // Estratégia: para cada empresa acessível, tenta getById. A primeira que retornar é a correta.
  // No app o user navega da lista, então cache provavelmente já tem.
  const [companyId, setCompanyId] = useState<number | null>(null);
  const candidateCompanies = useMemo(
    () => (companyId !== null ? [companyId] : accessibleCompanies.map((c) => c.id)),
    [companyId, accessibleCompanies],
  );

  const probeQueries = trpc.useQueries((t) =>
    candidateCompanies.map((cid) =>
      t.dossiers.getById(
        { companyId: cid, dossierId },
        { retry: false, refetchOnMount: false, enabled: !Number.isNaN(dossierId) },
      ),
    ),
  );

  const probeIndex = probeQueries.findIndex((q) => q.data != null);
  const resolvedCompanyId: number | null =
    probeIndex >= 0 ? (candidateCompanies[probeIndex] ?? null) : companyId;
  const dossierQuery = probeIndex >= 0 ? (probeQueries[probeIndex] ?? null) : null;
  const dossier = dossierQuery?.data ?? null;
  const allLoading = probeQueries.length > 0 && probeQueries.every((q) => q.isLoading);
  const allFailed = probeQueries.length > 0 && probeQueries.every((q) => q.error != null);
  // F-lista-carros — a ação da lista chega como /dossiers/:id?acao=faturar|receber|repassar|tecnico
  const temDossier = dossier != null;
  // CEO 2026-10-26 (GK-294-GV) — ficha do veículo: todos os dossiers do carro.
  const [fichaVeiculo, setFichaVeiculo] = useState(false);
  const veiculoQuery = trpc.vehicles.history.useQuery(
    { companyId: dossier?.companyId ?? 0, vehicleId: dossier?.vehicleId ?? 0 },
    { enabled: dossier?.vehicleId != null },
  );
  // CEO 2026-10-28 (EG-828-EM) — rascunho de fatura em espera: faixa no topo,
  // senão fica escondido na aba "Facturation et paiements".
  const rascunhoTopoQ = trpc.invoiceDrafts.forDossier.useQuery(
    { companyId: dossier?.companyId ?? 0, dossierId },
    { enabled: temDossier && canEditPage },
  );
  useEffect(() => {
    if (!temDossier) return;
    const acao = new URLSearchParams(window.location.search).get("acao");
    if (!acao) return;
    const alvo: [TabKey, string, boolean] | null =
      acao === "faturar"
        ? ["financeiro", "sec-faturas", true]
        : acao === "receber"
          ? ["financeiro", "sec-recebimentos", false]
          : acao === "repassar"
            ? ["financeiro", "sec-tec-pagamentos", false]
            : acao === "tecnico"
              ? ["resumo", "sec-devis", false]
              : null;
    if (alvo) setTimeout(() => irPara(alvo[0], alvo[1], alvo[2]), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temDossier]);

  // Quando descobrimos a empresa, fixamos para evitar re-queries em todas.
  // setState durante render é OK em React quando o valor está mudando — só
  // dispara update se mudou. Evita loop porque companyId === null vira false.
  if (resolvedCompanyId !== null && companyId === null) {
    setCompanyId(resolvedCompanyId);
  }

  if (Number.isNaN(dossierId)) {
    return (
      <Shell>
        <p className="text-sm text-red-400">{tr("dossierDetail.invalidId")}</p>
      </Shell>
    );
  }

  if (!user) return null;

  if (allLoading) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{tr("dossierDetail.loading")}</p>
      </Shell>
    );
  }
  if (allFailed || !dossier || resolvedCompanyId == null) {
    return (
      <Shell>
        <p className="text-sm text-red-400">{tr("dossierDetail.notFound")}</p>
        <Link href="/dossiers" className="text-sm text-lime hover:text-lime">
          {tr("dossierDetail.backToList")}
        </Link>
      </Shell>
    );
  }

  // Narrow para `number` (após guard).
  const cid: number = resolvedCompanyId;
  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === cid && m.active && m.role === "admin");
  const companyById = new Map((companiesQuery.data ?? []).map((c) => [c.id, c]));
  // F-acesso-por-menu — gerente de chantier: sem abas de dinheiro do técnico,
  // intercompany e auditoria (o servidor já não devolve comissão).
  const grantOnly = hasOnlyMenuGrantAccess(user);
  const TABS = buildTabs(tr).filter((tb) => !grantOnly || tb.key !== "historico");

  return (
    <Shell>
      <DossierHeader
        dossier={dossier}
        companyName={companyById.get(dossier.companyId)?.name}
        mostrarTrocarCliente={isAdmin && canEditPage && !grantOnly}
        onTrocarCliente={() => setTrocarCliente(true)}
        veiculoDossiers={
          dossier.vehicleId != null ? (veiculoQuery.data?.dossiers.length ?? null) : null
        }
        onAbrirVeiculo={() => setFichaVeiculo(true)}
      />
      {fichaVeiculo && dossier.vehicleId != null && (
        <VehicleHistoryModal
          companyId={dossier.companyId}
          vehicleId={dossier.vehicleId}
          currentDossierId={dossier.id}
          onClose={() => setFichaVeiculo(false)}
        />
      )}
      {trocarCliente && (
        <ChangeCustomerModal
          companyId={cid}
          dossierId={dossier.id}
          currentCustomerId={dossier.customerId}
          currentCustomerName={dossier.customerDisplayName ?? `#${dossier.customerId}`}
          initialCustomerId={trocarInicial}
          onClose={() => {
            setTrocarCliente(false);
            setTrocarInicial(null);
          }}
        />
      )}
      <LinhaDoTempo
        dossier={dossier}
        companyId={cid}
        grantOnly={grantOnly}
        canEdit={canEditPage}
        onIr={irPara}
        valorFixo={
          isAdmin &&
          canEditPage &&
          !grantOnly &&
          dossier.devisBreakdown != null &&
          !dossier.deletedAt
            ? {
                tem:
                  dossier.technicianFixedDspHt != null || dossier.technicianFixedMontageHt != null,
                // CEO 2026-10-26 — atalho do topo: leva ao card do Résumé e já abre o formulário.
                onClick: () => {
                  irPara("resumo", "sec-valor-fixo");
                  setTimeout(() => window.dispatchEvent(new Event(ABRIR_VALOR_FIXO)), 120);
                },
              }
            : null
        }
      />
      {rascunhoTopoQ.data && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2.5 text-sm"
          style={{ background: "#FEE2E2", borderColor: "#DC2626", color: "#7F1D1D" }}
          data-testid="dossier-draft-banner"
        >
          <span>
            📝 <strong>{tr("dossierDetail.draftBannerPre")}</strong>
            {rascunhoTopoQ.data.replacesNumber
              ? ` · ${tr("dossierDetail.draftBannerReplaces")} ${rascunhoTopoQ.data.replacesNumber}`
              : ""}
          </span>
          <button
            type="button"
            onClick={() => irPara("financeiro", "sec-faturas", true)}
            className="rounded-md border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: "#DC2626", background: "#FFFFFF", color: "#7F1D1D" }}
            data-testid="dossier-draft-banner-open"
          >
            {tr("dossierDetail.draftBannerOpen")}
          </button>
        </div>
      )}
      {/* F-troca-cliente-devis (CEO 2026-10-30, GZ-398-MX) — devis reenviado com
          OUTRO cliente: o escritório confirma a troca (modal já preenchido) ou
          mantém o atual. Só Administrador. */}
      {dossier.pendingCustomerId != null && isAdmin && canEditPage && !grantOnly && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2.5 text-sm"
          style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
          data-testid="dossier-pending-customer-banner"
        >
          <span>
            🔁 <strong>{tr("dossierDetail.pendingCustomerPre")}</strong>{" "}
            <strong>{dossier.pendingCustomerName ?? `#${dossier.pendingCustomerId}`}</strong>
            {" · "}
            {tr("dossierDetail.pendingCustomerNow")}{" "}
            {dossier.customerDisplayName ?? `#${dossier.customerId}`}
          </span>
          <span className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setTrocarInicial(dossier.pendingCustomerId);
                setTrocarCliente(true);
              }}
              className="rounded-md border px-3 py-1 text-xs font-semibold"
              style={{ borderColor: "#D97706", background: "#FFFFFF", color: "#78350F" }}
              data-testid="pending-customer-switch"
            >
              {tr("dossierDetail.pendingCustomerSwitch")} {dossier.pendingCustomerName ?? ""}
            </button>
            <button
              type="button"
              disabled={dismissPendingMut.isPending}
              onClick={() => dismissPendingMut.mutate({ companyId: cid, dossierId: dossier.id })}
              className="rounded-md border px-3 py-1 text-xs"
              style={{ borderColor: "#D97706", color: "#78350F" }}
              data-testid="pending-customer-keep"
            >
              {tr("dossierDetail.pendingCustomerKeep")} {dossier.customerDisplayName ?? ""}
            </button>
          </span>
        </div>
      )}
      {/* CEO 2026-10-13 — PDR finalizado = reparado; o dégarnissage do devis
          sem registro fica fora da fatura e do pagamento do técnico. */}
      {dossier.degarnissage?.pending && (
        <div
          role="alert"
          className="rounded-lg border px-4 py-2.5 text-sm"
          style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
          data-testid="dossier-degarn-alert"
        >
          <p className="font-bold">
            ⚠ {tr("dossierDetail.degarnAlertTitle")} ·{" "}
            {formatMoney(dossier.degarnissage.ht ?? 0, { decimals: 2 })} HT
          </p>
          <p className="text-xs">
            {dossier.degarnissage.semLinha
              ? tr("dossierDetail.degarnAlertHint")
              : tr("dossierDetail.degarnAlertOpenHint")}
          </p>
          {canEditPage && !dossier.deletedAt && (
            <button
              type="button"
              onClick={() => setLancarDegarn(true)}
              className="mt-2 rounded-md border px-3 py-1 text-xs font-semibold"
              style={{ borderColor: "#D97706", background: "#FFFFFF", color: "#78350F" }}
              data-testid="degarn-launch-alert"
            >
              {tr("dossierDetail.degarnLaunchBtn")}
            </button>
          )}
        </div>
      )}
      {lancarDegarn && (
        <LaunchTechnicianModal
          companyId={cid}
          dossierId={dossier.id}
          temPdr={false}
          temMontagem
          titulo={tr("dossierDetail.degarnLaunchTitle")}
          onClose={() => setLancarDegarn(false)}
        />
      )}

      {/* Celular: abas numa faixa que rola de lado (antes quebravam em 5 linhas). */}
      <nav
        className="flex gap-1 overflow-x-auto rounded-lg border border-neutral-800 bg-neutral-900 px-2 py-1 sm:flex-wrap"
        data-testid="dossier-tabs"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
              tab === t.key
                ? "bg-lime/20 text-lime border border-lime/40"
                : "hover:bg-neutral-800 text-neutral-400 border border-transparent"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "resumo" && (
        <TabResumo
          dossier={dossier}
          companyById={companyById}
          companyId={cid}
          dossierId={dossier.id}
          isAdmin={isAdmin}
        />
      )}
      {tab === "servicos" && dossier.degarnissage?.pending && canEditPage && !dossier.deletedAt && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2 text-xs"
          style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
          data-testid="degarn-launch-tab"
        >
          <span>{tr("dossierDetail.degarnLaunchTabHint")}</span>
          <button
            type="button"
            onClick={() => setLancarDegarn(true)}
            className="rounded-md border px-3 py-1 font-semibold"
            style={{ borderColor: "#D97706", background: "#FFFFFF", color: "#78350F" }}
          >
            {tr("dossierDetail.degarnLaunchBtn")}
          </button>
        </div>
      )}
      {tab === "servicos" && (
        <TabLinhas
          companyId={cid}
          dossierId={dossier.id}
          dossierDeleted={dossier.deletedAt != null}
          isMirrorDossier={dossier.source === "proforma_mirror"}
          dossierSource={dossier.source}
          dossierInvoiceDate={dossier.invoiceDate}
          accessibleCompanies={accessibleCompanies}
          recepcao={(() => {
            const rec = dossier.chantierChecks.find((c) => c.kind === "reception");
            return rec
              ? {
                  franchise: rec.franchise,
                  amountTtc: rec.franchiseAmount != null ? Number(rec.franchiseAmount) : null,
                }
              : null;
          })()}
        />
      )}
      {tab === "financeiro" && (
        <div className="space-y-6">
          <div id="sec-faturas">
            <TabFaturacao
              companyId={cid}
              dossierId={dossier.id}
              isAdmin={isAdmin}
              dossierDeleted={dossier.deletedAt != null}
              autoOpenIssue={autoIssue}
              onAutoOpenConsumed={() => setAutoIssue(false)}
            />
          </div>
          <div id="sec-recebimentos" className="border-t border-neutral-800 pt-6">
            <TabRecebimentos companyId={cid} dossierId={dossier.id} isAdmin={isAdmin} />
          </div>
          {!grantOnly && (
            <>
              <div id="sec-tec-faturas" className="border-t border-neutral-800 pt-6">
                <TabTecnicoFaturas
                  companyId={cid}
                  dossierId={dossier.id}
                  companyById={companyById}
                />
              </div>
              <div id="sec-tec-pagamentos" className="border-t border-neutral-800 pt-6">
                <TabTecnicoPagamentos companyId={cid} dossierId={dossier.id} isAdmin={isAdmin} />
              </div>
              <div id="sec-intercompany" className="border-t border-neutral-800 pt-6">
                <TabIntercompany
                  companyId={cid}
                  dossierId={dossier.id}
                  billingCompanyId={dossier.billingCompanyId}
                  accessibleCompanies={accessibleCompanies}
                  isAdmin={isAdmin}
                />
              </div>
            </>
          )}
        </div>
      )}
      {tab === "historico" && !grantOnly && (
        <TabAudit companyId={cid} dossierId={dossier.id} isAdmin={isAdmin} />
      )}
    </Shell>
  );
}

// ============================================================================
// HEADER
// ============================================================================

function DossierHeader({
  dossier,
  companyName,
  mostrarTrocarCliente,
  onTrocarCliente,
  veiculoDossiers,
  onAbrirVeiculo,
}: {
  dossier: {
    dossierCode: string;
    invoiceNumberExternal: string | null;
    deletedAt: Date | null;
    companyId: number;
    plate: string | null;
    vin: string | null;
    vehicleMakeSnapshot: string | null;
    vehicleModelSnapshot: string | null;
    customerDisplayName: string | null;
    endCustomerName: string | null;
    atelierName?: string | null;
    atelierCity?: string | null;
  };
  companyName: string | undefined;
  /** F-ficha-carro — "⋯ Mais" com as ações raras (só admin que pode editar). */
  mostrarTrocarCliente: boolean;
  onTrocarCliente: () => void;
  /** CEO 2026-10-26 — nº de dossiers do veículo (null = sem veículo ligado). */
  veiculoDossiers: number | null;
  onAbrirVeiculo: () => void;
}) {
  const t = useT();
  const [maisAberto, setMaisAberto] = useState(false);
  const identificacao = dossier.plate ?? dossier.vin ?? null;
  const marcaModelo = [dossier.vehicleMakeSnapshot, dossier.vehicleModelSnapshot]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-neutral-800 bg-neutral-900 p-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          {identificacao && (
            <span
              className="whitespace-nowrap font-mono text-2xl font-bold tracking-wide text-neutral-100"
              data-testid="dossier-plate"
            >
              {identificacao}
            </span>
          )}
          {marcaModelo && <span className="text-lg text-neutral-200">{marcaModelo}</span>}
          {/* CEO 2026-10-26 (GK-294-GV) — o veículo tem os SEUS dossiers, como
              no eDSP: a ficha do veículo lista todos (o carro que volta ganha
              dossier novo). Em destaque quando há outros além deste. */}
          {veiculoDossiers != null && (
            <button
              type="button"
              onClick={onAbrirVeiculo}
              title={veiculoDossiers > 1 ? t("dossierDetail.vehicleOthersHint") : undefined}
              className={
                veiculoDossiers > 1
                  ? "badge b-warn cursor-pointer text-xs font-semibold hover:opacity-80"
                  : "inline-flex items-center gap-1 rounded-md border border-neutral-700 px-2 py-0.5 text-xs text-neutral-400 transition-colors hover:border-lime hover:text-lime"
              }
              data-testid="dossier-vehicle-history"
            >
              🚗 {t("dossierDetail.vehicleHistoryBtn")}
              {veiculoDossiers > 1 && <span className="font-semibold">· {veiculoDossiers}</span>}
            </button>
          )}
          {dossier.deletedAt != null && (
            <span className="rounded bg-red-500/15 px-2 py-0.5 text-xs text-red-300">
              {t("dossierDetail.softDeletedBadge")}
            </span>
          )}
        </div>
        <p className="mt-1 break-words text-xs text-neutral-500">
          {dossier.customerDisplayName && (
            <>
              <span className="text-neutral-300">{dossier.customerDisplayName}</span>
              {dossier.endCustomerName && ` (${dossier.endCustomerName})`}
              {" · "}
            </>
          )}
          {companyName ?? `#${dossier.companyId}`}
          {dossier.atelierName && <> · 🏭 {dossier.atelierName}</>}
          {dossier.invoiceNumberExternal && (
            <>
              {" · "}
              <span className="font-mono">{dossier.invoiceNumberExternal}</span>
            </>
          )}
          {" · "}
          {t("dossierDetail.internalCodePre")}{" "}
          <span className="font-mono">{dossier.dossierCode}</span>
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {mostrarTrocarCliente && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setMaisAberto((v) => !v)}
              className={`inline-flex h-9 items-center gap-1 rounded-md border px-3 text-sm transition-colors ${
                maisAberto
                  ? "border-lime text-lime"
                  : "border-neutral-700 text-neutral-300 hover:border-lime hover:text-lime"
              }`}
              aria-haspopup="menu"
              aria-expanded={maisAberto}
              data-testid="dossier-more"
            >
              ⋯ {t("dossierDetail.moreBtn")}
            </button>
            {maisAberto && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMaisAberto(false)} />
                <div
                  role="menu"
                  className="absolute right-0 z-20 mt-1 min-w-[220px] rounded-lg border border-neutral-700 bg-neutral-950 p-1 text-left shadow-xl"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMaisAberto(false);
                      onTrocarCliente();
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-text-primary hover:bg-neutral-800"
                    data-testid="change-customer-btn"
                  >
                    ⇄ {t("dossierDetail.chgCustBtn")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <Link
          href="/dossiers"
          className="inline-flex h-9 items-center text-sm text-neutral-400 hover:text-lime"
        >
          {t("dossierDetail.backShort")}
        </Link>
      </div>
    </div>
  );
}

// ============================================================================
// LINHA DO TEMPO (F-ficha-carro) — onde o carro está + a ação do próximo passo
// ============================================================================
interface PassoTl {
  key: string;
  label: string;
  date: Date | null;
  feito: boolean;
  /** etapa registrada no portal (técnico/gerente), não pelo escritório. */
  portal: boolean;
}

/**
 * CEO 2026-10-26 — fração FATURÁVEL do dégarnissage: metade feita por
 * terceiro (garage) não entra na fatura. Mesma regra do servidor
 * (`fatorDegarnissageFaturavel`).
 */
function fatorFaturavel(l: {
  serviceType?: string | null;
  demontageExternal?: boolean | null;
  montageExternal?: boolean | null;
}): number {
  if (l.serviceType !== "montage_demontage") return 1;
  return (2 - (l.demontageExternal ? 1 : 0) - (l.montageExternal ? 1 : 0)) / 2;
}

function maiorData(xs: Array<string | Date | null | undefined>): Date | null {
  return (
    xs
      .filter((x): x is string | Date => x != null)
      .map((x) => new Date(x))
      .sort((a, b) => b.getTime() - a.getTime())[0] ?? null
  );
}

function LinhaDoTempo({
  dossier,
  companyId,
  grantOnly,
  canEdit,
  onIr,
  valorFixo,
}: {
  dossier: {
    id: number;
    source: string;
    createdAt: Date;
    devisDate: string | null;
    customerInvoiceStatus: string;
    customerChantierId?: number | null;
    deletedAt: Date | null;
    chantierChecks: Array<{ kind: string; updatedAt: Date | string }>;
    lines: ReadonlyArray<{
      serviceType?: string;
      technicianId?: number | null;
      technicianPaymentStatus?: string;
      dspDoneAt?: string | Date | null;
      montageDoneAt?: string | Date | null;
      demontageDoneAt?: string | Date | null;
      montageTakenAt?: string | Date | null;
      demontageExternal?: boolean;
      montageExternal?: boolean;
      technicianAmount?: string | number | null;
      technicianCommissionAmount?: string | number | null;
      shares?: ReadonlyArray<{ phase: string; doneAt?: string | Date | null }>;
    }>;
  };
  companyId: number;
  grantOnly: boolean;
  canEdit: boolean;
  onIr: (tab: TabKey, anchor?: string, abrirFatura?: boolean) => void;
  /** CEO 2026-10-26 — atalho "💶 Montant fixe" ao lado da ação principal (só admin). */
  valorFixo?: { tem: boolean; onClick: () => void } | null;
}) {
  const t = useT();
  // mesma consulta (e cache) do Resumo
  const faturasQ = trpc.customerInvoices.list.useQuery({
    companyId,
    dossierId: dossier.id,
    limit: 10,
    offset: 0,
  });
  const fatura = (faturasQ.data?.rows ?? []).find(
    (f) => f.dossierId === dossier.id && (f.fiscalStatus === "issued" || f.fiscalStatus === "paid"),
  );
  const ls = dossier.lines;
  const linhasTec = ls.filter(
    (l) => l.serviceType === "dsp" || l.serviceType === "montage_demontage",
  );
  const temTecnico = linhasTec.some((l) => l.technicianId != null);
  const pdr = maiorData(ls.filter((l) => l.serviceType === "dsp").map((l) => l.dspDoneAt));
  // import do eDSP: o carro chega já reparado (a ordem do eDSP é a prova).
  // CEO 2026-10-25 — espelho ES→BS idem: as etapas ficam no carro da ES.
  const etapasDeFora = dossier.source === "edsp_import" || dossier.source === "proforma_mirror";
  // As metades (shares) vêm da lista de linhas do dossier (mesma consulta, e
  // cache, da aba Serviços); o cabeçalho do dossier não as traz.
  const linhasQ = trpc.serviceLines.list.useQuery({ companyId, dossierId: dossier.id });
  type LinhaMontagem = (typeof ls)[number];
  const linhasMontagem: ReadonlyArray<LinhaMontagem> = (
    (linhasQ.data as ReadonlyArray<LinhaMontagem> | undefined) ?? ls
  ).filter((l) => l.serviceType === "montage_demontage");
  const temMontagem = ls.some((l) => l.serviceType === "montage_demontage");
  // CEO 2026-10-25 (GF-921-ZY) — a montagem está feita quando QUEM a fez a
  // concluiu: o dono da linha, o técnico da metade (share) ou um terceiro.
  // Linha antiga, sem nenhuma marca de etapa, num carro do eDSP ou espelho:
  // conta como feita, sem data (mesma regra do "Reparado").
  const montagens = linhasMontagem.map((l) => {
    const metade = (l.shares ?? []).find((s) => s.phase === "montage");
    if (metade) return { feita: metade.doneAt != null, data: metade.doneAt ?? null };
    if (l.montageDoneAt != null) return { feita: true, data: l.montageDoneAt };
    if (l.montageExternal) return { feita: true, data: null };
    const semMarca =
      l.demontageDoneAt == null &&
      l.montageTakenAt == null &&
      !l.demontageExternal &&
      (l.shares ?? []).length === 0;
    const valor = Number(l.technicianAmount ?? l.technicianCommissionAmount ?? 0);
    return { feita: semMarca && valor > 0 && etapasDeFora, data: null };
  });
  const montFeita = montagens.length > 0 && montagens.every((m) => m.feita);
  const mont = maiorData(montagens.map((m) => m.data));
  const faturado =
    dossier.customerInvoiceStatus === "issued" || dossier.customerInvoiceStatus === "paid";
  const recebido = dossier.customerInvoiceStatus === "paid" || fatura?.lastReceivedAt != null;
  const reparado = pdr != null || (etapasDeFora && ls.length > 0);
  const eChantier = dossier.customerChantierId != null;
  const entrega = dossier.chantierChecks.find((c) => c.kind === "delivery");
  const repassado =
    linhasTec.length > 0 && linhasTec.every((l) => l.technicianPaymentStatus === "paid");

  const passos: PassoTl[] = [
    {
      key: "devis",
      label: t("dossierDetail.tlDevis"),
      date: dossier.devisDate ? new Date(dossier.devisDate) : new Date(dossier.createdAt),
      feito: true,
      portal: false,
    },
    {
      key: "reparado",
      label: t("dossierDetail.tlRepaired"),
      date: pdr,
      feito: reparado,
      portal: true,
    },
    ...(temMontagem
      ? [
          {
            key: "montagem",
            label: t("dossierDetail.tlMontage"),
            date: mont,
            feito: montFeita,
            portal: true,
          },
        ]
      : []),
    ...(eChantier
      ? [
          {
            key: "entregue",
            label: t("dossierDetail.tlDelivered"),
            date: entrega ? new Date(entrega.updatedAt) : null,
            feito: entrega != null,
            portal: true,
          },
        ]
      : []),
    {
      key: "faturado",
      label: t("dossierDetail.tlInvoiced"),
      date: fatura?.issuedAt ? new Date(fatura.issuedAt) : null,
      feito: faturado,
      portal: false,
    },
    {
      key: "recebido",
      label: t("dossierDetail.tlReceived"),
      date: fatura?.lastReceivedAt ? new Date(fatura.lastReceivedAt) : null,
      feito: recebido,
      portal: false,
    },
    ...(grantOnly
      ? []
      : [
          {
            key: "repasse",
            label: t("dossierDetail.tlPayout"),
            date: null,
            feito: repassado,
            portal: false,
          },
        ]),
  ];
  const agora = passos.find((p) => !p.feito) ?? null;
  const dataCurta = (d: Date | null) =>
    d ? d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : null;

  // ação principal = próximo passo que o ESCRITÓRIO executa
  let acao: { label: string; onClick: () => void; testid: string } | null = null;
  let dica: string | null = null;
  if (!dossier.deletedAt && agora && canEdit) {
    if (agora.key === "reparado" && !temTecnico) {
      acao = {
        label: t("dossierDetail.launchTechBtn"),
        onClick: () => onIr("resumo", "sec-devis"),
        testid: "tl-action-launch",
      };
    } else if (agora.portal) {
      dica = t("dossierDetail.tlPortalHint");
    } else if (agora.key === "faturado") {
      acao = {
        label: t("dossierDetail.tlActIssue"),
        onClick: () => onIr("financeiro", "sec-faturas", true),
        testid: "tl-action-issue",
      };
    } else if (agora.key === "recebido" && !grantOnly) {
      // CEO 2026-10-29 — recebimento só o escritório registra.
      acao = {
        label: t("dossierDetail.tlActReceipt"),
        onClick: () => onIr("financeiro", "sec-recebimentos"),
        testid: "tl-action-receipt",
      };
    } else if (agora.key === "repasse") {
      acao = {
        label: t("dossierDetail.tlActPayout"),
        onClick: () => onIr("financeiro", "sec-tec-pagamentos"),
        testid: "tl-action-payout",
      };
    }
  }

  return (
    <section
      className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 sm:p-4"
      aria-label={t("dossierDetail.tlTitle")}
      data-testid="dossier-timeline"
    >
      {/* CEO 2026-10-27 ("os botões me parecem mal distribuídos") — duas linhas
          fixas: em cima só as etapas, na largura toda; embaixo, separada por um
          traço, o aviso do portal à esquerda e os botões à direita. Antes as
          ações dividiam a linha com as etapas, que quebravam em 3+3+1. */}
      <ol className="flex min-w-0 flex-wrap gap-1.5">
        {passos.map((p, i) => {
          const estado = p.feito ? "feito" : agora?.key === p.key ? "agora" : "futuro";
          return (
            <li
              key={p.key}
              className={`flex shrink-0 items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                estado === "feito"
                  ? "border-lime/30 bg-lime/10 text-text-primary"
                  : estado === "agora"
                    ? "border-amber-500/60 bg-amber-500/15 text-text-primary"
                    : "border-neutral-800 text-neutral-500"
              }`}
              data-testid={`tl-step-${p.key}`}
              data-estado={estado}
            >
              <span
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold ${
                  estado === "feito"
                    ? "bg-lime text-on-lime"
                    : estado === "agora"
                      ? "bg-amber-500 text-black"
                      : "border border-neutral-700 text-neutral-500"
                }`}
              >
                {estado === "feito" ? "✓" : i + 1}
              </span>
              <span className="whitespace-nowrap">
                <span className="font-semibold">{p.label}</span>
                {p.feito && p.date && (
                  <span className="ml-1 tabular-nums text-neutral-500">{dataCurta(p.date)}</span>
                )}
                {estado === "agora" && p.portal && (
                  <span className="ml-1 text-amber-300/80">· {t("dossierDetail.tlPortal")}</span>
                )}
                {p.key === "reparado" && estado === "agora" && !temTecnico && (
                  <span className="ml-1 text-amber-300/80">· {t("dossierDetail.tlNoTech")}</span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {((!agora && !dossier.deletedAt) || valorFixo || acao || dica) && (
        <div
          className="mt-3 flex flex-col gap-2 border-t border-neutral-800 pt-3 sm:flex-row sm:items-center sm:justify-between"
          data-testid="tl-actions"
        >
          <p className="min-w-0 text-xs text-neutral-500">{!acao && dica ? `ⓘ ${dica}` : null}</p>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!agora && !dossier.deletedAt && (
              <span className="rounded-md bg-lime/15 px-3 py-1.5 text-sm font-semibold text-lime">
                ✓ {t("dossierDetail.tlDone")}
              </span>
            )}
            {valorFixo && (
              <button
                type="button"
                onClick={valorFixo.onClick}
                title={t("dossierDetail.fixedTopHint")}
                className="inline-flex h-10 items-center gap-1 rounded-md border border-neutral-700 px-3 text-sm text-neutral-300 transition-colors hover:border-lime hover:text-lime"
                data-testid="tl-fixed-amount"
              >
                💶 {t("dossierDetail.fixedTopBtn")}
                {valorFixo.tem && <span className="font-semibold text-lime">✓</span>}
              </button>
            )}
            {acao && (
              <button
                type="button"
                onClick={acao.onClick}
                className="inline-flex h-10 items-center rounded-md bg-lime px-4 text-sm font-semibold text-on-lime hover:bg-lime-bright"
                data-testid={acao.testid}
              >
                {acao.label}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================================================
// TAB RESUMO
// ============================================================================
function TabResumo({
  dossier,
  companyById,
  companyId,
  dossierId,
  isAdmin,
}: {
  // F-ordre-reparation — contexto para o card da OR.
  companyId: number;
  dossierId: number;
  /** F-trocar-cliente — só admin troca o cliente do dossier. */
  isAdmin: boolean;
  dossier: {
    customerId: number;
    customerDisplayName: string | null;
    endCustomerName: string | null;
    atelierName?: string | null;
    atelierCity?: string | null;
    billingCompanyId: number;
    receivingCompanyId: number | null;
    plate: string | null;
    vin: string | null;
    vehicleId: number | null;
    vehicleMakeSnapshot: string | null;
    vehicleModelSnapshot: string | null;
    invoiceDate: string | null;
    customerRetentionPct: string | null;
    customerRetentionAmountSuggested: string | null;
    customerRetentionAmount: string | null;
    customerInvoiceStatus: string;
    notes: string | null;
    /** F-derrogacao-ui — a trava de recepção só vale p/ devis PDF novo. */
    source: string;
    createdAt: Date;
    updatedAt: Date;
    deletedAt: Date | null;
    deletedReason: string | null;
    overrideReason: string | null;
    overrideAt: Date | null;
    externalDevisRef: string | null;
    devisDate: string | null;
    devisBreakdown: unknown;
    /** F-entrega-derrogacao — cliente de chantier (card aparece mesmo sem checks). */
    customerChantierId?: number | null;
    chantierChecks: Array<{
      kind: string;
      damages: unknown;
      notes: string | null;
      franchise: boolean;
      franchiseAmount: string | null;
      workOk: boolean | null;
      updatedAt: Date | string;
    }>;
    totals: {
      totalOrcamento: number | null;
      totalFaturado: number | null;
      totalEurostormRetention: number | null;
      totalIntermediaryRetention: number | null;
      totalTechnicianCommission: number | null;
      totalIntercompanyInvoice: number | null;
    };
    lines: ReadonlyArray<{
      clientPct: string | null;
      clientAmount: string | null;
      serviceType?: string;
      dspDoneAt?: string | Date | null;
      demontageDoneAt?: string | Date | null;
      montageDoneAt?: string | Date | null;
      createdAt?: string | Date | null;
    }>;
  };
  companyById: ReadonlyMap<number, { name: string }>;
}) {
  // F-acesso-por-menu — gerente de chantier não vê comissões nem margem.
  const { grantOnly } = useGrantOnly();
  const t = useT();
  // F-avoir-dossier — avoirs das faturas deste carro.
  const avoirsQ = trpc.customerCreditNotes.byDossier.useQuery({ companyId, dossierId });
  const avoirsAtivos = (avoirsQ.data?.rows ?? []).filter((a) => a.status === "issued");
  // F-conta-avoir — avoirs já descontados no valor da fatura (forfaits do eDSP):
  // o documento da fatura = valor do Gestion + eles.
  const avoirsEmbutidos = (avoirsQ.data?.rows ?? []).filter((a) => a.status === "embedded");
  const avoirsDoc = [...avoirsEmbutidos, ...avoirsAtivos];
  const avoirsHt = avoirsAtivos.reduce((x, a) => x + Number(a.totalHt), 0);
  // F-ht-ttc — TVA da fatura deste carro (a própria; consolidada não entra).
  const faturasQ = trpc.customerInvoices.list.useQuery({
    companyId,
    dossierId,
    limit: 10,
    offset: 0,
  });
  const faturaDoCarro = (faturasQ.data?.rows ?? []).find(
    (f) => f.dossierId === dossierId && (f.fiscalStatus === "issued" || f.fiscalStatus === "paid"),
  );
  // CEO 2026-10-12 ("não encontrei a data do pagamento desta fatura, nem a
  // de emissão"): fatura(s) do carro — inclusive consolidada — com emissão e
  // último recebimento, no Resumo.
  const faturasDoCarro = (faturasQ.data?.rows ?? []).filter(
    (f) => f.fiscalStatus === "issued" || f.fiscalStatus === "paid",
  );
  const taxaTva = faturaDoCarro?.tvaReverseCharge ? 0 : Number(faturaDoCarro?.tvaRate ?? 20);
  const comTva = (ht: number) => Math.round(ht * (1 + taxaTva / 100) * 100) / 100;
  const devisHt = Number(dossier.totals.totalOrcamento ?? 0);
  const faturadoHt = Number(dossier.totals.totalFaturado ?? 0);
  const faturadoTtc =
    faturaDoCarro?.totalTtc != null &&
    Math.abs(Number(faturaDoCarro.totalFaturadoSnapshot ?? 0) - faturadoHt) < 0.01
      ? Number(faturaDoCarro.totalTtc)
      : comTva(faturadoHt);
  const avoirsTtc = avoirsAtivos.reduce(
    (x, a) => x + (a.totalTtc != null ? Number(a.totalTtc) : comTva(Number(a.totalHt))),
    0,
  );
  const m2 = (n: number) => formatMoney(n, { decimals: 2 });
  const embHt = avoirsEmbutidos.reduce((x, a) => x + Number(a.totalHt), 0);
  const embTtc = avoirsEmbutidos.reduce(
    (x, a) => x + (a.totalTtc != null ? Number(a.totalTtc) : comTva(Number(a.totalHt))),
    0,
  );
  const valoresHtTtc: Array<{ label: string; ht: string; ttc: string; forte?: boolean }> = [
    { label: t("dossierDetail.totalQuote"), ht: m2(devisHt), ttc: m2(comTva(devisHt)) },
    {
      label: t("dossierDetail.totalInvoiced"),
      ht: m2(faturadoHt + embHt),
      ttc: m2(faturadoTtc + embTtc),
    },
    ...avoirsDoc.map((x) => {
      const xht = Number(x.totalHt);
      const xttc = x.totalTtc != null ? Number(x.totalTtc) : comTva(xht);
      return {
        label: `${t("dossierDetail.avoirWord")} ${x.creditNoteNumber}${x.status === "embedded" ? ` (${t("dossierDetail.avoirEmbeddedTag")})` : ""}`,
        ht: `− ${m2(xht)}`,
        ttc: `− ${m2(xttc)}`,
      };
    }),
    ...(avoirsDoc.length > 0 && !avoirsDoc.some((x) => x.consolidated)
      ? [
          {
            label: t("dossierDetail.netInvoicedLabel"),
            ht: m2(faturadoHt - avoirsHt),
            ttc: m2(faturadoTtc - avoirsTtc),
            forte: true,
          },
        ]
      : []),
  ];
  const resumoCanEdit = useCanEdit();
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          {t("dossierDetail.sectionHeader")}
        </h3>
        <dl className="space-y-1.5 text-sm">
          <div className="flex gap-2">
            <dt className="text-neutral-500 w-40 text-xs">{t("dossierDetail.fieldCustomer")}:</dt>
            <dd className="min-w-0 break-words">
              {dossier.customerDisplayName ?? `#${dossier.customerId}`}
            </dd>
          </div>
          {dossier.endCustomerName && (
            <DlRow label={t("dossierDetail.fieldEndCustomer")} value={dossier.endCustomerName} />
          )}
          {/* CEO 2026-10-09: o ateliê (onde o carro foi reparado) não aparecia
              na ficha. Para trocar: "⇄ Trocar cliente" (cliente + ateliê). */}
          {/* F-ficha-carro — campo vazio não ocupa linha ("—" em 5 linhas). */}
          {dossier.atelierName && (
            <DlRow
              label={t("dossierDetail.fieldAtelier")}
              value={`🏭 ${dossier.atelierName}${dossier.atelierCity ? ` — ${dossier.atelierCity}` : ""}`}
            />
          )}
          <DlRow
            label={t("dossierDetail.fieldBillingCompany")}
            value={
              companyById.get(dossier.billingCompanyId)?.name ?? `#${dossier.billingCompanyId}`
            }
          />
          {dossier.receivingCompanyId != null && (
            <DlRow
              label={t("dossierDetail.fieldReceivingCompany")}
              value={
                companyById.get(dossier.receivingCompanyId)?.name ??
                `#${dossier.receivingCompanyId}`
              }
            />
          )}
          <DlRow
            label={t("dossierDetail.fieldVehicle")}
            value={
              [dossier.plate, dossier.vehicleMakeSnapshot, dossier.vehicleModelSnapshot]
                .filter(Boolean)
                .join(" · ") ||
              dossier.vin ||
              (dossier.vehicleId ? `#${dossier.vehicleId}` : "—")
            }
          />
          {dossier.vin && <DlRow label="VIN" value={dossier.vin} mono />}
          {dossier.invoiceDate && (
            <DlRow label={t("dossierDetail.fieldDate")} value={dossier.invoiceDate} />
          )}
          {/* CEO 2026-10-12: "no dossier do veículo não há data de reparação". */}
          {dossier.lines.length > 0 && (
            <DlRow
              label={t("dossierDetail.fieldRepair")}
              value={(() => {
                const ls = dossier.lines;
                if (ls.length === 0) return "—";
                if (dossier.source === "edsp_import") {
                  return `${t("dossierDetail.repairEdspOrderPre")} ${dataCurta(dossier.invoiceDate)}`;
                }
                const maior = (xs: Array<string | Date | null | undefined>) =>
                  xs
                    .filter((x): x is string | Date => x != null)
                    .map((x) => new Date(x))
                    .sort((a, b) => b.getTime() - a.getTime())[0] ?? null;
                const menor = (xs: Array<string | Date | null | undefined>) =>
                  xs
                    .filter((x): x is string | Date => x != null)
                    .map((x) => new Date(x))
                    .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;
                const inicio = menor(ls.map((l) => l.createdAt));
                const pdr = maior(
                  ls.filter((l) => l.serviceType === "dsp").map((l) => l.dspDoneAt),
                );
                const mont = maior(
                  ls
                    .filter((l) => l.serviceType === "montage_demontage")
                    .map((l) => l.montageDoneAt),
                );
                const partes = [
                  inicio ? `${t("dossierDetail.lineTakenOnPre")} ${dataCurta(inicio)}` : null,
                  pdr ? `${t("dossierDetail.phasePdrDone")} ${dataCurta(pdr)}` : null,
                  mont ? `${t("dossierDetail.phaseMontage")} ✓ ${dataCurta(mont)}` : null,
                ].filter(Boolean);
                return partes.length ? partes.join(" · ") : "—";
              })()}
            />
          )}
          <DlRow
            label={t("dossierDetail.fieldInvoiceStatus")}
            value={dossier.customerInvoiceStatus}
            badge={CUSTOMER_INVOICE_TONE[dossier.customerInvoiceStatus]}
            badgeText={
              customerInvoiceLabels(t)[dossier.customerInvoiceStatus] ??
              dossier.customerInvoiceStatus
            }
          />
          <DlRow
            label={t("dossierDetail.fieldInvoices")}
            value={
              faturasDoCarro.length === 0
                ? t("dossierDetail.invoicesNone")
                : faturasDoCarro
                    .map(
                      (f) =>
                        `${f.invoiceNumber} · ${t("dossierDetail.invoiceIssuedOnPre")} ${
                          f.issuedAt ? new Date(f.issuedAt).toLocaleDateString("pt-BR") : "—"
                        }${
                          f.lastReceivedAt
                            ? ` · ${t("dossierDetail.invoiceReceivedOnPre")} ${new Date(
                                f.lastReceivedAt,
                              ).toLocaleDateString("pt-BR")}`
                            : ""
                        }`,
                    )
                    .join(" / ")
            }
          />
        </dl>
      </section>

      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          {t("dossierDetail.sectionTotals")}
        </h3>
        <dl className="space-y-1.5 text-sm">
          {/* F-ht-ttc (CEO 2026-09-27, GF-367-VX: "a fatura deve ter o valor do
              eDSP") — os valores do dossier são HT; o cliente paga TTC. Devis,
              faturado, avoirs e líquido saem nas duas colunas (o PDF do eDSP
              mostra os dois). TVA = a da fatura do carro (0 em autoliquidação). */}
          <div className="grid grid-cols-[minmax(6.5rem,1fr)_auto_auto] gap-x-3 gap-y-1.5 items-baseline">
            <span />
            <span className="text-right text-[10px] uppercase tracking-wider text-neutral-500">
              HT
            </span>
            <span className="text-right text-[10px] uppercase tracking-wider text-neutral-500">
              TTC
            </span>
            {valoresHtTtc.map((v) => (
              <Fragment key={v.label}>
                <span className="text-neutral-500 text-xs">{v.label}:</span>
                <span className="font-mono text-right whitespace-nowrap">{v.ht}</span>
                <span
                  className={`font-mono text-right whitespace-nowrap ${v.forte ? "font-semibold" : ""}`}
                >
                  {v.ttc}
                </span>
              </Fragment>
            ))}
          </div>
          {/* totalClientRetention foi adicionado em F-dossier-detail-v3-fields;
              cast porque tRPC type inference às vezes não invalida AppRouter
              em projetos grandes mesmo após mudança no router. Backend manda
              o campo em runtime — type assertion é seguro. */}
          <DlRow
            label={t("dossierDetail.totalCustomerRetention")}
            value={formatMoney(
              (dossier.totals as typeof dossier.totals & { totalClientRetention: number | null })
                .totalClientRetention,
              { decimals: 2 },
            )}
            mono
          />
          {!grantOnly && (
            <>
              <DlRow
                label={t("dossierDetail.totalTechnicianCommission")}
                value={formatMoney(dossier.totals.totalTechnicianCommission, { decimals: 2 })}
                mono
              />
              <DlRow
                label={t("dossierDetail.totalIntermediaryCommission")}
                value={formatMoney(dossier.totals.totalIntermediaryRetention, { decimals: 2 })}
                mono
              />
              <DlRow
                label={`${companyById.get(dossier.billingCompanyId)?.name ?? "Euro Storm"} ${t("dossierDetail.netSuffix")}`}
                value={formatMoney(dossier.totals.totalEurostormRetention, { decimals: 2 })}
                mono
              />
              <DlRow
                label={t("dossierDetail.totalIntercompanyInvoice")}
                value={formatMoney(dossier.totals.totalIntercompanyInvoice, { decimals: 2 })}
                mono
              />
            </>
          )}
          <DlRow label={t("dossierDetail.fieldLines")} value={String(dossier.lines.length)} />
        </dl>
      </section>

      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 lg:col-span-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          {t("dossierDetail.sectionCustomerRetention")}
        </h3>
        {/* F-retention-card-from-lines (CEO 2026-10-01, FR-136-YF) — no modelo
            por par técnico×cliente a remise do cliente vive nas LINHAS
            (client_pct/client_amount); o cabeçalho só é preenchido no modelo
            antigo. Sem isto o card mostrava "—" enquanto os totais mostravam
            476,45 €. Cabeçalho preenchido continua mandando (override). */}
        {(() => {
          const linhasComRemise = dossier.lines.filter((l) => l.clientPct != null);
          const pctsLinhas = [...new Set(linhasComRemise.map((l) => Number(l.clientPct)))];
          const totalLinhas = linhasComRemise.reduce<number>(
            (acc, l) => acc + Number(l.clientAmount ?? 0),
            0,
          );
          const temLinhas = linhasComRemise.length > 0;
          // Cada campo: cabeçalho (modelo antigo / override) se existir, senão as linhas.
          const pctTexto =
            dossier.customerRetentionPct ??
            (temLinhas
              ? pctsLinhas.length === 1
                ? String(pctsLinhas[0])
                : t("dossierDetail.retentionPctVaries")
              : "—");
          const sugerido = formatMoney(
            dossier.customerRetentionAmountSuggested ?? (temLinhas ? totalLinhas : null),
            { decimals: 2 },
          );
          const aplicado = formatMoney(
            dossier.customerRetentionAmount ?? (temLinhas ? totalLinhas : null),
            { decimals: 2 },
          );
          const usaLinhas =
            temLinhas &&
            (dossier.customerRetentionPct == null ||
              dossier.customerRetentionAmountSuggested == null ||
              dossier.customerRetentionAmount == null);
          return (
            <>
              <dl className="grid grid-cols-3 gap-4 text-sm">
                <DlBlock label={t("dossierDetail.pctLabel")} value={pctTexto} suffix="%" />
                <DlBlock label={t("dossierDetail.suggestedLabel")} value={sugerido} />
                <DlBlock
                  label={t("dossierDetail.appliedLabel")}
                  value={aplicado}
                  tone={dossier.overrideReason ? "amber" : undefined}
                />
              </dl>
              {usaLinhas && (
                <p className="mt-2 text-xs text-neutral-500">
                  {t("dossierDetail.retentionFromPairNote")}
                </p>
              )}
            </>
          );
        })()}
        {dossier.overrideReason && (
          <p className="mt-3 text-xs text-amber-300 bg-amber-500/5 border border-amber-500/30 rounded-md px-3 py-2">
            {t("dossierDetail.overrideApplied")}: {dossier.overrideReason}{" "}
            {dossier.overrideAt && (
              <span className="text-amber-200/70">
                ({new Date(dossier.overrideAt).toLocaleString("pt-BR")})
              </span>
            )}
          </p>
        )}
      </section>

      {/* F-devis-panels — devis do eDSP RECRIADO para consulta (layout próprio
          STORM, mesmas informações). Aparece quando o dossier veio de devis
          PDF e a tabela de peças foi extraída. */}
      <div id="sec-devis" />
      <DevisCard
        externalDevisRef={
          ((dossier.devisBreakdown as { devisRef?: string } | null)?.devisRef ??
            dossier.externalDevisRef) as string | null
        }
        devisDate={dossier.devisDate}
        devisBreakdown={dossier.devisBreakdown}
        customerName={dossier.customerDisplayName}
        plate={dossier.plate}
        vehicle={[dossier.vehicleMakeSnapshot, dossier.vehicleModelSnapshot]
          .filter(Boolean)
          .join(" ")}
        companyId={companyId}
        dossierId={dossierId}
        canEdit={resumoCanEdit && !dossier.deletedAt}
        invoiceIssued={dossier.customerInvoiceStatus !== "pending"}
      />

      {/* CEO 2026-10-26 — valor fixo do técnico definido JUNTO DO DEVIS, antes
          de o técnico assumir no portal (exceção; o padrão é o par). Só admin. */}
      {dossier.devisBreakdown != null && !dossier.deletedAt && (
        <div id="sec-valor-fixo" className="scroll-mt-24 lg:col-span-2">
          <ValorFixoTecnico companyId={companyId} dossierId={dossierId} canEdit={resumoCanEdit} />
        </div>
      )}

      {/* F-ordre-reparation — a OR entre o devis e a fatura (fluxo eDSP). */}
      <RepairOrderCard companyId={companyId} dossierId={dossierId} canEdit={resumoCanEdit} />

      {/* F-derrogacao-ui (CEO 2026-09-28) — liberar o reparo sem recepção. */}
      {dossier.source === "edsp_devis_pdf" &&
        new Date(dossier.createdAt) >= new Date("2026-09-19T00:00:00Z") &&
        !dossier.deletedAt && (
          <LiberarReparo
            dossierId={dossierId}
            temRecepcao={dossier.chantierChecks.some((c) => c.kind === "reception")}
          />
        )}

      {/* F-franquia-sem-recepcao (CEO 2026-09-28) — carro sem recepção:
          o escritório lança a franquia aqui (antes não havia onde). */}
      {!dossier.chantierChecks.some((c) => c.kind === "reception") && !dossier.deletedAt && (
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 lg:col-span-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
            {t("dossierDetail.franchiseSectionTitle")}
          </h3>
          <FranquiaRecepcao
            companyId={companyId}
            dossierId={dossierId}
            franchise={false}
            amountTtc={null}
            canEdit={resumoCanEdit}
            semRecepcao
          />
        </section>
      )}

      {/* F-chantier-manager — check-lists do gerente de projeto (portal
          /chantier) + alerta de FRANQUIA a abater da fatura/avoir. */}
      {(dossier.chantierChecks.length > 0 || dossier.customerChantierId != null) && (
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 lg:col-span-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
            {t("dossierDetail.chantierSectionTitle")}
          </h3>
          {(() => {
            const rec = dossier.chantierChecks.find((c) => c.kind === "reception");
            if (!rec) return null;
            return (
              <FranquiaRecepcao
                companyId={companyId}
                dossierId={dossierId}
                franchise={rec.franchise}
                amountTtc={rec.franchiseAmount != null ? Number(rec.franchiseAmount) : null}
                canEdit={resumoCanEdit}
              />
            );
          })()}
          <div className="grid gap-3 md:grid-cols-2">
            {(["reception", "delivery"] as const).map((kind) => {
              const c = dossier.chantierChecks.find((x) => x.kind === kind);
              // CEO 2026-10-12 (GY-669-KZ Lagarde): fora de chantier não há
              // entrega do gerente — o carro conta como entregue ao faturar.
              // "Entrega — pendente" com botão de derrogação só confundia.
              if (!c && kind === "delivery" && dossier.customerChantierId == null)
                return (
                  <div
                    key={kind}
                    className="rounded-md border border-neutral-800 bg-neutral-950/50 p-3 text-sm text-neutral-600"
                  >
                    {t("dossierDetail.chantierDelivery")} — {t("dossierDetail.chantierDeliveryNa")}
                  </div>
                );
              if (!c)
                return (
                  <div
                    key={kind}
                    className="rounded-md border border-neutral-800 bg-neutral-950/50 p-3 text-sm text-neutral-600 flex items-center justify-between gap-2"
                  >
                    <span>
                      {kind === "reception"
                        ? t("dossierDetail.chantierReception")
                        : t("dossierDetail.chantierDelivery")}{" "}
                      — {t("dossierDetail.chantierPendingWord")}
                    </span>
                    {/* F-entrega-derrogacao — Super Admin registra a entrega sem fotos. */}
                    {kind === "delivery" && <EntregaDerrogacao dossierId={dossierId} />}
                  </div>
                );
              const damages = Array.isArray(c.damages) ? (c.damages as string[]) : [];
              return (
                <div
                  key={kind}
                  className="rounded-md border border-neutral-800 bg-neutral-950/50 p-3 text-sm space-y-1.5"
                >
                  <p className="font-semibold">
                    {kind === "reception" ? "📥 " : "📤 "}
                    {kind === "reception"
                      ? t("dossierDetail.chantierReception")
                      : t("dossierDetail.chantierDelivery")}
                    <span className="ml-2 text-xs text-neutral-500">
                      {new Date(c.updatedAt).toLocaleString("fr-FR")}
                    </span>
                  </p>
                  {kind === "delivery" && c.workOk != null && (
                    <p className={c.workOk ? "text-lime" : "text-amber-300"}>
                      {c.workOk
                        ? t("dossierDetail.chantierWorkOkYes")
                        : t("dossierDetail.chantierWorkOkNo")}
                    </p>
                  )}
                  {damages.length > 0 && (
                    <p className="flex flex-wrap gap-1">
                      {damages.map((dm, i) => (
                        <span
                          key={i}
                          className="rounded px-1.5 py-0.5 text-xs"
                          style={{
                            background: "#FEF3C7",
                            border: "1px solid #F59E0B",
                            color: "#92400E",
                          }}
                        >
                          {dm}
                        </span>
                      ))}
                    </p>
                  )}
                  {c.notes && <p className="text-xs text-neutral-400">{c.notes}</p>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* F-portal-files (CEO 2026-10-19) — o que os portais subiram. */}
      <PortalFilesSection companyId={companyId} dossierId={dossierId} />

      {dossier.notes && (
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 lg:col-span-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-2">
            {t("dossierDetail.notesLabel")}
          </h3>
          <p className="text-sm whitespace-pre-wrap text-neutral-300">{dossier.notes}</p>
        </section>
      )}

      {dossier.deletedAt != null && (
        <section className="rounded-lg border border-red-500/40 bg-red-500/5 p-4 lg:col-span-2">
          <h3 className="text-sm font-semibold text-red-300 mb-2">
            {t("dossierDetail.deletedTitle")}
          </h3>
          <p className="text-xs text-red-200">
            {t("dossierDetail.onDate")} {new Date(dossier.deletedAt).toLocaleString("pt-BR")}
            {dossier.deletedReason && (
              <>
                {" "}
                {t("dossierDetail.reasonInline")} {dossier.deletedReason}
              </>
            )}
          </p>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// DEVIS RECRIADO (F-devis-panels)
// ============================================================================

type DevisBd = {
  totalDsp?: number;
  degarnissage?: number;
  carrosserie?: number;
  pieces?: number;
  pareBrise?: number;
  grandTotalHt?: number;
  tva?: number;
  totalTtc?: number;
  panelLines?: Array<{
    panel: string;
    method: string;
    impacts: number | null;
    sizeClass: string | null;
    note: string | null;
  }>;
  deposeRepose?: string | null;
  impactsTotal?: number | null;
};

/** Devis do eDSP recriado para consulta — mesmas informações, layout STORM. */
/**
 * F-portal-files (CEO 2026-10-19) — documentos da recepção e fotos dos
 * check-lists subidos pelos portais (gerente e técnicos), visíveis no dossier
 * do ERP. Some quando não há nada.
 */
function PortalFilesSection({ companyId, dossierId }: { companyId: number; dossierId: number }) {
  const t = useT();
  const utils = trpc.useUtils();
  const q = trpc.dossiers.portalFiles.useQuery({ companyId, dossierId });
  const docs = q.data?.docs ?? [];
  const grupos = (q.data?.photos ?? []).filter((g) => g.rows.length > 0);
  if (docs.length === 0 && grupos.length === 0) return null;
  // Mesmos rótulos do portal do técnico ("front_right" → chkPhotoFrontRight).
  const rotuloFoto = (kind: string) => {
    const key = ("techPortal.chkPhoto" +
      kind
        .split("_")
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join("")) as MessageKey;
    const s = t(key);
    return !s || s === key ? kind : s;
  };
  const rotuloDoc = (cat: string | null) =>
    t(`dossierDetail.portalDoc_${cat ?? "outro"}` as MessageKey);
  const tituloGrupo: Record<string, string> = {
    reception: t("dossierDetail.portalPhotosReception"),
    delivery: t("dossierDetail.portalPhotosDelivery"),
    montage: t("dossierDetail.portalPhotosMontage"),
    montage_end: t("dossierDetail.portalPhotosMontageEnd"),
  };
  return (
    <section
      className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 lg:col-span-2"
      data-testid="dossier-portal-files"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-1">
        {t("dossierDetail.portalFilesTitle")}
      </h3>
      <p className="text-xs text-neutral-500 mb-3">{t("dossierDetail.portalFilesHint")}</p>
      {docs.length > 0 && (
        <ul className="mb-3 space-y-1">
          {docs.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-300">
                {rotuloDoc(d.category)}
              </span>
              <span className="text-neutral-300">{d.fileName}</span>
              <span className="text-xs text-neutral-500">
                {Math.max(1, Math.round((d.sizeBytes ?? 0) / 1024))} KB ·{" "}
                {new Date(d.uploadedAt).toLocaleDateString("fr-FR")}
              </span>
              <button
                type="button"
                onClick={() =>
                  void abrirArquivo(
                    async () =>
                      (
                        await utils.attachments.getDownloadUrl.fetch({
                          companyId,
                          attachmentId: d.id,
                        })
                      ).url,
                  )
                }
                className="rounded-md border border-neutral-700 px-2 py-0.5 text-xs hover:border-lime hover:text-lime"
              >
                {t("dossierDetail.portalOpenBtn")}
              </button>
            </li>
          ))}
        </ul>
      )}
      {grupos.map((g) => (
        <div key={g.kind} className="mb-2">
          <p className="mb-1 text-xs font-semibold text-neutral-400">
            {tituloGrupo[g.kind] ?? g.kind} · {g.rows.length}
          </p>
          <div className="flex flex-wrap gap-2">
            {g.rows.map((p) => (
              <a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-28"
                title={rotuloFoto(p.kind)}
              >
                <img
                  src={p.url}
                  alt={rotuloFoto(p.kind)}
                  loading="lazy"
                  className="h-20 w-28 rounded border border-neutral-700 object-cover"
                />
                <span className="block truncate text-[10px] text-neutral-500">
                  {rotuloFoto(p.kind)}
                  {p.issue ? " ⚠" : ""}
                </span>
              </a>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

/**
 * CEO 2026-10-26 — o PDF ORIGINAL do devis (portal, devis@ ou escritório),
 * guardado a partir desta versão. Abre a versão mais nova; havendo reenvio,
 * as anteriores ficam na lista "versões".
 */
function DevisOriginalBotao({ companyId, dossierId }: { companyId: number; dossierId: number }) {
  const t = useT();
  const utils = trpc.useUtils();
  const q = trpc.dossiers.devisOriginals.useQuery({ companyId, dossierId });
  const [verVersoes, setVerVersoes] = useState(false);
  const versoes = q.data ?? [];
  if (versoes.length === 0) return null;
  const abrir = (attachmentId: number) =>
    void abrirArquivo(
      async () => (await utils.attachments.getDownloadUrl.fetch({ companyId, attachmentId })).url,
    );
  return (
    <span className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => abrir(versoes[0]!.id)}
        title={t("dossierDetail.devisOriginalHint")}
        data-testid="devis-original"
        className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs text-text-secondary hover:border-lime hover:text-lime"
      >
        📄 {t("dossierDetail.devisOriginalBtn")}
      </button>
      {versoes.length > 1 && (
        <button
          type="button"
          onClick={() => setVerVersoes((v) => !v)}
          data-testid="devis-original-versions"
          className="text-[11px] text-text-muted underline hover:text-lime"
        >
          {versoes.length} {t("dossierDetail.devisOriginalVersions")}
        </button>
      )}
      {verVersoes && versoes.length > 1 && (
        <ul className="absolute right-0 top-full z-20 mt-1 min-w-[260px] rounded-md border border-neutral-700 bg-neutral-900 p-1 shadow-lg">
          {versoes.map((v, i) => (
            <li key={v.id}>
              <button
                type="button"
                onClick={() => {
                  setVerVersoes(false);
                  abrir(v.id);
                }}
                className="flex w-full items-center justify-between gap-3 rounded px-2 py-1 text-left text-xs text-neutral-300 hover:bg-neutral-800 hover:text-lime"
              >
                <span className="truncate">{v.fileName}</span>
                <span className="shrink-0 whitespace-nowrap text-neutral-500">
                  {new Date(v.uploadedAt).toLocaleDateString("fr-FR")}
                  {i === 0 ? ` · ${t("dossierDetail.devisOriginalLatest")}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

function DevisCard({
  externalDevisRef,
  devisDate,
  devisBreakdown,
  customerName,
  plate,
  vehicle,
  companyId,
  dossierId,
  canEdit,
  invoiceIssued,
}: {
  externalDevisRef: string | null;
  devisDate: string | null;
  devisBreakdown: unknown;
  customerName: string | null;
  plate: string | null;
  vehicle: string;
  companyId: number;
  dossierId: number;
  canEdit: boolean;
  invoiceIssued: boolean;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [editing, setEditing] = useState(false);
  // CEO 2026-10-08: com fatura emitida o botão ficava apagado e o motivo só
  // aparecia no hover — o clique "não fazia nada". Agora o clique mostra o aviso.
  const [avisoBloqueio, setAvisoBloqueio] = useState(false);
  // F-lancar-tecnico (CEO 2026-10-09) — o escritório lança o técnico.
  const [lancando, setLancando] = useState(false);
  // O status do cabeçalho não acompanha a emissão por dossier: olha as faturas.
  const invoicesQ = trpc.customerInvoices.list.useQuery(
    { companyId, dossierId, limit: 50, offset: 0 },
    { enabled: canEdit },
  );
  const temFaturaViva =
    invoiceIssued ||
    (invoicesQ.data?.rows ?? []).some(
      (i) => i.fiscalStatus === "issued" || i.fiscalStatus === "paid",
    );
  const bd = (devisBreakdown ?? null) as DevisBd | null;
  if (!bd || (!bd.panelLines?.length && !(bd.totalDsp || bd.grandTotalHt))) return null;
  const methodTone = (m: string) =>
    m.toUpperCase() === "DSP"
      ? "bg-lime/15 text-lime"
      : m.toUpperCase() === "DAP"
        ? "bg-amber-500/15 text-amber-300"
        : /ECHANGE|REMPLACEMENT/i.test(m)
          ? "bg-red-500/15 text-red-300"
          : "bg-blue-500/15 text-blue-300";
  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 sm:p-4 lg:col-span-2">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("dossierDetail.devisSectionTitle")}
          {externalDevisRef && (
            <span className="ml-2 font-mono text-neutral-300">{externalDevisRef}</span>
          )}
        </h3>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs text-neutral-500">
            {devisDate ?? ""}
            {bd.impactsTotal != null && ` · ${bd.impactsTotal} ${t("dossierDetail.impactsWord")}`}
          </p>
          <DevisOriginalBotao companyId={companyId} dossierId={dossierId} />
          {/* F-devis-edit (CEO 2026-09-24) — o devis é o começo de tudo: mudou,
              linhas, comissões, franquia e ordem seguem. */}
          {canEdit && (
            <button
              type="button"
              // F-fatura-editada (CEO 2026-11-03) — com fatura emitida o devis
              // também muda: a fatura é refeita no mesmo número (aviso no modal).
              onClick={() => setEditing(true)}
              title={temFaturaViva ? t("dossierDetail.editDevisInvoiceNote") : undefined}
              data-testid="edit-devis"
              className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs text-text-secondary hover:border-lime hover:text-lime"
            >
              {t("dossierDetail.editDevisBtn")}
            </button>
          )}
          {canEdit && (Number(bd.totalDsp ?? 0) > 0 || Number(bd.degarnissage ?? 0) > 0) && (
            <button
              type="button"
              onClick={() => (temFaturaViva ? setAvisoBloqueio((v) => !v) : setLancando(true))}
              data-testid="launch-tech"
              className="rounded-md border border-lime/50 px-2.5 py-1 text-xs text-lime hover:bg-lime/10"
            >
              {temFaturaViva ? "🔒 " : "👷 "}
              {t("dossierDetail.launchTechBtn")}
            </button>
          )}
        </div>
      </div>
      {avisoBloqueio && temFaturaViva && (
        <p
          className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
          data-testid="edit-devis-blocked"
        >
          🔒 {t("dossierDetail.editDevisBlockedInvoice")}
          {(() => {
            const nums = (invoicesQ.data?.rows ?? [])
              .filter((i) => i.fiscalStatus === "issued" || i.fiscalStatus === "paid")
              .map((i) => i.invoiceNumber)
              .filter(Boolean);
            return nums.length > 0 ? ` (${nums.join(", ")})` : "";
          })()}
        </p>
      )}
      {editing && (
        <EditDevisModal
          companyId={companyId}
          dossierId={dossierId}
          initial={bd}
          faturasVivas={(invoicesQ.data?.rows ?? [])
            .filter((i) => i.fiscalStatus === "issued" || i.fiscalStatus === "paid")
            .map((i) => i.invoiceNumber)
            .filter(Boolean)}
          onClose={() => setEditing(false)}
        />
      )}
      {lancando && (
        <LaunchTechnicianModal
          companyId={companyId}
          dossierId={dossierId}
          temPdr={Number(bd.totalDsp ?? 0) > 0}
          temMontagem={Number(bd.degarnissage ?? 0) > 0}
          onClose={() => setLancando(false)}
        />
      )}
      <p className="mb-3 flex items-center gap-2 text-sm text-neutral-300">
        <CarLogo make={vehicle} size={26} />
        <span>
          {customerName ?? "—"} · <span className="font-mono">{plate ?? "—"}</span>
          {vehicle && <span className="text-neutral-500"> · {vehicle}</span>}
        </span>
      </p>

      {!!bd.panelLines?.length && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-normal text-neutral-500 sm:text-[11px] sm:tracking-wider">
                <th className="py-1 pr-2">{t("dossierDetail.devisPanelCol")}</th>
                <th className="whitespace-nowrap py-1 pr-2 text-right">
                  {t("dossierDetail.devisImpactsCol")}
                </th>
                <th className="whitespace-nowrap py-1 pr-2">{t("dossierDetail.devisClassCol")}</th>
                <th className="whitespace-nowrap py-1 pr-2">{t("dossierDetail.devisMethodCol")}</th>
                <th className="hidden py-1 sm:table-cell">{t("dossierDetail.devisNoteCol")}</th>
              </tr>
            </thead>
            <tbody>
              {bd.panelLines.map((l, i) => (
                <tr key={i} className="border-t border-neutral-800/70">
                  <td className="py-1.5 pr-2">
                    {trPanel(l.panel, locale)}
                    {l.note && (
                      <span className="block text-xs text-amber-300 sm:hidden">
                        {trNote(l.note, locale)}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono">{l.impacts ?? "—"}</td>
                  <td className="py-1.5 pr-2">{l.sizeClass ?? "—"}</td>
                  <td className="py-1.5 pr-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${methodTone(l.method)}`}
                    >
                      {trMethod(l.method, locale)}
                    </span>
                  </td>
                  <td className="hidden py-1.5 text-xs text-amber-300 sm:table-cell">
                    {l.note ? trNote(l.note, locale) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {bd.deposeRepose && (
        <p className="mt-2 text-xs text-neutral-400">
          {t("dossierDetail.deposeReposeLabel")}: {trDepose(bd.deposeRepose, locale)}
        </p>
      )}

      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 border-t border-neutral-800 pt-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        {[
          ["Total DSP", bd.totalDsp],
          ["Dégarnissage", bd.degarnissage],
          ["Carrosserie", bd.carrosserie],
          ["Pièces", bd.pieces],
          ["Pare-brise", bd.pareBrise],
        ]
          .filter(([, v]) => Number(v ?? 0) > 0)
          .map(([label, v]) => (
            <div key={String(label)} className="flex justify-between gap-2">
              <dt className="text-neutral-500">{label}</dt>
              <dd className="whitespace-nowrap font-mono">
                {formatMoney(Number(v), { decimals: 2 })}
              </dd>
            </div>
          ))}
        <div className="flex justify-between gap-2">
          <dt className="text-neutral-500">Total HT</dt>
          <dd className="whitespace-nowrap font-mono font-semibold">
            {formatMoney(bd.grandTotalHt ?? null, { decimals: 2 })}
          </dd>
        </div>
        {Number(bd.tva ?? 0) > 0 && (
          <div className="flex justify-between gap-2">
            <dt className="text-neutral-500">TVA</dt>
            <dd className="whitespace-nowrap font-mono">
              {formatMoney(bd.tva ?? null, { decimals: 2 })}
            </dd>
          </div>
        )}
        {Number(bd.totalTtc ?? 0) > 0 && (
          <div className="flex justify-between gap-2">
            <dt className="text-neutral-500">Total TTC</dt>
            <dd className="whitespace-nowrap font-mono font-semibold">
              {formatMoney(bd.totalTtc ?? null, { decimals: 2 })}
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

// ============================================================================
// F-devis-edit — MODAL: editar o devis dentro do dossier
// ============================================================================

const DEVIS_METHODS = ["DSP", "DAP", "Echange", "Peinture", "Rien"] as const;

type PanelDraft = {
  panel: string;
  method: string;
  impacts: string;
  sizeClass: string;
  note: string;
};

function EditDevisModal({
  companyId,
  dossierId,
  initial,
  faturasVivas,
  onClose,
}: {
  companyId: number;
  dossierId: number;
  initial: DevisBd;
  /** F-fatura-editada — nº das faturas emitidas do carro (refeitas no mesmo número). */
  faturasVivas: string[];
  onClose: () => void;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const modals = useModals();
  const num = (v: unknown) => (Number(v ?? 0) > 0 ? String(Number(v)) : "0");
  const [totalDsp, setTotalDsp] = useState(num(initial.totalDsp));
  const [degarnissage, setDegarnissage] = useState(num(initial.degarnissage));
  const [carrosserie, setCarrosserie] = useState(num(initial.carrosserie));
  const [pieces, setPieces] = useState(num(initial.pieces));
  const [pareBrise, setPareBrise] = useState(num(initial.pareBrise));
  const [outros, setOutros] = useState<Array<{ label: string; value: string }>>(
    Object.entries(
      ((initial as { outros?: Record<string, number> }).outros ?? {}) as Record<string, number>,
    ).map(([label, value]) => ({ label, value: String(value) })),
  );
  const [deposeRepose, setDeposeRepose] = useState(initial.deposeRepose ?? "");
  // F-remise-exc-devis — remise exceptionnelle: a do devis, senão a que já
  // estava na ordem de reparação (montante inicial − a faturar).
  const orQ = trpc.repairOrders.byDossier.useQuery({ companyId, dossierId });
  const remiseDoDevis = Number(
    (initial as { remiseExceptionnelleHt?: number }).remiseExceptionnelleHt ?? 0,
  );
  const [remise, setRemise] = useState<string | null>(null);
  const remiseDaOr = orQ.data?.order
    ? Math.max(
        0,
        Math.round((orQ.data.order.amountHtInitial - orQ.data.order.amountHt) * 100) / 100,
      )
    : 0;
  const remiseValor = remise ?? String(remiseDoDevis > 0 ? remiseDoDevis : remiseDaOr);
  const [panels, setPanels] = useState<PanelDraft[]>(
    (initial.panelLines ?? []).map((p) => ({
      panel: p.panel,
      method: p.method,
      impacts: p.impacts != null ? String(p.impacts) : "",
      sizeClass: p.sizeClass ?? "",
      note: p.note ?? "",
    })),
  );
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const n = (s: string) => {
    const v = Number(String(s).replace(",", "."));
    return Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : 0;
  };
  const outrosSum = outros.reduce((s, o) => s + n(o.value), 0);
  const total =
    n(totalDsp) + n(degarnissage) + n(carrosserie) + n(pieces) + n(pareBrise) + outrosSum;
  const impacts = panels.reduce((s, p) => s + (Number(p.impacts) || 0), 0);

  const mut = trpc.dossiers.updateDevis.useMutation({
    onSuccess: async (r) => {
      // Linhas, cabeçalho E a ordem de reparação (o card da OR ficava velho).
      await Promise.all([
        utils.dossiers.invalidate(),
        utils.serviceLines.invalidate(),
        utils.repairOrders.invalidate(),
      ]);
      onClose();
      const resumo = [
        `${t("dossierDetail.editDevisDoneTotalPre")} ${formatMoney(r.grandTotalHt, { decimals: 2 })}`,
        `${r.updatedLines} ${t("dossierDetail.editDevisDoneLines")}`,
        r.createdLines > 0 ? `${r.createdLines} ${t("dossierDetail.editDevisDoneCreated")}` : null,
        r.removedLines > 0 ? `${r.removedLines} ${t("dossierDetail.editDevisDoneRemoved")}` : null,
        r.orUpdated ? t("dossierDetail.editDevisDoneOr") : null,
        r.faturaEditada
          ? `🧾 ${r.faturaEditada.invoiceNumber} ${t("dossierDetail.editDevisInvoiceAmended")} (v${r.faturaEditada.versao}): ${formatMoney(r.faturaEditada.antes.ht, { decimals: 2 })} → ${formatMoney(r.faturaEditada.depois.ht, { decimals: 2 })} HT — ${t("dossierDetail.editDevisInvoiceResend")}`
          : null,
        ...r.warnings.map((w) => `⚠ ${w}`),
      ].filter(Boolean);
      void modals.alert({
        title: t("dossierDetail.editDevisDoneTitle"),
        message: resumo.join("\n"),
        tone: r.warnings.length ? "warning" : "default",
      });
    },
    onError: async (e) => {
      // F-fatura-editada — a fatura apareceu depois de abrir a tela: confirma e repete.
      if (e.message.startsWith("FATURA_EMITIDA_CONFIRMAR|") && ultimoEnvio.current) {
        const ok = await modals.confirm({
          title: t("dossierDetail.editDevisTitle"),
          message: `${e.message.slice("FATURA_EMITIDA_CONFIRMAR|".length)}

${t("dossierDetail.editDevisInvoiceConfirm")}`,
        });
        if (ok) mut.mutate({ ...ultimoEnvio.current, atualizarFatura: true });
        return;
      }
      setError(e.message);
    },
  });
  const ultimoEnvio = useRef<Parameters<typeof mut.mutate>[0] | null>(null);

  const setPanel = (i: number, k: keyof PanelDraft, v: string) =>
    setPanels((ps) => ps.map((p, j) => (j === i ? { ...p, [k]: v } : p)));

  return (
    <ModalShell title={t("dossierDetail.editDevisTitle")} onClose={onClose} wide>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const envio = {
            companyId,
            dossierId,
            devis: {
              totalDsp: n(totalDsp),
              degarnissage: n(degarnissage),
              carrosserie: n(carrosserie),
              pieces: n(pieces),
              pareBrise: n(pareBrise),
              outros: Object.fromEntries(
                outros
                  .filter((o) => o.label.trim() && n(o.value) > 0)
                  .map((o) => [o.label.trim(), n(o.value)]),
              ),
              panelLines: panels
                .filter((p) => p.panel.trim())
                .map((p) => ({
                  panel: p.panel.trim(),
                  method: p.method || "DSP",
                  impacts:
                    p.impacts.trim() === ""
                      ? null
                      : Math.max(0, Math.round(Number(p.impacts) || 0)),
                  sizeClass: p.sizeClass.trim() || null,
                  note: p.note.trim() || null,
                })),
              deposeRepose: deposeRepose.trim() || null,
              remiseExceptionnelleHt: n(remiseValor),
            },
            reason: reason.trim(),
            atualizarFatura: faturasVivas.length > 0,
          };
          ultimoEnvio.current = envio;
          mut.mutate(envio);
        }}
        className="p-5 space-y-4"
      >
        <p className="text-xs text-text-muted">{t("dossierDetail.editDevisHint")}</p>
        {faturasVivas.length > 0 && (
          <p
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs"
            data-testid="edit-devis-invoice-note"
          >
            🧾 {t("dossierDetail.editDevisInvoiceNote")} ({faturasVivas.join(", ")})
          </p>
        )}

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              {t("dossierDetail.editDevisPanels")} · {impacts} {t("dossierDetail.impactsWord")}
            </h4>
            <button
              type="button"
              className="text-xs text-lime hover:underline"
              onClick={() =>
                setPanels((ps) => [
                  ...ps,
                  { panel: "", method: "DSP", impacts: "", sizeClass: "", note: "" },
                ])
              }
            >
              + {t("dossierDetail.editDevisAddPanel")}
            </button>
          </div>
          {panels.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
                    <th className="py-1 pr-2">{t("dossierDetail.devisPanelCol")}</th>
                    <th className="py-1 pr-2">{t("dossierDetail.devisImpactsCol")}</th>
                    <th className="py-1 pr-2">{t("dossierDetail.devisClassCol")}</th>
                    <th className="py-1 pr-2">{t("dossierDetail.devisMethodCol")}</th>
                    <th className="py-1 pr-2">{t("dossierDetail.devisNoteCol")}</th>
                    <th className="py-1" />
                  </tr>
                </thead>
                <tbody>
                  {panels.map((p, i) => (
                    <tr key={i} className="border-t border-neutral-800/70">
                      <td className="py-1 pr-2">
                        <input
                          value={p.panel}
                          onChange={(e) => setPanel(i, "panel", e.target.value)}
                          className={`${inputClass} h-8`}
                          placeholder="Porte Avant Gauche"
                        />
                      </td>
                      <td className="py-1 pr-2 w-20">
                        <input
                          value={p.impacts}
                          onChange={(e) => setPanel(i, "impacts", e.target.value)}
                          inputMode="numeric"
                          className={`${inputClass} h-8 text-right font-mono`}
                        />
                      </td>
                      <td className="py-1 pr-2 w-20">
                        <input
                          value={p.sizeClass}
                          onChange={(e) => setPanel(i, "sizeClass", e.target.value)}
                          className={`${inputClass} h-8`}
                          placeholder="D1"
                        />
                      </td>
                      <td className="py-1 pr-2 w-32">
                        <select
                          value={p.method}
                          onChange={(e) => setPanel(i, "method", e.target.value)}
                          className={`${inputClass} h-8`}
                        >
                          {DEVIS_METHODS.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          value={p.note}
                          onChange={(e) => setPanel(i, "note", e.target.value)}
                          className={`${inputClass} h-8`}
                        />
                      </td>
                      <td className="py-1 text-right">
                        <button
                          type="button"
                          className="text-xs text-red-400 hover:underline"
                          onClick={() => setPanels((ps) => ps.filter((_, j) => j !== i))}
                          aria-label="remove-panel"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Field label={t("dossierDetail.deposeReposeLabel")}>
            <input
              value={deposeRepose}
              onChange={(e) => setDeposeRepose(e.target.value)}
              className={inputClass}
            />
          </Field>
        </section>

        <section className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            {t("dossierDetail.editDevisAmounts")}
          </h4>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {(
              [
                ["Total DSP", totalDsp, setTotalDsp, "devis-total-dsp"],
                ["Dégarnissage", degarnissage, setDegarnissage, "devis-degarnissage"],
                ["Carrosserie", carrosserie, setCarrosserie, "devis-carrosserie"],
                ["Pièces", pieces, setPieces, "devis-pieces"],
                ["Pare-brise", pareBrise, setPareBrise, "devis-pare-brise"],
              ] as Array<[string, string, (v: string) => void, string]>
            ).map(([label, value, set, tid]) => (
              <Field key={label} label={label}>
                <input
                  value={value}
                  onChange={(e) => set(e.target.value)}
                  inputMode="decimal"
                  data-testid={tid}
                  className={`${inputClass} text-right font-mono`}
                />
              </Field>
            ))}
          </div>
          <div className="space-y-1">
            {outros.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={o.label}
                  onChange={(e) =>
                    setOutros((os) =>
                      os.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                    )
                  }
                  placeholder="Prestations diverses"
                  className={inputClass}
                />
                <input
                  value={o.value}
                  onChange={(e) =>
                    setOutros((os) =>
                      os.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)),
                    )
                  }
                  inputMode="decimal"
                  className={`${inputClass} w-32 text-right font-mono`}
                />
                <button
                  type="button"
                  className="text-xs text-red-400"
                  onClick={() => setOutros((os) => os.filter((_, j) => j !== i))}
                  aria-label="remove-outro"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-xs text-lime hover:underline"
              onClick={() => setOutros((os) => [...os, { label: "", value: "" }])}
            >
              + {t("dossierDetail.editDevisAddOther")}
            </button>
          </div>
          <Field label={t("dossierDetail.editDevisRemiseExc")}>
            <input
              value={remiseValor}
              onChange={(e) => setRemise(e.target.value)}
              inputMode="decimal"
              data-testid="devis-remise-exc"
              className={`${inputClass} text-right font-mono`}
            />
          </Field>
          <p className="text-[11px] text-text-muted">{t("dossierDetail.editDevisRemiseExcHint")}</p>
          <p className="text-sm">
            <span className="text-neutral-500">Total HT</span>{" "}
            <span className="font-mono font-semibold" data-testid="devis-total-ht">
              {formatMoney(total, { decimals: 2 })}
            </span>
          </p>
        </section>

        <Field label={t("dossierDetail.editDevisReason")}>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={inputClass}
            maxLength={500}
            data-testid="devis-reason"
          />
        </Field>
        {error && <p className="text-xs text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-neutral-800 pt-3">
          <button type="button" onClick={onClose} className="btn">
            {t("dossierDetail.cancel")}
          </button>
          <button
            type="submit"
            disabled={mut.isPending || reason.trim().length < 3 || total <= 0}
            className="btn btn--primary"
            data-testid="devis-save"
          >
            {mut.isPending ? "…" : t("dossierDetail.editDevisSave")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// TAB: LINHAS
// ============================================================================

function TabLinhas({
  companyId,
  dossierId,
  dossierDeleted,
  isMirrorDossier = false,
  accessibleCompanies,
  recepcao,
  dossierSource,
  dossierInvoiceDate,
}: {
  companyId: number;
  dossierId: number;
  dossierDeleted: boolean;
  /** CEO 2026-10-12 — data do reparo: carro do eDSP conta pela ordem (invoice_date). */
  dossierSource?: string;
  dossierInvoiceDate?: string | null;
  /** F-franquia-nas-linhas — franquia da recepção (null = sem recepção). */
  recepcao: { franchise: boolean; amountTtc: number | null } | null;
  /** F-mirror-source-split — espelho: faturado da linha é o valor REAL (BS→ES). */
  isMirrorDossier?: boolean;
  accessibleCompanies: ReadonlyArray<{ id: number; name: string }>;
}) {
  const modals = useModals();
  const canEdit = useCanEdit();
  const tr = useT();
  const utils = trpc.useUtils();
  const linesQuery = trpc.serviceLines.list.useQuery({ companyId, dossierId });
  // F-acesso-por-menu — gerente de chantier: o card mostra o trabalho (quem,
  // o quê, quando, devis/faturado/remise) e NUNCA dinheiro de técnico/casa.
  const { grantOnly } = useGrantOnly();
  // Carro do eDSP: as datas "feito" das linhas são a data do IMPORT, não do
  // reparo — o reparo é a data da ordem (invoice_date).
  const doEdsp = dossierSource === "edsp_import";
  // F-gerente-no-dossier — comissão do responsável do chantier (derivada).
  const managerQuery = trpc.dossiers.managerCommission.useQuery(
    { companyId, dossierId },
    { enabled: !grantOnly },
  );
  const gerenteTotal = (managerQuery.data?.rows ?? []).reduce((sum, m) => sum + m.amount, 0);
  // CEO 2026-10-27 — avoirs do carro saem do lucro da casa (só ela absorve).
  const dossierInfoQ = trpc.dossiers.getById.useQuery(
    { companyId, dossierId },
    { enabled: !grantOnly },
  );
  const avoirsCarro = dossierInfoQ.data?.avoirs ?? null;
  const avoirsTotal = avoirsCarro ? avoirsCarro.ca + avoirsCarro.perda : 0;
  // F-reatribuir — só o Super Admin troca técnico dentro do dossier.
  const { user: authUser } = useAuth();
  // CEO 2026-10-02: admin da empresa (não só super admin) reatribui cada
  // etapa — PDR, desmontagem, montagem — em qualquer dossier.
  const podeReatribuir =
    authUser?.isSuperAdmin === true ||
    (authUser?.memberships ?? []).some(
      (m) => m.companyId === companyId && m.active && m.role === "admin",
    );
  const [reassignLine, setReassignLine] = useState<{
    id: number;
    serviceType: string;
    technicianId: number;
    montageTechnicianId: number | null;
    demontageDone: boolean;
    montageDone: boolean;
    dspDone: boolean;
    demontageExternal: boolean;
    montageExternal: boolean;
  } | null>(null);
  const techniciansQuery = trpc.technicians.list.useQuery({
    companyId,
    active: true,
    limit: 200,
    offset: 0,
  });

  // F-comm-cleanup C5 — line cuja comissão está sendo editada (modal aberto).
  // null = nenhum modal aberto.
  const [editCommissionLineId, setEditCommissionLineId] = useState<number | null>(null);
  // F-edsp-activities — linha de atividade (peinture/pièces) em edição.
  const [editActivityLine, setEditActivityLine] = useState<{
    id: number;
    serviceType: string;
    orcamento: string | null;
    faturado: string | null;
    custo: string | null;
  } | null>(null);
  const technicians = techniciansQuery.data?.rows ?? [];
  const technicianById = new Map(technicians.map((t) => [t.id, t]));
  const companyByLineId = new Map(accessibleCompanies.map((c) => [c.id, c]));

  const changeStatus = trpc.serviceLines.changeStatus.useMutation({
    onSuccess: () => {
      void utils.serviceLines.invalidate();
      // "Cancelar linha" muda status do carro, totais e OR.
      void utils.dossiers.invalidate();
      void utils.repairOrders.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: tr("dossierDetail.errorTitle"),
        message: `${tr("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {tr("dossierDetail.serviceLinesTitle")}
        </h3>
        {/* CEO 2026-10-13 — adiantar ao técnico antes de faturar: a proforma
            não depende da fatura do cliente (o assistente já vem com o carro). */}
        {canEdit &&
          !grantOnly &&
          !dossierDeleted &&
          (linesQuery.data ?? []).some(
            (l) =>
              l.technicianPaymentStatus === "pending" &&
              l.serviceType !== "peinture" &&
              l.serviceType !== "pieces",
          ) && (
            <Link
              href={`/proformas/nova?dossierIds=${dossierId}`}
              className="rounded-md border border-lime/50 bg-lime/10 px-3 py-1 text-xs font-medium text-lime hover:bg-lime/20"
              title={tr("dossierDetail.requestProformaTitle")}
              data-testid="dossier-request-proforma"
            >
              💶 {tr("dossierDetail.requestProformaBtn")}
            </Link>
          )}
        {/* F-comm-cleanup C5 — botão "+ Adicionar linha" removido. No modelo V3
            dossier = fatura importada do eDSP; operador não cria linhas
            manualmente. Para editar comissão de uma linha existente, use o
            botão "Editar comissão" no card. */}
      </div>
      {/* F-valor-fixo-dossier (CEO 2026-10-02) — valor fixo do técnico neste
          carro, definido ANTES do claim; vale para quem assumir. */}
      {!grantOnly && (
        <ValorFixoTecnico companyId={companyId} dossierId={dossierId} canEdit={canEdit} />
      )}
      {/* F-franquia-nas-linhas (CEO 2026-09-28: "continuo não encontrando")
          — a franquia muda o valor das linhas; o CEO confere o carro para
          faturar AQUI, não no Resumo. Mesmo componente do Resumo. */}
      {!dossierDeleted && (
        <FranquiaRecepcao
          companyId={companyId}
          dossierId={dossierId}
          franchise={recepcao?.franchise ?? false}
          amountTtc={recepcao?.amountTtc ?? null}
          canEdit={canEdit}
          semRecepcao={recepcao == null}
        />
      )}

      {linesQuery.isLoading && (
        <p className="text-sm text-neutral-500">{tr("dossierDetail.loading")}</p>
      )}
      {linesQuery.error && <p className="text-sm text-red-400">{linesQuery.error.message}</p>}
      {linesQuery.data?.length === 0 && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {tr("dossierDetail.linesEmpty")}
        </p>
      )}

      <div className="space-y-3">
        {(linesQuery.data ?? []).map((line) => {
          const technician = technicianById.get(line.technicianId);
          const responsible = companyByLineId.get(line.responsibleCompanyId);
          // F-edsp-activities — peinture/pièces: linha SEM comissão de técnico
          // (100% casa). Card simplificado: valores + custo + margem.
          const isActivity = line.serviceType === "peinture" || line.serviceType === "pieces";
          const serviceTypeLabel =
            line.serviceType === "dsp"
              ? "DSP"
              : line.serviceType === "peinture"
                ? tr("dossierDetail.serviceTypePeinture")
                : line.serviceType === "pieces"
                  ? tr("dossierDetail.serviceTypePieces")
                  : tr("dossierDetail.serviceTypeMontage");
          return (
            <div
              key={line.id}
              className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <span className="text-xs uppercase tracking-wider text-neutral-500">
                    {serviceTypeLabel}
                  </span>
                  {/* F-atividade-sem-tecnico (CEO 2026-09-24, Tiguan DZ-185-ET):
                      linha 100% casa NÃO mostra técnico — o technician_id dela é
                      só preenchimento obrigatório (quem fez o 1º claim / o técnico
                      da linha do CSV), ninguém foi atribuído a carrosserie/pièces. */}
                  <h4 className="text-sm font-semibold">
                    {isActivity ? (
                      <span className="text-neutral-300">
                        {responsible?.name ?? `#${line.responsibleCompanyId}`}
                      </span>
                    ) : (
                      <>
                        {technician?.name ??
                          `${tr("dossierDetail.technician")} #${line.technicianId}`}{" "}
                        ·{" "}
                        <span className="text-neutral-500">
                          {responsible?.name ?? `#${line.responsibleCompanyId}`}
                        </span>
                      </>
                    )}
                  </h4>
                  {/* F-metades-no-dossier — a mesma história que o portal conta. */}
                  {line.serviceType === "montage_demontage" &&
                    (() => {
                      const monta = line.shares.find((s) => s.phase === "montage");
                      const dono = technician?.name ?? `#${line.technicianId}`;
                      const desmontaOk = line.demontageDoneAt != null;
                      const montaOk = (monta?.doneAt ?? line.montageDoneAt) != null;
                      return (
                        <p className="mt-0.5 text-xs text-neutral-400">
                          {tr("dossierDetail.phaseDemontage")}:{" "}
                          {line.demontageExternal ? tr("dossierDetail.phaseExternal") : dono}{" "}
                          <span className={desmontaOk ? "text-lime" : "text-neutral-600"}>
                            {desmontaOk ? "✓" : "○"}
                          </span>
                          {desmontaOk && !doEdsp && (
                            <span className="text-neutral-500">
                              {" "}
                              {dataCurta(line.demontageDoneAt)}
                            </span>
                          )}
                          {/* F-dossier-metades-valor (CEO 2026-09-28: "cálculos
                              confusos") — quanto cada técnico ganha na sua metade. */}
                          {monta && line.technicianAmount != null && !grantOnly && (
                            <span className="font-mono text-neutral-300">
                              {" "}
                              {formatMoney(Number(line.technicianAmount), { decimals: 2 })}
                            </span>
                          )}
                          {" · "}
                          {tr("dossierDetail.phaseMontage")}:{" "}
                          {line.montageExternal
                            ? tr("dossierDetail.phaseExternal")
                            : (monta?.technicianName ?? dono)}{" "}
                          <span className={montaOk ? "text-lime" : "text-neutral-600"}>
                            {montaOk ? "✓" : "○"}
                          </span>
                          {montaOk && !doEdsp && (
                            <span className="text-neutral-500">
                              {" "}
                              {dataCurta(monta?.doneAt ?? line.montageDoneAt)}
                            </span>
                          )}
                          {monta?.amount != null && !grantOnly && (
                            <span className="font-mono text-neutral-300">
                              {" "}
                              {formatMoney(Number(monta.amount), { decimals: 2 })}
                            </span>
                          )}
                        </p>
                      );
                    })()}
                  {line.serviceType === "dsp" && (
                    <p className="mt-0.5 text-xs text-neutral-400">
                      {/* CEO 2026-10-12: "no dossier não há data de reparação". */}
                      {doEdsp ? (
                        <span className="text-lime">
                          ✓ {tr("dossierDetail.phasePdrDone")} ·{" "}
                          {tr("dossierDetail.repairEdspOrderPre")} {dataCurta(dossierInvoiceDate)}
                        </span>
                      ) : line.dspDoneAt != null ? (
                        <span className="text-lime">
                          ✓ {tr("dossierDetail.phasePdrDone")} {dataCurta(line.dspDoneAt)}
                        </span>
                      ) : (
                        <span className="text-neutral-600">
                          ○ {tr("dossierDetail.phasePdrOpen")}
                        </span>
                      )}
                      {!doEdsp && (
                        <span className="text-neutral-500">
                          {" · "}
                          {tr("dossierDetail.lineTakenOnPre")} {dataCurta(line.createdAt)}
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {isActivity ? (
                    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs bg-violet-500/10 border border-violet-500/30 text-violet-300">
                      {tr("dossierDetail.activityBadge")}
                    </span>
                  ) : grantOnly ? null : (
                    <>
                      <StatusBadge
                        label={tr("dossierDetail.intermediary")}
                        value={line.intermediaryPassthroughStatus}
                      />
                      <StatusBadge
                        label={tr("dossierDetail.technicianInvoice")}
                        value={line.technicianInvoiceStatus}
                      />
                      <StatusBadge
                        label={tr("dossierDetail.payment")}
                        value={line.technicianPaymentStatus}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* F-dossier-detail-v3-fields (2026-05-27): mostra campos V3
                  (clientPct/technicianPct/intermediaryPct/eurostormPct +
                  Amounts) quando commission_model='complementary'; fallback
                  para campos legacy nos casos antigos. Em complementary, o
                  Intercompany efetivo é a soma técnico+intermediário (= o
                  que Euro Storm paga à empresa do técnico via proforma). */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {/* F-line-values-money (2026-09-09, CEO: "números estranhos") —
                    o pro-rata do Total Facture produz 4 casas (1878.6993);
                    exibe formatado como dinheiro, igual ao resto da UI. */}
                <ValueBlock label={tr("dossierDetail.quote")} value={line.orcamento} money />
                {/* F-dossier-faturado-fix (2026-05-27): em complementary,
                    Faturado = orcamento − client_amount (Total Facture da
                    liste grêle). line.faturado herdado do importador antigo
                    ficou igual ao orcamento; mostramos o valor correto. */}
                <ValueBlock
                  label={tr("dossierDetail.invoiced")}
                  value={
                    line.commissionModel === "complementary" && !isActivity && !isMirrorDossier
                      ? // F-franquia-na-fatura — mesma conta do que a fatura cobra
                        // (dossierInvoiceBreakdown): devis − remise − franquia já
                        // descontada do faturado; nunca acima do faturado.
                        (
                          Math.min(
                            Number(line.faturado ?? line.orcamento ?? 0),
                            Number(line.orcamento ?? 0) -
                              Number(line.clientAmount ?? 0) -
                              Math.max(
                                0,
                                Number(line.faturadoSemFranquia ?? line.faturado ?? 0) -
                                  Number(line.faturado ?? 0),
                              ),
                          ) * fatorFaturavel(line)
                        ).toFixed(4)
                      : line.faturado != null
                        ? (Number(line.faturado) * fatorFaturavel(line)).toFixed(4)
                        : line.faturado
                  }
                  money
                />
                {fatorFaturavel(line) < 1 && (
                  <p
                    className="col-span-full text-[11px] text-amber-300"
                    data-testid={`line-terceiro-${line.id}`}
                  >
                    {fatorFaturavel(line) === 0
                      ? tr("dossierDetail.thirdPartyNotBilledAll")
                      : tr("dossierDetail.thirdPartyNotBilledHalf")}
                  </p>
                )}
                {isActivity ? (
                  <>
                    <ValueBlock label={tr("dossierDetail.activityCost")} value={line.custo} money />
                    <ValueBlock
                      label={tr("dossierDetail.activityMargin")}
                      value={
                        line.faturado != null
                          ? (Number(line.faturado) - Number(line.custo ?? 0)).toFixed(2)
                          : null
                      }
                      money
                    />
                  </>
                ) : line.commissionModel === "complementary" ? (
                  <>
                    {/* F-dossier-base-franquia — os % incidem sobre a base
                        DEPOIS da franquia, não sobre o devis mostrado acima. */}
                    {line.orcamentoPosFranquia != null && (
                      <ValueBlock
                        label={tr("dossierDetail.baseAfterFranchise")}
                        value={line.orcamentoPosFranquia}
                        money
                      />
                    )}
                    <ValueBlock
                      label={tr("dossierDetail.customerPct")}
                      value={line.clientPct}
                      suffix="%"
                    />
                    <ValueBlock
                      label={tr("dossierDetail.customerEffective")}
                      value={line.clientAmount}
                      money
                    />
                    {!grantOnly && (
                      <>
                        <ValueBlock
                          label={tr("dossierDetail.technicianPct")}
                          value={line.technicianPct}
                          suffix="%"
                        />
                        <ValueBlock
                          label={tr("dossierDetail.technicianEffective")}
                          value={line.technicianAmount}
                          money
                        />
                        <ValueBlock
                          label={tr("dossierDetail.intermediaryPct")}
                          value={line.intermediaryPct}
                          suffix="%"
                        />
                        <ValueBlock
                          label={tr("dossierDetail.intermediaryEffective")}
                          value={line.intermediaryAmount}
                          money
                        />
                        {/* F-dossier-metades-valor — a metade que OUTRO técnico
                        assumiu sai da parte da casa; sem isto o % da casa
                        não fechava com o valor. */}
                        {line.shares
                          .filter((sh) => sh.amount != null)
                          .map((sh) => (
                            <ValueBlock
                              key={sh.phase}
                              label={`${sh.phase === "montage" ? tr("dossierDetail.phaseMontage") : tr("dossierDetail.phaseDemontage")} · ${sh.technicianName}`}
                              value={sh.amount}
                              money
                            />
                          ))}
                        <ValueBlock
                          label={tr("dossierDetail.eurostormPct")}
                          value={
                            line.shares.some((sh) => sh.amount != null) &&
                            Number(line.orcamentoPosFranquia ?? line.orcamento ?? 0) > 0
                              ? (
                                  (Number(line.eurostormAmount ?? 0) /
                                    Number(line.orcamentoPosFranquia ?? line.orcamento)) *
                                  100
                                ).toFixed(2)
                              : line.eurostormPct
                          }
                          suffix="%"
                        />
                        <ValueBlock
                          label={tr("dossierDetail.eurostormEffective")}
                          value={line.eurostormAmount}
                          money
                        />
                        <ValueBlock
                          // CEO 2026-10-02: "Intercompany" só quando a linha é de outra
                          // empresa do grupo / tem intermediário do grupo / espelho; no
                          // dossier próprio é só o repasse (técnico + intermediário).
                          label={
                            isMirrorDossier ||
                            line.responsibleCompanyId !== companyId ||
                            line.intermediaryCompanyId != null
                              ? tr("dossierDetail.intercompanyEffective")
                              : tr("dossierDetail.payoutEffective")
                          }
                          value={
                            line.technicianAmount != null && line.intermediaryAmount != null
                              ? (
                                  line.shares.reduce((sm, sh) => sm + Number(sh.amount ?? 0), 0) +
                                  Number(line.technicianAmount) +
                                  // F-mirror-source-split — no espelho o "interm" é a
                                  // fatia da Euro Storm (retida lá): a BS só paga o téc.
                                  (isMirrorDossier ? 0 : Number(line.intermediaryAmount))
                                ).toFixed(2)
                              : null
                          }
                          money
                        />
                      </>
                    )}
                  </>
                ) : grantOnly ? null : (
                  <>
                    <ValueBlock
                      label={tr("dossierDetail.eurostormPct")}
                      value={line.eurostormRetentionPct}
                      suffix="%"
                    />
                    <ValueBlock
                      label={tr("dossierDetail.eurostormEffective")}
                      value={line.eurostormRetentionAmount}
                      money
                    />
                    <ValueBlock
                      label={tr("dossierDetail.intermediaryPct")}
                      value={line.intermediaryRetentionPct}
                      suffix="%"
                    />
                    <ValueBlock
                      label={tr("dossierDetail.intermediaryEffective")}
                      value={line.intermediaryRetentionAmount}
                      money
                    />
                    <ValueBlock
                      label={tr("dossierDetail.technicianPct")}
                      value={line.technicianCommissionPct}
                      suffix="%"
                    />
                    <ValueBlock
                      label={tr("dossierDetail.technicianEffective")}
                      value={line.technicianCommissionAmount}
                      money
                    />
                    <ValueBlock
                      label={
                        isMirrorDossier ||
                        line.responsibleCompanyId !== companyId ||
                        line.intermediaryCompanyId != null
                          ? tr("dossierDetail.intercompanyEffective")
                          : tr("dossierDetail.payoutEffective")
                      }
                      value={line.intercompanyInvoiceAmount}
                      money
                    />
                  </>
                )}
                {line.technicianAmountKnown === false && !grantOnly && (
                  <div className="col-span-full text-xs text-amber-300 bg-amber-500/5 border border-amber-500/30 rounded px-2 py-1">
                    {tr("dossierDetail.technicianAmountUnknownWarn")}
                  </div>
                )}
              </div>

              {line.overrideReason && !grantOnly && (
                <p className="text-xs text-amber-300 bg-amber-500/5 border border-amber-500/30 rounded px-2 py-1">
                  {tr("dossierDetail.overrideLabel")}: {line.overrideReason}
                </p>
              )}

              {/* CEO 2026-10-27 (HJ-551-YJ) — por que a linha está sem comissão. */}
              {line.commissionBlock && !grantOnly && (
                <p
                  className="rounded-md border px-2 py-1 text-xs leading-snug"
                  style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
                  data-testid={`commission-block-${line.id}`}
                >
                  ⚠{" "}
                  {line.commissionBlock === "pro_sem_remise"
                    ? tr("dossierDetail.commBlockProSemRemise")
                    : tr("dossierDetail.commBlockSemPar")}
                </p>
              )}

              {canEdit && !grantOnly && (
                <div className="flex gap-1 flex-wrap pt-2 border-t border-neutral-800">
                  {/* F-comm-cleanup C5 — editar comissão (promove legacy → complementary
                    ou ajusta complementary). Destaca em linhas legacy onde pcts
                    estão zerados. Linha de atividade não tem comissão — abre a
                    edição de atividade (tipo/valores/custo). */}
                  {isActivity ? (
                    <button
                      onClick={() => setEditActivityLine(line)}
                      disabled={dossierDeleted}
                      className="rounded-md border border-violet-500/40 text-violet-300 hover:border-violet-400 px-2 py-0.5 text-xs"
                    >
                      {tr("dossierDetail.editActivityBtn")}
                    </button>
                  ) : (
                    <>
                      {podeReatribuir && (
                        <button
                          onClick={() =>
                            setReassignLine({
                              id: line.id,
                              serviceType: line.serviceType,
                              technicianId: line.technicianId,
                              montageTechnicianId:
                                line.shares.find((s) => s.phase === "montage")?.technicianId ??
                                null,
                              demontageDone: line.demontageDoneAt != null,
                              montageDone:
                                (line.shares.find((s) => s.phase === "montage")?.doneAt ??
                                  line.montageDoneAt) != null,
                              dspDone: line.dspDoneAt != null,
                              demontageExternal: line.demontageExternal,
                              montageExternal: line.montageExternal,
                            })
                          }
                          disabled={
                            dossierDeleted ||
                            (line.technicianPaymentStatus !== "pending" &&
                              !(
                                line.technicianPaymentStatus === "paid" &&
                                Math.abs(
                                  Number(
                                    line.technicianAmount ?? line.technicianCommissionAmount ?? 0,
                                  ),
                                ) < 0.005
                              ))
                          }
                          title={tr("dossierDetail.reassignTitle")}
                          className="rounded-md border border-amber-500/50 px-2 py-0.5 text-xs text-amber-300 hover:border-amber-400 disabled:opacity-40"
                        >
                          {tr("dossierDetail.reassignBtn")}
                        </button>
                      )}
                      {/* CEO 2026-10-19 (GA-629-JB): PDR assumido por engano e
                          não havia como tirar a linha — "Reatribuir" só troca
                          o técnico. Só Admin (o servidor confere). */}
                      {line.technicianPaymentStatus === "pending" && (
                        <button
                          type="button"
                          onClick={async () => {
                            const reason = await modals.promptReason({
                              title: tr("dossierDetail.cancelLineTitle"),
                              message: tr("dossierDetail.cancelLineMsg"),
                              placeholder: tr("dossierDetail.derogReasonPh"),
                              confirmLabel: tr("dossierDetail.cancelLineBtn"),
                              required: true,
                              maxLength: 500,
                            });
                            if (reason == null || reason.trim().length < 3) return;
                            changeStatus.mutate({
                              companyId,
                              lineId: line.id,
                              technicianPaymentStatus: "cancelled",
                              reason: reason.trim(),
                            });
                          }}
                          disabled={dossierDeleted || changeStatus.isPending}
                          title={tr("dossierDetail.cancelLineTitle")}
                          className="rounded-md border border-red-500/50 px-2 py-0.5 text-xs text-red-300 hover:border-red-400 disabled:opacity-40"
                          data-testid={`cancel-line-${line.id}`}
                        >
                          {tr("dossierDetail.cancelLineBtn")}
                        </button>
                      )}
                      <button
                        onClick={() => setEditCommissionLineId(line.id)}
                        disabled={dossierDeleted}
                        className={`rounded-md border px-2 py-0.5 text-xs ${
                          line.commissionModel === "legacy"
                            ? "border-lime/60 bg-lime/10 text-lime hover:border-lime"
                            : "border-neutral-700 hover:border-lime hover:text-lime"
                        }`}
                        title={
                          line.commissionModel === "legacy"
                            ? tr("dossierDetail.calcCommissionTitle")
                            : tr("dossierDetail.editCommission")
                        }
                      >
                        {line.commissionModel === "legacy"
                          ? tr("dossierDetail.calcCommissionBtn")
                          : tr("dossierDetail.editCommission")}
                      </button>
                    </>
                  )}
                  {line.technicianInvoiceStatus === "pending" && (
                    <button
                      onClick={() =>
                        changeStatus.mutate({
                          companyId,
                          lineId: line.id,
                          technicianInvoiceStatus: "received",
                        })
                      }
                      disabled={changeStatus.isPending}
                      className="rounded-md border border-sky-500/40 hover:border-sky-500 text-sky-300 px-2 py-0.5 text-xs"
                    >
                      {tr("dossierDetail.markInvoiceReceived")}
                    </button>
                  )}
                  {line.technicianInvoiceStatus === "received" && (
                    <button
                      onClick={() =>
                        changeStatus.mutate({
                          companyId,
                          lineId: line.id,
                          technicianInvoiceStatus: "approved",
                        })
                      }
                      disabled={changeStatus.isPending}
                      className="rounded-md border border-cyan-500/40 hover:border-cyan-500 text-cyan-300 px-2 py-0.5 text-xs"
                    >
                      {tr("dossierDetail.approveInvoice")}
                    </button>
                  )}
                  {line.technicianInvoiceStatus === "received" && (
                    <button
                      onClick={async () => {
                        const reason = await modals.promptReason({
                          title: tr("dossierDetail.rejectTechnicianInvoiceTitle"),
                          message: tr("dossierDetail.reasonRequired"),
                          required: true,
                          tone: "warning",
                        });
                        if (!reason) return;
                        changeStatus.mutate({
                          companyId,
                          lineId: line.id,
                          technicianInvoiceStatus: "rejected",
                          reason,
                        });
                      }}
                      disabled={changeStatus.isPending}
                      className="rounded-md border border-amber-500/40 hover:border-amber-500 text-amber-300 px-2 py-0.5 text-xs"
                    >
                      {tr("dossierDetail.rejectInvoice")}
                    </button>
                  )}
                  {line.technicianPaymentStatus === "pending" &&
                    line.technicianInvoiceStatus === "approved" && (
                      <button
                        onClick={() =>
                          changeStatus.mutate({
                            companyId,
                            lineId: line.id,
                            technicianPaymentStatus: "paid",
                          })
                        }
                        disabled={changeStatus.isPending}
                        className="rounded-md border border-lime/40 hover:border-lime text-lime px-2 py-0.5 text-xs"
                      >
                        {tr("dossierDetail.markPaid")}
                      </button>
                    )}
                </div>
              )}
            </div>
          );
        })}

        {/* F-lines-total (pedido do CEO 2026-09-12) — TOTAL do dossier ao
            final das linhas: soma dos mesmos valores exibidos nos cards
            (Faturado segue a regra do card: complementary = orçamento −
            cliente; técnico soma amount V3 OU legado; intercompany no
            espelho não soma o interm — a BS só paga o técnico). */}
        {/* F-gerente-no-dossier (CEO 2026-09-26) — o responsável do chantier
            também comissiona (só débosselage, base devis pós-franquia, sai da
            parte da casa). Antes só aparecia no portal dele. */}
        {(managerQuery.data?.rows ?? []).map((m) => (
          <CartaoGerente
            key={m.userId}
            m={m}
            companyId={companyId}
            dossierId={dossierId}
            podeEditar={authUser?.isSuperAdmin === true && !dossierDeleted}
          />
        ))}

        {(linesQuery.data ?? []).length > 1 &&
          (() => {
            const rows = linesQuery.data ?? [];
            const n = (v: number | string | null | undefined) => Number(v ?? 0) || 0;
            const tot = {
              orcamento: 0,
              faturado: 0,
              cliente: 0,
              tecnico: 0,
              interm: 0,
              eurostorm: 0,
              intercompany: 0,
            };
            for (const line of rows) {
              const isActivity = line.serviceType === "peinture" || line.serviceType === "pieces";
              tot.orcamento += n(line.orcamento);
              // F-franquia-na-fatura — mesma conta do que a fatura cobra.
              // CEO 2026-10-26 — metade do dégarnissage de terceiro não é faturada.
              tot.faturado +=
                (line.commissionModel === "complementary" && !isActivity && !isMirrorDossier
                  ? Math.min(
                      n(line.faturado ?? line.orcamento),
                      n(line.orcamento) -
                        n(line.clientAmount) -
                        Math.max(
                          0,
                          n(line.faturadoSemFranquia ?? line.faturado) - n(line.faturado),
                        ),
                    )
                  : n(line.faturado)) * fatorFaturavel(line);
              // F-dossier-metades-valor — metade de outro técnico conta como técnico.
              const metades = line.shares.reduce((sm, sh) => sm + n(sh.amount), 0);
              tot.tecnico += metades;
              tot.intercompany += metades;
              if (isActivity) continue;
              if (line.commissionModel === "complementary") {
                tot.cliente += n(line.clientAmount);
                tot.tecnico += n(line.technicianAmount);
                tot.interm += n(line.intermediaryAmount);
                tot.eurostorm += n(line.eurostormAmount);
                tot.intercompany +=
                  n(line.technicianAmount) + (isMirrorDossier ? 0 : n(line.intermediaryAmount));
              } else {
                tot.tecnico += n(line.technicianCommissionAmount);
                tot.interm += n(line.intermediaryRetentionAmount);
                tot.eurostorm += n(line.eurostormRetentionAmount);
                tot.intercompany += n(line.intercompanyInvoiceAmount);
              }
            }
            return (
              <div className="rounded-lg border border-lime/30 bg-lime/5 p-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-lime">
                  {tr("dossierDetail.linesTotalTitle")}
                </span>
                <div className="mt-2 grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
                  <ValueBlock label={tr("dossierDetail.quote")} value={tot.orcamento} money />
                  <ValueBlock label={tr("dossierDetail.invoiced")} value={tot.faturado} money />
                  {tot.cliente > 0 && (
                    <ValueBlock
                      label={tr("dossierDetail.customerEffective")}
                      value={tot.cliente}
                      money
                    />
                  )}
                  <ValueBlock
                    label={tr("dossierDetail.technicianEffective")}
                    value={tot.tecnico}
                    money
                  />
                  {tot.interm > 0 && (
                    <ValueBlock
                      label={tr("dossierDetail.intermediaryEffective")}
                      value={tot.interm}
                      money
                    />
                  )}
                  {gerenteTotal > 0 && (
                    <ValueBlock
                      label={tr("dossierDetail.managerEffective")}
                      value={gerenteTotal}
                      money
                    />
                  )}
                  {avoirsTotal > 0.005 && (
                    <ValueBlock
                      label={tr("dossierDetail.avoirsEffective")}
                      value={-avoirsTotal}
                      money
                    />
                  )}
                  <ValueBlock
                    label={tr("dossierDetail.eurostormEffective")}
                    value={tot.eurostorm - gerenteTotal - avoirsTotal}
                    money
                  />
                  <ValueBlock
                    label={
                      isMirrorDossier ||
                      rows.some(
                        (l) =>
                          l.responsibleCompanyId !== companyId || l.intermediaryCompanyId != null,
                      )
                        ? tr("dossierDetail.intercompanyEffective")
                        : tr("dossierDetail.payoutEffective")
                    }
                    value={tot.intercompany}
                    money
                  />
                </div>
                {avoirsCarro && avoirsTotal > 0.005 && (
                  <p
                    className="mt-2 text-[11px] text-neutral-500"
                    data-testid="dossier-avoirs-note"
                  >
                    {tr("dossierDetail.avoirsNotePre")}{" "}
                    {avoirsCarro.refs
                      .map(
                        (r) =>
                          `${r.number} ${formatMoney(r.ht, { decimals: 2 })}${r.rateado ? ` (${tr("dossierDetail.avoirsSplit")})` : ""}`,
                      )
                      .join(" · ")}
                  </p>
                )}
              </div>
            );
          })()}
      </div>

      {/* F-reatribuir — Super Admin troca o técnico (PDR / desmontagem / montagem). */}
      {reassignLine !== null && (
        <ReassignTechnicianModal
          companyId={companyId}
          line={reassignLine}
          technicians={technicians.map((t) => ({ id: t.id, name: t.name }))}
          onClose={() => setReassignLine(null)}
          onSaved={() => {
            setReassignLine(null);
            utils.serviceLines.invalidate();
            utils.dossiers.invalidate();
          }}
        />
      )}

      {/* F-comm-cleanup C5 — Modal de edição da comissão de uma linha existente.
          Promove legacy → complementary OU ajusta complementary. Pre-popula com
          o par técnico×cliente (F-tcc2) e a retenção do cliente. */}
      {canEdit &&
        editCommissionLineId !== null &&
        (() => {
          const line = (linesQuery.data ?? []).find((l) => l.id === editCommissionLineId);
          if (!line) return null;
          return (
            <EditCommissionModal
              companyId={companyId}
              line={line}
              onClose={() => setEditCommissionLineId(null)}
              onSaved={() => {
                setEditCommissionLineId(null);
                utils.serviceLines.invalidate();
                utils.dossiers.invalidate();
              }}
            />
          );
        })()}

      {/* F-edsp-activities — edição de linha de atividade (tipo/valores/custo). */}
      {canEdit && editActivityLine !== null && (
        <EditActivityModal
          companyId={companyId}
          line={editActivityLine}
          onClose={() => setEditActivityLine(null)}
          onSaved={() => {
            setEditActivityLine(null);
            utils.serviceLines.invalidate();
            utils.dossiers.invalidate();
          }}
        />
      )}
    </section>
  );
}

/**
 * F-edsp-activities — Modal de edição de linha de ATIVIDADE (peinture/pièces).
 * Sem comissão (100% casa): edita o tipo (reclassificação da heurística do
 * import), valores e o custo de compra (margem real = faturado − custo).
 */
function EditActivityModal({
  companyId,
  line,
  onClose,
  onSaved,
}: {
  companyId: number;
  line: {
    id: number;
    serviceType: string;
    orcamento: string | null;
    faturado: string | null;
    custo: string | null;
  };
  onClose: () => void;
  onSaved: () => void;
}) {
  const tr = useT();
  const modals = useModals();
  const [serviceType, setServiceType] = useState<"peinture" | "pieces">(
    line.serviceType === "pieces" ? "pieces" : "peinture",
  );
  const [faturado, setFaturado] = useState(line.faturado != null ? Number(line.faturado) : 0);
  const [custo, setCusto] = useState<string>(line.custo != null ? String(Number(line.custo)) : "");

  const mutation = trpc.serviceLines.updateActivity.useMutation({
    onSuccess: onSaved,
    onError: (e) =>
      void modals.alert({
        title: tr("dossierDetail.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  const custoNum = custo.trim() === "" ? null : Number(custo.replace(",", "."));
  const margem = faturado - (custoNum ?? 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-lg border border-neutral-700 bg-neutral-900 p-5 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
          {tr("dossierDetail.editActivityTitle")}
        </h3>
        <label className="block text-xs text-neutral-400">
          {tr("dossierDetail.activityTypeLabel")}
          <select
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value as "peinture" | "pieces")}
            className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-sm"
          >
            <option value="peinture">{tr("dossierDetail.serviceTypePeinture")}</option>
            <option value="pieces">{tr("dossierDetail.serviceTypePieces")}</option>
          </select>
        </label>
        <label className="block text-xs text-neutral-400">
          {tr("dossierDetail.invoiced")}
          <input
            type="number"
            min={0}
            step="0.01"
            value={faturado}
            onChange={(e) => setFaturado(Number(e.target.value))}
            className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <label className="block text-xs text-neutral-400">
          {tr("dossierDetail.activityCost")}
          <input
            type="text"
            inputMode="decimal"
            value={custo}
            onChange={(e) => setCusto(e.target.value)}
            placeholder="—"
            className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <p className="text-xs text-neutral-500">
          {tr("dossierDetail.activityMargin")}:{" "}
          <span className="font-mono text-neutral-300">{margem.toFixed(2)} €</span>
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs hover:border-neutral-500"
          >
            {tr("dossierDetail.cancel")}
          </button>
          <button
            onClick={() =>
              mutation.mutate({
                companyId,
                lineId: line.id,
                patch: {
                  serviceType,
                  faturado,
                  custo: custoNum,
                },
              })
            }
            disabled={mutation.isPending || !(faturado >= 0)}
            className="rounded-md border border-lime/60 bg-lime/10 text-lime px-3 py-1.5 text-xs hover:border-lime disabled:opacity-50"
          >
            {tr("dossierDetail.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * F-comm-cleanup C5 — Modal de edição da comissão de uma service_line existente.
 *
 * Aceita linha legacy (eDSP-importada sem cascade) OU complementary (já calculada).
 * Quando legacy, pré-popula com o par técnico×cliente (F-tcc2 — % + base +
 * intermediário do par) e a retenção do cliente (defaultRetentionPct). Quando
 * complementary, pré-popula com os valores atuais da linha. Ao salvar, chama
 * `serviceLines.editCommission` que promove para complementary (se necessário)
 * e persiste os 4 pcts + amounts calculados via calculateCommission.
 */
/**
 * F-reatribuir (CEO 2026-09-23) — o Super Admin escolhe quem é o técnico da
 * linha; na montagem, também quem fica com a 2ª metade. A comissão é refeita
 * pelo par do novo técnico no servidor.
 */
/**
 * F-trocar-cliente (CEO 2026-10-05) — troca o cliente do dossier. Mostra a
 * prévia do servidor (travas + o que muda em cada linha: remise, faturado,
 * parte do técnico) antes de confirmar; motivo obrigatório (auditoria).
 */
function ChangeCustomerModal({
  companyId,
  dossierId,
  currentCustomerId,
  currentCustomerName,
  initialCustomerId = null,
  onClose,
}: {
  companyId: number;
  dossierId: number;
  currentCustomerId: number;
  currentCustomerName: string;
  /** F-troca-cliente-devis — cliente proposto pelo devis reenviado, já selecionado. */
  initialCustomerId?: number | null;
  onClose: () => void;
}) {
  const tr = useT();
  const utils = trpc.useUtils();
  const [busca, setBusca] = useState("");
  const [customerId, setCustomerId] = useState<number | null>(initialCustomerId);
  const [atelierId, setAtelierId] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [avisos, setAvisos] = useState<string[] | null>(null);
  const clientesQ = trpc.customers.list.useQuery({
    companyId,
    active: true,
    q: busca.trim() || undefined,
    limit: 200,
    offset: 0,
  });
  const clientes = (clientesQ.data?.rows ?? []).filter((c) => c.id !== currentCustomerId);
  const ateliersQ = trpc.customers.listAteliers.useQuery(
    { companyId, customerId: customerId ?? 0 },
    { enabled: customerId != null },
  );
  const ateliers = (ateliersQ.data?.rows ?? []).filter((a) => a.active);
  // F-tarifa-atelier — a prévia depende do ateliê (par e remise por ateliê).
  const previaQ = trpc.dossiers.changeCustomerPreview.useQuery(
    { companyId, dossierId, customerId: customerId ?? 0, atelierId },
    { enabled: customerId != null, staleTime: 0 },
  );
  const previa = previaQ.data;
  const mut = trpc.dossiers.changeCustomer.useMutation({
    onSuccess: (r) => {
      void utils.dossiers.invalidate();
      void utils.serviceLines.invalidate();
      setAvisos(r.warnings);
    },
  });
  const sel = "w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm";
  const m2 = (n: number | null) => (n == null ? "—" : formatMoney(n, { decimals: 2 }));
  const pct = (n: number | null) => (n == null ? "—" : `${n.toFixed(2).replace(/\.00$/, "")} %`);
  const svc = (st: string) =>
    st === "dsp"
      ? "DSP"
      : st === "peinture"
        ? tr("dossierDetail.serviceTypePeinture")
        : st === "pieces"
          ? tr("dossierDetail.serviceTypePieces")
          : tr("dossierDetail.serviceTypeMontage");
  const bloqueado = !previa || previa.bloqueios.length > 0;
  const podeSalvar =
    customerId != null && !bloqueado && reason.trim().length >= 3 && !mut.isPending;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-900 p-5 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
          {tr("dossierDetail.chgCustTitle")}
        </h3>
        {avisos ? (
          <>
            <p className="rounded-md border border-lime/40 bg-lime/10 px-3 py-2 text-sm font-semibold text-lime">
              ✓ {tr("dossierDetail.chgCustDone")}
            </p>
            {avisos.map((w) => (
              <p key={w} className="text-xs text-amber-300">
                ⚠ {w}
              </p>
            ))}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime"
              >
                OK
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-neutral-400">
              {tr("dossierDetail.chgCustCurrent")}:{" "}
              <strong className="text-neutral-200">{currentCustomerName}</strong>
            </p>
            <label className="block text-xs text-neutral-400">
              {tr("dossierDetail.chgCustNew")}
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder={tr("dossierDetail.chgCustSearchPh")}
                className={sel + " mt-1"}
                data-testid="change-customer-search"
              />
              <select
                value={customerId ?? ""}
                onChange={(e) => {
                  setCustomerId(e.target.value ? Number(e.target.value) : null);
                  setAtelierId(null);
                }}
                className={sel + " mt-1"}
                data-testid="change-customer-select"
              >
                <option value="">{tr("dossierDetail.chgCustChoose")}</option>
                {/* CEO 2026-10-26 — o cliente atual fica na lista para trocar só o ateliê. */}
                <option value={currentCustomerId}>
                  {currentCustomerName} — {tr("dossierDetail.chgCustOnlyAtelier")}
                </option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
            </label>
            {customerId != null && ateliers.length > 0 && (
              <label className="block text-xs text-neutral-400">
                {tr("dossierDetail.chgCustAtelier")}
                <select
                  value={atelierId ?? ""}
                  onChange={(e) => setAtelierId(e.target.value ? Number(e.target.value) : null)}
                  className={sel + " mt-1"}
                >
                  <option value="">{tr("dossierDetail.chgCustNoAtelier")}</option>
                  {ateliers.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.city ? ` — ${a.city}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {customerId != null && previaQ.isLoading && (
              <p className="text-xs text-neutral-500">…</p>
            )}
            {previaQ.error && <p className="text-xs text-red-400">{previaQ.error.message}</p>}
            {previa && previa.bloqueios.length > 0 && (
              <div className="space-y-1 rounded-md border border-red-500/40 bg-red-500/10 p-2">
                <p className="text-xs font-semibold text-red-300">
                  {tr("dossierDetail.chgCustBlocked")}
                </p>
                {previa.bloqueios.map((b) => (
                  <p key={b} className="text-xs text-red-300">
                    • {b}
                  </p>
                ))}
              </div>
            )}
            {previa && previa.bloqueios.length === 0 && (
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-neutral-500">
                  {tr("dossierDetail.chgCustImpact")}
                </p>
                {previa.linhas.length > 0 && (
                  <div className="overflow-x-auto rounded-md border border-neutral-800">
                    <table className="w-full text-xs">
                      <thead className="text-neutral-500">
                        <tr>
                          <th className="px-2 py-1 text-left">
                            {tr("dossierDetail.chgCustColService")}
                          </th>
                          <th className="px-2 py-1 text-left">
                            {tr("dossierDetail.chgCustColTech")}
                          </th>
                          <th className="px-2 py-1 text-right">
                            {tr("dossierDetail.chgCustColRemise")}
                          </th>
                          <th className="px-2 py-1 text-right">
                            {tr("dossierDetail.chgCustColInvoiced")}
                          </th>
                          <th className="px-2 py-1 text-right">
                            {tr("dossierDetail.chgCustColTechAmount")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="tabular-nums">
                        {previa.linhas.map((l) => (
                          <tr key={l.lineId} className="border-t border-neutral-800">
                            <td className="px-2 py-1">{svc(l.serviceType)}</td>
                            <td className="px-2 py-1">{l.technicianName ?? "—"}</td>
                            <td className="px-2 py-1 text-right">
                              {pct(l.antes.clientPct)} → <strong>{pct(l.depois.clientPct)}</strong>
                            </td>
                            <td className="px-2 py-1 text-right">
                              {m2(l.antes.faturado)} → <strong>{m2(l.depois.faturado)}</strong>
                            </td>
                            <td className="px-2 py-1 text-right">
                              {m2(l.antes.technicianAmount)} →{" "}
                              <strong>{m2(l.depois.technicianAmount)}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {previa.avisos.map((w) => (
                  <p key={w} className="text-[11px] text-neutral-400">
                    ℹ {w}
                  </p>
                ))}
              </div>
            )}
            <label className="block text-xs text-neutral-400">
              {tr("dossierDetail.chgCustReason")}
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className={sel + " mt-1"}
                data-testid="change-customer-reason"
              />
              {reason.trim().length < 3 && (
                <span className="mt-1 block text-[11px] text-amber-300">
                  {tr("dossierDetail.reassignReasonMin")}
                </span>
              )}
            </label>
            {mut.error && <p className="text-xs text-red-400">{mut.error.message}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs hover:border-neutral-500"
              >
                {tr("dossierDetail.cancel")}
              </button>
              <button
                type="button"
                disabled={!podeSalvar}
                onClick={() =>
                  customerId != null &&
                  mut.mutate({
                    companyId,
                    dossierId,
                    customerId,
                    atelierId,
                    reason: reason.trim(),
                  })
                }
                className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
                data-testid="change-customer-save"
              >
                {mut.isPending ? "…" : tr("dossierDetail.chgCustSave")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReassignTechnicianModal({
  companyId,
  line,
  technicians,
  onClose,
  onSaved,
}: {
  companyId: number;
  line: {
    id: number;
    serviceType: string;
    technicianId: number;
    montageTechnicianId: number | null;
    demontageDone: boolean;
    montageDone: boolean;
    dspDone: boolean;
    demontageExternal: boolean;
    montageExternal: boolean;
  };
  technicians: Array<{ id: number; name: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const tr = useT();
  const isMontage = line.serviceType === "montage_demontage";
  // F-montagem-externa — metade feita por terceiro (garage): ninguém recebe.
  const [demontageExternal, setDemontageExternal] = useState<boolean>(line.demontageExternal);
  const [montageExternal, setMontageExternal] = useState<boolean>(line.montageExternal);
  const [technicianId, setTechnicianId] = useState<number>(line.technicianId);
  const [montageTechnicianId, setMontageTechnicianId] = useState<number | null>(
    line.montageTechnicianId,
  );
  // Estado do trabalho — carro já feito na vida real precisa das marcas para
  // a comissão da metade existir (o portal só paga metade concluída).
  const [demontageDone, setDemontageDone] = useState<boolean>(line.demontageDone);
  const [montageDone, setMontageDone] = useState<boolean>(line.montageDone);
  const [dspDone, setDspDone] = useState<boolean>(line.dspDone);
  const [reason, setReason] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const mut = trpc.serviceLines.reassignTechnician.useMutation({
    onSuccess: (r) => {
      if (r.warnings.length > 0) {
        setWarnings(r.warnings);
        window.setTimeout(onSaved, 2500);
      } else onSaved();
    },
  });
  // O dono atual pode não estar na lista (inativo/fixture): sem ele, o
  // <select> cairia em silêncio no PRIMEIRO da lista e o "Reatribuir" trocaria
  // o técnico sem ninguém ter escolhido nada.
  const lista = technicians.some((t) => t.id === line.technicianId)
    ? technicians
    : [...technicians, { id: line.technicianId, name: `#${line.technicianId}` }];
  const ordenados = [...lista].sort((a, b) => a.name.localeCompare(b.name));
  const sel = "w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-lg border border-neutral-700 bg-neutral-900 p-5 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
          {tr("dossierDetail.reassignTitle")}
        </h3>
        <label className="block text-xs text-neutral-400">
          {isMontage
            ? tr("dossierDetail.reassignDemontageLabel")
            : tr("dossierDetail.reassignPdrLabel")}
          <select
            value={technicianId}
            onChange={(e) => {
              setTechnicianId(Number(e.target.value));
              // CEO 2026-10-22 (FH-699-RA): escolher uma pessoa contradiz
              // "feita por terceiro" — a marca cai sozinha e a comissão é
              // refeita pelo par do novo técnico.
              setDemontageExternal(false);
            }}
            className={sel + " mt-1"}
          >
            {ordenados.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        {isMontage && (
          <label className="block text-xs text-neutral-400">
            {tr("dossierDetail.reassignMontageLabel")}
            <select
              value={montageTechnicianId ?? ""}
              onChange={(e) => {
                setMontageTechnicianId(e.target.value === "" ? null : Number(e.target.value));
                setMontageExternal(false);
              }}
              className={sel + " mt-1"}
            >
              <option value="">{tr("dossierDetail.reassignSameAsOwner")}</option>
              {ordenados
                .filter((t) => t.id !== technicianId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </label>
        )}
        <div className="space-y-1 rounded-md border border-neutral-800 bg-neutral-950/60 p-2">
          <p className="text-[11px] uppercase tracking-wider text-neutral-500">
            {tr("dossierDetail.reassignWorkState")}
          </p>
          {isMontage ? (
            <>
              <label className="flex items-center gap-2 text-xs text-neutral-300">
                <input
                  type="checkbox"
                  checked={demontageExternal || demontageDone}
                  disabled={demontageExternal}
                  onChange={(e) => setDemontageDone(e.target.checked)}
                />
                {tr("dossierDetail.reassignDemontageDone")}
              </label>
              <label className="flex items-center gap-2 text-xs text-amber-300">
                <input
                  type="checkbox"
                  checked={demontageExternal}
                  onChange={(e) => setDemontageExternal(e.target.checked)}
                />
                {tr("dossierDetail.reassignDemontageExternal")}
              </label>
              <label className="flex items-center gap-2 text-xs text-neutral-300">
                <input
                  type="checkbox"
                  checked={montageExternal || montageDone}
                  disabled={montageExternal}
                  onChange={(e) => setMontageDone(e.target.checked)}
                />
                {tr("dossierDetail.reassignMontageDone")}
              </label>
              <label className="flex items-center gap-2 text-xs text-amber-300">
                <input
                  type="checkbox"
                  checked={montageExternal}
                  onChange={(e) => setMontageExternal(e.target.checked)}
                />
                {tr("dossierDetail.reassignMontageExternal")}
              </label>
              {(demontageExternal || montageExternal) && (
                <p className="text-[11px] text-neutral-500">
                  {tr("dossierDetail.reassignExternalHint")}
                </p>
              )}
            </>
          ) : (
            <label className="flex items-center gap-2 text-xs text-neutral-300">
              <input
                type="checkbox"
                checked={dspDone}
                onChange={(e) => setDspDone(e.target.checked)}
              />
              {tr("dossierDetail.reassignDspDone")}
            </label>
          )}
        </div>
        <label className="block text-xs text-neutral-400">
          {tr("dossierDetail.reassignReasonLabel")}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className={sel + " mt-1"}
          />
          {reason.trim().length < 3 && (
            <span className="mt-1 block text-[11px] text-amber-300">
              {tr("dossierDetail.reassignReasonMin")}
            </span>
          )}
        </label>
        <p className="text-[11px] text-neutral-500">{tr("dossierDetail.reassignHint")}</p>
        {mut.error && <p className="text-xs text-red-400">{mut.error.message}</p>}
        {warnings.map((w) => (
          <p key={w} className="text-xs text-amber-300">
            ⚠ {w}
          </p>
        ))}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs hover:border-neutral-500"
          >
            {tr("dossierDetail.cancel")}
          </button>
          <button
            type="button"
            disabled={mut.isPending || reason.trim().length < 3}
            onClick={() =>
              mut.mutate({
                companyId,
                lineId: line.id,
                technicianId,
                montageTechnicianId:
                  isMontage && !montageExternal ? montageTechnicianId : undefined,
                demontageDone: isMontage ? demontageDone : undefined,
                montageDone: isMontage ? montageDone : undefined,
                dspDone: isMontage ? undefined : dspDone,
                demontageExternal: isMontage ? demontageExternal : undefined,
                montageExternal: isMontage ? montageExternal : undefined,
                reason: reason.trim(),
              })
            }
            className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
          >
            {tr("dossierDetail.reassignSave")}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditCommissionModal({
  companyId,
  line,
  onClose,
  onSaved,
}: {
  companyId: number;
  line: {
    id: number;
    technicianId: number;
    commissionModel: string;
    commissionBase: string;
    clientPct: string | null;
    technicianPct: string | null;
    intermediaryPct: string | null;
    orcamento: string | null;
    faturado: string | null;
    /** F-valor-fixo-tecnico — devis pós-franquia (base real em "orçamento"). */
    orcamentoPosFranquia?: string | null;
    dossierId: number;
  };
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const isLegacy = line.commissionModel === "legacy";
  // F-billing-company-label — nome da empresa faturadora (cache do companies.list).
  const companiesQuery = trpc.companies.list.useQuery();
  const billingCompanyName =
    (companiesQuery.data ?? []).find((c) => c.id === companyId)?.name ?? "Euro Storm";
  // Base: usa a já registrada na linha complementary, OU fallback "budget".
  const [commissionBase, setCommissionBase] = useState<"budget" | "invoiced">(
    line.commissionBase === "invoiced" ? "invoiced" : "budget",
  );
  // Pre-fill com valores atuais (complementary) ou string vazia (legacy — vai
  // ser preenchido pelo useEffect abaixo com defaults do técnico/cliente).
  const [clientPctInput, setClientPctInput] = useState(
    !isLegacy && line.clientPct != null && Number(line.clientPct) > 0 ? line.clientPct : "",
  );
  const [technicianPctInput, setTechnicianPctInput] = useState(
    !isLegacy && line.technicianPct != null && Number(line.technicianPct) > 0
      ? line.technicianPct
      : "",
  );
  const [intermediaryPctInput, setIntermediaryPctInput] = useState(
    !isLegacy && line.intermediaryPct != null && Number(line.intermediaryPct) > 0
      ? line.intermediaryPct
      : "",
  );
  const [cascadeValid, setCascadeValid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carrega técnico + dossier/cliente pra pre-popular defaults quando legacy.
  const techQuery = trpc.technicians.getById.useQuery(
    { companyId, technicianId: line.technicianId },
    { staleTime: 30_000 },
  );
  const dossierQuery = trpc.dossiers.getById.useQuery(
    { companyId, dossierId: line.dossierId },
    { staleTime: 30_000 },
  );
  const customerId = dossierQuery.data?.customerId ?? null;
  const customerQuery = trpc.customers.getById.useQuery(
    { companyId, customerId: customerId ?? 0 },
    { enabled: customerId !== null, staleTime: 30_000 },
  );
  // F-tcc2 — par técnico×cliente: fonte dos % de comissão (default descontinuado).
  const pairsQuery = trpc.technicians.listClientCommissions.useQuery(
    { companyId, technicianId: line.technicianId },
    { staleTime: 30_000 },
  );
  // F-tarifa-atelier — par do ateliê do dossier > par do cliente.
  const dossierAtelierId = dossierQuery.data?.atelierId ?? null;
  const pair =
    customerId != null
      ? ((pairsQuery.data ?? []).find(
          (p) => p.customerId === customerId && (p.atelierId ?? null) === dossierAtelierId,
        ) ??
        (pairsQuery.data ?? []).find((p) => p.customerId === customerId && p.atelierId == null))
      : undefined;

  // Pré-popula inputs com o par técnico×cliente + retenção do cliente (apenas
  // para linhas legacy — em complementary já foi preenchido no useState inicial).
  useEffect(() => {
    if (!isLegacy) return;
    const customer = customerQuery.data;
    if (technicianPctInput === "" && pair != null) {
      setTechnicianPctInput(String(Number(pair.commissionPct)));
    }
    if (intermediaryPctInput === "" && pair?.intermediaryPct != null) {
      setIntermediaryPctInput(String(Number(pair.intermediaryPct)));
    }
    if (clientPctInput === "" && customer?.defaultRetentionPct != null) {
      setClientPctInput(String(customer.defaultRetentionPct));
    }
    // Adota a base do par se ainda não foi tocada pelo operador.
    if (pair != null && line.commissionBase === "budget") {
      setCommissionBase(pair.commissionBase === "faturado" ? "invoiced" : "budget");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pair, customerQuery.data?.id]);

  const utils = trpc.useUtils();
  const editMutation = trpc.serviceLines.editCommission.useMutation({
    onSuccess: () => {
      utils.serviceLines.invalidate();
      utils.dossiers.invalidate();
      onSaved();
    },
    onError: (e) => setError(e.message),
  });

  const faturadoNum = line.faturado != null ? Number(line.faturado) : 0;
  // F-valor-fixo-tecnico — base "orçamento" é o devis PÓS-franquia (mesma do servidor).
  const orcamentoNum =
    line.orcamentoPosFranquia != null
      ? Number(line.orcamentoPosFranquia)
      : line.orcamento != null
        ? Number(line.orcamento)
        : faturadoNum;
  // Valor EXATO que o técnico deve receber (opcional): o % é derivado e a
  // casa absorve a diferença. CEO 2026-10-02: "quero que o técnico receba
  // 1 200 € independente do valor do devis".
  const [fixedAmountInput, setFixedAmountInput] = useState("");
  const baseTotal = commissionBase === "invoiced" ? faturadoNum : orcamentoNum;
  const fixedAmount = fixedAmountInput.trim() === "" ? null : Number(fixedAmountInput);
  function aplicarValorFixo(v: string) {
    setFixedAmountInput(v);
    const n = Number(v);
    if (v.trim() !== "" && Number.isFinite(n) && n >= 0 && baseTotal > 0) {
      setTechnicianPctInput(String(Math.round((n / baseTotal) * 10000) / 100));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!cascadeValid) {
      setError(t("dossierDetail.sumMust100Save"));
      return;
    }
    editMutation.mutate({
      companyId,
      lineId: line.id,
      commissionBase,
      clientPct: clientPctInput ? Number(clientPctInput) || 0 : 0,
      technicianPct: technicianPctInput ? Number(technicianPctInput) || 0 : 0,
      intermediaryPct: intermediaryPctInput ? Number(intermediaryPctInput) || 0 : 0,
      ...(fixedAmount != null && Number.isFinite(fixedAmount)
        ? { technicianAmountFixed: fixedAmount }
        : {}),
    });
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <div>
            <h2 className="text-lg font-semibold">
              {isLegacy
                ? t("dossierDetail.calcLineCommissionTitle")
                : t("dossierDetail.editLineCommissionTitle")}
            </h2>
            <p className="text-xs text-neutral-500 mt-0.5">
              {isLegacy
                ? `${t("dossierDetail.legacyHintPre")} (${techQuery.data?.name ?? ""}). ${t("dossierDetail.legacyHintSuf")}`
                : `${t("dossierDetail.technician")}: ${techQuery.data?.name ?? "—"} · ${t("dossierDetail.fieldCustomer")}: ${customerQuery.data?.displayName ?? "—"}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <Field label={t("dossierDetail.calcBase")}>
            <select
              value={commissionBase}
              onChange={(e) => setCommissionBase(e.target.value as "budget" | "invoiced")}
              disabled={editMutation.isPending}
              className={inputClass}
            >
              <option value="budget">{t("dossierDetail.baseBudget")}</option>
              <option value="invoiced">{t("dossierDetail.baseInvoiced")}</option>
            </select>
          </Field>
          <CommissionCascadeEditor
            commissionBase={commissionBase}
            prixBrut={orcamentoNum > 0 ? orcamentoNum : null}
            totalFacture={faturadoNum > 0 ? faturadoNum : null}
            clientPctInput={clientPctInput}
            setClientPctInput={setClientPctInput}
            technicianPctInput={technicianPctInput}
            setTechnicianPctInput={setTechnicianPctInput}
            intermediaryPctInput={intermediaryPctInput}
            setIntermediaryPctInput={setIntermediaryPctInput}
            technicianSelected={true}
            disabled={editMutation.isPending}
            onValidityChange={setCascadeValid}
            companyName={billingCompanyName}
          />
          <Field label={t("dossierDetail.fixedTechAmountLabel")}>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={fixedAmountInput}
              onChange={(e) => aplicarValorFixo(e.target.value)}
              disabled={editMutation.isPending}
              placeholder={t("dossierDetail.fixedTechAmountPh")}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-neutral-500">
              {t("dossierDetail.fixedTechAmountHint")}
              {fixedAmount != null && baseTotal > 0 && (
                <> → {Math.round((fixedAmount / baseTotal) * 10000) / 100} %</>
              )}
            </p>
          </Field>
          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
            <button
              type="button"
              onClick={onClose}
              disabled={editMutation.isPending}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
            >
              {t("dossierDetail.cancel")}
            </button>
            <button
              type="submit"
              disabled={editMutation.isPending || !cascadeValid}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
              title={!cascadeValid ? t("dossierDetail.sumMust100") : undefined}
            >
              {editMutation.isPending
                ? t("dossierDetail.saving")
                : isLegacy
                  ? t("dossierDetail.calcAndPromote")
                  : t("dossierDetail.saveCommission")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddLineModal({
  companyId,
  dossierId,
  accessibleCompanies,
  technicians,
  onClose,
  onSaved,
}: {
  companyId: number;
  dossierId: number;
  accessibleCompanies: ReadonlyArray<{ id: number; name: string }>;
  technicians: ReadonlyArray<{
    id: number;
    name: string;
  }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const tr = useT();
  const [serviceType, setServiceType] = useState<"dsp" | "montage_demontage">("montage_demontage");
  // F-billing-company-label — empresa faturadora ativa (absorve o resíduo).
  const billingCompanyName =
    accessibleCompanies.find((c) => c.id === companyId)?.name ?? "Euro Storm";
  const [responsibleCompanyId, setResponsibleCompanyId] = useState<number | null>(
    accessibleCompanies[0]?.id ?? null,
  );
  const [technicianId, setTechnicianId] = useState<number | null>(null);
  const [orcamento, setOrcamento] = useState("");
  const [faturado, setFaturado] = useState("");
  const [technicianAmountKnown, setTechnicianAmountKnown] = useState(true);
  // F8 — modelo de comissão (default legacy preserva comportamento)
  const [commissionModel, setCommissionModel] = useState<"legacy" | "complementary">("legacy");
  const [commissionBase, setCommissionBase] = useState<"budget" | "invoiced">("budget");
  const [clientPctInput, setClientPctInput] = useState("");
  const [technicianPctOverrideInput, setTechnicianPctOverrideInput] = useState("");
  // F-commission-cleanup C3 — % intermediário editável no nível da line.
  const [intermediaryPctOverrideInput, setIntermediaryPctOverrideInput] = useState("");
  // Cascade validity — controla disabled do Salvar quando complementary.
  const [cascadeValid, setCascadeValid] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // F-tcc2 — quando complementary + técnico selecionado, busca o PAR
  // técnico×cliente do dossier (a comissão padrão do técnico foi descontinuada).
  const dossierForPairQuery = trpc.dossiers.getById.useQuery(
    { companyId, dossierId },
    { staleTime: 30_000 },
  );
  const pairsQuery = trpc.technicians.listClientCommissions.useQuery(
    { companyId, technicianId: technicianId ?? 0 },
    {
      enabled: commissionModel === "complementary" && technicianId != null,
      staleTime: 30_000,
    },
  );
  const pairCustomerId = dossierForPairQuery.data?.customerId ?? null;
  const pairAtelierId = dossierForPairQuery.data?.atelierId ?? null;
  // F-tarifa-atelier — par do ateliê do dossier > par do cliente.
  const selectedPair =
    pairCustomerId != null
      ? ((pairsQuery.data ?? []).find(
          (p) => p.customerId === pairCustomerId && (p.atelierId ?? null) === pairAtelierId,
        ) ??
        (pairsQuery.data ?? []).find((p) => p.customerId === pairCustomerId && p.atelierId == null))
      : undefined;

  // F-tcc2 — quando o par carregar, pré-popular os campos (só se o input
  // estiver vazio — não sobrescreve override manual do operador).
  useEffect(() => {
    if (!selectedPair) return;
    if (technicianPctOverrideInput === "") {
      setTechnicianPctOverrideInput(String(Number(selectedPair.commissionPct)));
    }
    if (intermediaryPctOverrideInput === "" && selectedPair.intermediaryPct != null) {
      setIntermediaryPctOverrideInput(String(Number(selectedPair.intermediaryPct)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPair]);

  const createLine = trpc.serviceLines.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (responsibleCompanyId == null) {
      setError(tr("dossierDetail.responsibleCompanyRequired"));
      return;
    }
    if (technicianId == null) {
      setError(tr("dossierDetail.technicianRequired"));
      return;
    }
    const payload: {
      companyId: number;
      dossierId: number;
      serviceType: "dsp" | "montage_demontage";
      responsibleCompanyId: number;
      technicianId: number;
      orcamento?: number;
      faturado?: number;
      technicianAmountKnown: boolean;
      // F8
      commissionModel?: "legacy" | "complementary";
      commissionBase?: "budget" | "invoiced";
      clientPct?: number;
      technicianPctOverride?: number;
      intermediaryPctOverride?: number;
    } = {
      companyId,
      dossierId,
      serviceType,
      responsibleCompanyId,
      technicianId,
      technicianAmountKnown,
    };
    const orc = parseDecimal(orcamento);
    const fat = parseDecimal(faturado);
    if (orc != null) payload.orcamento = orc;
    if (fat != null) payload.faturado = fat;
    // F8 — só envia campos do modelo complementar quando ativado
    if (commissionModel === "complementary") {
      if (orc == null || fat == null) {
        setError(tr("dossierDetail.complementaryRequiresValues"));
        return;
      }
      payload.commissionModel = "complementary";
      payload.commissionBase = commissionBase;
      const clientPctNum = clientPctInput ? Number(clientPctInput) : undefined;
      if (clientPctNum !== undefined) {
        if (Number.isNaN(clientPctNum) || clientPctNum < 0 || clientPctNum > 100) {
          setError(tr("dossierDetail.clientPctRange"));
          return;
        }
        payload.clientPct = clientPctNum;
      }
      const techOverride = technicianPctOverrideInput
        ? Number(technicianPctOverrideInput)
        : undefined;
      if (techOverride !== undefined) {
        if (Number.isNaN(techOverride) || techOverride < 0 || techOverride > 100) {
          setError(tr("dossierDetail.technicianPctRange"));
          return;
        }
        payload.technicianPctOverride = techOverride;
      }
      const intermOverride = intermediaryPctOverrideInput
        ? Number(intermediaryPctOverrideInput)
        : undefined;
      if (intermOverride !== undefined) {
        if (Number.isNaN(intermOverride) || intermOverride < 0 || intermOverride > 100) {
          setError(tr("dossierDetail.intermediaryPctRange"));
          return;
        }
        payload.intermediaryPctOverride = intermOverride;
      }
    }
    createLine.mutate(payload);
  }

  return (
    <ModalShell title={tr("dossierDetail.addServiceLineTitle")} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={tr("dossierDetail.type")} required>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value as "dsp" | "montage_demontage")}
              disabled={createLine.isPending}
              className={inputClass}
            >
              <option value="dsp">DSP</option>
              <option value="montage_demontage">{tr("dossierDetail.serviceTypeMontage")}</option>
            </select>
          </Field>
          <Field label={tr("dossierDetail.responsibleCompany")} required>
            <select
              value={responsibleCompanyId ?? ""}
              onChange={(e) => setResponsibleCompanyId(Number(e.target.value))}
              disabled={createLine.isPending}
              className={inputClass}
            >
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={tr("dossierDetail.technician")} required>
          <select
            value={technicianId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setTechnicianId(v === "" ? null : Number(v));
            }}
            disabled={createLine.isPending}
            className={inputClass}
          >
            <option value="">{tr("dossierDetail.selectPlaceholder")}</option>
            {technicians.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={tr("dossierDetail.quote")}>
            <input
              type="text"
              inputMode="decimal"
              value={orcamento}
              onChange={(e) => setOrcamento(e.target.value)}
              disabled={createLine.isPending}
              className={`${inputClass} font-mono text-right`}
            />
          </Field>
          <Field label={tr("dossierDetail.invoiced")}>
            <input
              type="text"
              inputMode="decimal"
              value={faturado}
              onChange={(e) => setFaturado(e.target.value)}
              disabled={createLine.isPending}
              className={`${inputClass} font-mono text-right`}
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={technicianAmountKnown}
            onChange={(e) => setTechnicianAmountKnown(e.target.checked)}
            disabled={createLine.isPending}
            className="accent-lime"
          />
          {tr("dossierDetail.technicianAmountKnown")}
        </label>

        {/* ─── F8 — Modelo de comissão ────────────────────────────────────── */}
        <div className="rounded-md border border-neutral-800 bg-neutral-900/50 p-3 space-y-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium text-neutral-300">{tr("dossierDetail.model")}:</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                checked={commissionModel === "legacy"}
                onChange={() => setCommissionModel("legacy")}
                disabled={createLine.isPending}
              />
              <span className="text-xs text-neutral-400">{tr("dossierDetail.modelLegacy")}</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                checked={commissionModel === "complementary"}
                onChange={() => setCommissionModel("complementary")}
                disabled={createLine.isPending}
              />
              <span className="text-xs text-neutral-400">
                {tr("dossierDetail.modelComplementary")}
              </span>
            </label>
          </div>
          {commissionModel === "complementary" && (
            <>
              <Field label={tr("dossierDetail.calcBase")}>
                <select
                  value={commissionBase}
                  onChange={(e) => setCommissionBase(e.target.value as "budget" | "invoiced")}
                  disabled={createLine.isPending}
                  className={inputClass}
                >
                  <option value="budget">{tr("dossierDetail.baseBudget")}</option>
                  <option value="invoiced">{tr("dossierDetail.baseInvoiced")}</option>
                </select>
              </Field>
              <CommissionCascadeEditor
                commissionBase={commissionBase}
                prixBrut={parseDecimal(orcamento)}
                totalFacture={parseDecimal(faturado)}
                clientPctInput={clientPctInput}
                setClientPctInput={setClientPctInput}
                technicianPctInput={technicianPctOverrideInput}
                setTechnicianPctInput={setTechnicianPctOverrideInput}
                intermediaryPctInput={intermediaryPctOverrideInput}
                setIntermediaryPctInput={setIntermediaryPctOverrideInput}
                technicianSelected={technicianId != null}
                disabled={createLine.isPending}
                onValidityChange={setCascadeValid}
                companyName={billingCompanyName}
              />
            </>
          )}
        </div>

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            disabled={createLine.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {tr("dossierDetail.cancel")}
          </button>
          <button
            type="submit"
            disabled={
              createLine.isPending || (commissionModel === "complementary" && !cascadeValid)
            }
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
            title={
              commissionModel === "complementary" && !cascadeValid
                ? tr("dossierDetail.sumMust100")
                : undefined
            }
          >
            {createLine.isPending ? tr("dossierDetail.saving") : tr("dossierDetail.save")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// TAB: FATURAÇÃO CLIENTE
// ============================================================================

function TabFaturacao({
  companyId,
  dossierId,
  isAdmin,
  dossierDeleted,
  autoOpenIssue = false,
  onAutoOpenConsumed,
}: {
  companyId: number;
  dossierId: number;
  isAdmin: boolean;
  dossierDeleted: boolean;
  /** F-ficha-carro — a ação "Emitir fatura" da linha do tempo já abre o modal. */
  autoOpenIssue?: boolean;
  onAutoOpenConsumed?: () => void;
}) {
  const modals = useModals();
  const canEdit = useCanEdit();
  // CEO 2026-10-29 — o gerente de chantier (acesso por menu "operar") também
  // emite avoir, direto, com motivo obrigatório.
  const { grantOnly: avoirGrantOnly, canOperate: avoirCanOperate } = useGrantOnly();
  const podeAvoir =
    isAdmin ||
    (avoirGrantOnly &&
      (avoirCanOperate("dossiers", companyId) || avoirCanOperate("faturas", companyId)));
  const t = useT();
  const utils = trpc.useUtils();
  const listQuery = trpc.customerInvoices.list.useQuery({
    companyId,
    dossierId,
    limit: 50,
    offset: 0,
  });
  // F-avoir-dossier — avoirs de cada fatura, logo abaixo dela.
  const avoirsQ = trpc.customerCreditNotes.byDossier.useQuery({ companyId, dossierId });
  // CEO 2026-10-19 — "Faire un avoir" também aqui (modelo eDSP).
  const [avoirFor, setAvoirFor] = useState<{
    id: number;
    invoiceNumber: string;
    totalHt: number;
  } | null>(null);

  /** F-customer-invoice-pdf — invoice id sendo enviada por email (modal aberto). */
  const [emailModalInvoiceId, setEmailModalInvoiceId] = useState<number | null>(null);

  // CEO 2026-10-27 — "Emitir fatura" abre direto o RASCUNHO: conferência
  // automática + prévia do PDF + "Valider et émettre" (o formulário pequeno
  // de antes saiu). Carro com rascunho aberto reabre o mesmo.
  const [draftId, setDraftId] = useState<number | null>(null);
  const draftQ = trpc.invoiceDrafts.forDossier.useQuery(
    { companyId, dossierId },
    { enabled: canEdit },
  );
  const dossierInfoQ = trpc.dossiers.getById.useQuery({ companyId, dossierId });
  const createDraft = trpc.invoiceDrafts.create.useMutation({
    onSuccess: (r) => {
      void utils.invoiceDrafts.invalidate();
      setDraftId(r.id);
    },
    onError: (e) => {
      if (e.message.startsWith("DRAFT_EXISTS:")) {
        setDraftId(Number(e.message.slice("DRAFT_EXISTS:".length)));
        return;
      }
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: e.message,
        tone: "danger",
      });
    },
  });
  // Um pedido por vez (clique duplo / efeito rodando duas vezes).
  const abrindoRascunho = useRef(false);
  const abrirRascunho = async () => {
    if (draftQ.data) {
      setDraftId(draftQ.data.id);
      return;
    }
    if (abrindoRascunho.current) return;
    abrindoRascunho.current = true;
    // Nº do eDSP (fatura que veio com número) fica; o próximo da série não,
    // para a série não ficar presa no rascunho.
    const info =
      dossierInfoQ.data ??
      (await utils.dossiers.getById.fetch({ companyId, dossierId }).catch(() => null));
    const ext = info?.invoiceNumberExternal ?? "";
    createDraft.mutate(
      {
        companyId,
        dossierIds: [dossierId],
        dueAt: dateMaisDias(30),
        ...(/^[A-Z]+-?\d{4}-\d+$/.test(ext) ? { invoiceNumber: ext } : {}),
      },
      {
        onSettled: () => {
          abrindoRascunho.current = false;
        },
      },
    );
  };
  useEffect(() => {
    if (autoOpenIssue && !dossierDeleted) {
      void abrirRascunho();
      onAutoOpenConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenIssue]);

  const cancelMutation = trpc.customerInvoices.cancel.useMutation({
    onSuccess: () => {
      utils.customerInvoices.invalidate();
      utils.dossiers.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: `${t("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const voidMutation = trpc.customerInvoices.void.useMutation({
    onSuccess: () => {
      utils.customerInvoices.invalidate();
      utils.dossiers.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: `${t("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });
  // F-customer-invoice-pdf
  const regeneratePdfMutation = trpc.customerInvoices.regeneratePdf.useMutation({
    onSuccess: () => utils.customerInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: `${t("dossierDetail.errorGeneratingPdf")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const utilsTrpc = trpc.useUtils();
  /** Helper: garante PDF + abre download. Se attachmentId existe, usa; se não, regenera primeiro. */
  async function downloadInvoicePdf(invoiceId: number, attachmentId: number | null) {
    // A aba abre no clique; o PDF entra nela quando o link chega (sem bloqueio de pop-up).
    await abrirArquivo(async () => {
      let resolvedAttachmentId = attachmentId;
      if (resolvedAttachmentId == null) {
        const result = await regeneratePdfMutation.mutateAsync({ companyId, invoiceId });
        resolvedAttachmentId = result.attachmentId;
      }
      const url = await utilsTrpc.attachments.getDownloadUrl.fetch({
        companyId,
        attachmentId: resolvedAttachmentId,
      });
      return url.url;
    }).catch(() => {
      /* erro já mostrado pelo onError da mutação */
    });
  }

  const rows = listQuery.data?.rows ?? [];

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("dossierDetail.customerInvoicesTitle")}
        </h3>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {draftQ.data ? (
              <button
                type="button"
                onClick={() => setDraftId(draftQ.data!.id)}
                className="rounded-md border px-3 py-1 text-xs font-medium"
                style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
                data-testid="dossier-open-draft"
              >
                {t("invoiceDraft.openDraft")}
              </button>
            ) : (
              <button
                onClick={() => void abrirRascunho()}
                disabled={dossierDeleted || createDraft.isPending}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-3 py-1 text-xs font-medium"
                data-testid="dossier-issue-invoice"
              >
                {createDraft.isPending ? "…" : t("dossierDetail.issueInvoiceBtn")}
              </button>
            )}
          </div>
        )}
      </div>

      {listQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("dossierDetail.loading")}</p>
      )}
      {rows.length === 0 && !listQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("dossierDetail.noInvoicesYet")}
        </p>
      )}

      <div className="space-y-2">
        {rows.map((inv) => (
          <div key={inv.id} className="space-y-1">
            <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 flex items-center gap-3 flex-wrap">
              <div className="font-mono text-sm">{inv.invoiceNumber}</div>
              <span
                className={`inline-block rounded px-2 py-0.5 text-xs ${CUSTOMER_INVOICE_TONE[inv.fiscalStatus]}`}
              >
                {customerInvoiceLabels(t)[inv.fiscalStatus] ?? inv.fiscalStatus}
              </span>
              <div className="text-xs text-neutral-400">
                {t("dossierDetail.invoiceIssuedOnPre")}{" "}
                {inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString("pt-BR") : "—"}
                {/* CEO 2026-10-12 — data do pagamento junto da fatura. */}
                {inv.lastReceivedAt && (
                  <span className="ml-2 text-lime">
                    · {t("dossierDetail.invoiceReceivedOnPre")}{" "}
                    {new Date(inv.lastReceivedAt).toLocaleDateString("pt-BR")}
                  </span>
                )}
                {/* F-fatura-editada (CEO 2026-11-03) — refeita no mesmo número. */}
                {inv.amendedAt && (
                  <span
                    className="ml-2 text-amber-300"
                    data-testid={`dossier-invoice-amended-${inv.id}`}
                  >
                    · ✎ {t("invoices.badgeAmended")}{" "}
                    {new Date(inv.amendedAt).toLocaleDateString("pt-BR")} (v{inv.amendVersion})
                    {inv.naoEnviada ? ` — ${t("invoices.amendedResendBadge")}` : ""}
                  </span>
                )}
              </div>
              <div className="font-mono text-sm flex-1">
                {(() => {
                  // F-conta-avoir — documento (valor + já descontados) e líquido.
                  const av = (avoirsQ.data?.rows ?? []).filter(
                    (a) =>
                      a.invoiceNumber === inv.invoiceNumber &&
                      (a.status === "issued" || a.status === "embedded"),
                  );
                  const valor = Number(inv.totalFaturadoSnapshot ?? 0);
                  if (av.length === 0) return formatMoney(valor, { decimals: 2 });
                  const emb = av
                    .filter((a) => a.status === "embedded")
                    .reduce((x, a) => x + Number(a.totalHt), 0);
                  const iss = av
                    .filter((a) => a.status === "issued")
                    .reduce((x, a) => x + Number(a.totalHt), 0);
                  return `${formatMoney(valor + emb, { decimals: 2 })} HT (${t("dossierDetail.netAfterAvoirPre")} ${formatMoney(valor - iss, { decimals: 2 })})`;
                })()}
              </div>
              <div className="flex gap-1 flex-wrap">
                {/* F-customer-invoice-pdf — Botões PDF + Email para faturas issued/paid */}
                {(inv.fiscalStatus === "issued" || inv.fiscalStatus === "paid") && (
                  <>
                    {podeAvoir && (
                      <button
                        type="button"
                        onClick={() =>
                          setAvoirFor({
                            id: inv.id,
                            invoiceNumber: inv.invoiceNumber,
                            totalHt: Number(inv.totalFaturadoSnapshot ?? 0),
                          })
                        }
                        className="rounded-md border border-amber-500/50 px-2 py-0.5 text-xs text-amber-300 hover:border-amber-400"
                        title={t("dossierDetail.avoirBtnTitle")}
                        data-testid={`dossier-avoir-btn-${inv.id}`}
                      >
                        ↶ {t("dossierDetail.avoirBtn")}
                      </button>
                    )}
                    <button
                      onClick={() => downloadInvoicePdf(inv.id, inv.attachmentId)}
                      disabled={regeneratePdfMutation.isPending}
                      className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime text-neutral-200 px-2 py-0.5 text-xs disabled:opacity-40"
                      title={t("dossierDetail.downloadInvoicePdfTitle")}
                    >
                      📄 PDF
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => setEmailModalInvoiceId(inv.id)}
                        className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime text-neutral-200 px-2 py-0.5 text-xs"
                        title={t("dossierDetail.sendInvoiceEmailTitle")}
                      >
                        {t("dossierDetail.emailBtn")}
                      </button>
                    )}
                  </>
                )}
                {isAdmin && inv.fiscalStatus === "issued" && (
                  <>
                    <button
                      onClick={async () => {
                        const reason = await modals.promptReason({
                          title: t("dossierDetail.cancelInvoiceTitle"),
                          message: t("dossierDetail.cancelInvoiceMsg"),
                          required: true,
                          tone: "warning",
                        });
                        if (!reason) return;
                        cancelMutation.mutate({
                          companyId,
                          invoiceId: inv.id,
                          reason,
                        });
                      }}
                      className="rounded-md border border-red-500/40 hover:border-red-500 text-red-300 px-2 py-0.5 text-xs"
                    >
                      {t("dossierDetail.cancelLower")}
                    </button>
                    <button
                      onClick={async () => {
                        const reason = await modals.promptReason({
                          title: t("dossierDetail.voidInvoiceTitle"),
                          message: t("dossierDetail.voidInvoiceMsg"),
                          required: true,
                          tone: "warning",
                        });
                        if (!reason) return;
                        voidMutation.mutate({
                          companyId,
                          invoiceId: inv.id,
                          reason,
                        });
                      }}
                      className="rounded-md border border-neutral-700 hover:border-neutral-600 text-neutral-300 px-2 py-0.5 text-xs"
                    >
                      {t("dossierDetail.voidLower")}
                    </button>
                  </>
                )}
              </div>
            </div>
            {(avoirsQ.data?.rows ?? [])
              .filter((a) => a.invoiceNumber === inv.invoiceNumber)
              .map((a) => (
                <div
                  key={a.id}
                  className="ml-6 rounded-md border border-neutral-800 px-3 py-1.5 text-xs flex items-center gap-3 flex-wrap"
                  data-testid="dossier-avoir"
                >
                  <span className="font-mono text-amber-300">↳ {a.creditNoteNumber}</span>
                  <span className="text-neutral-400">
                    {new Date(a.issuedAt).toLocaleDateString("fr-FR")}
                  </span>
                  <span className="font-mono">− {formatMoney(a.totalHt, { decimals: 2 })} HT</span>
                  {a.reason && <span className="text-neutral-400">{a.reason}</span>}
                  {/* CEO 2026-10-29 — PDF/e-mail do avoir (gerado pelo Gestion;
                      avoir do eDSP já descontado só tem o PDF original). */}
                  {a.status === "issued" ? (
                    <CreditNoteDocButtons
                      companyId={companyId}
                      creditNoteId={a.id}
                      creditNoteNumber={a.creditNoteNumber}
                    />
                  ) : (
                    a.attachmentId != null && (
                      <button
                        onClick={() =>
                          void abrirArquivo(
                            async () =>
                              (
                                await utilsTrpc.attachments.getDownloadUrl.fetch({
                                  companyId,
                                  attachmentId: a.attachmentId!,
                                })
                              ).url,
                          )
                        }
                        className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-2 py-0.5"
                      >
                        📄 PDF
                      </button>
                    )
                  )}
                  {a.status === "embedded" && (
                    <span className="badge b-mut">{t("dossierDetail.avoirEmbeddedTag")}</span>
                  )}
                  {a.status === "cancelled" && (
                    <span className="badge b-dang">{t("dossierDetail.avoirCancelledTag")}</span>
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>

      {draftId !== null && (
        <InvoiceDraftModal
          companyId={companyId}
          draftId={draftId}
          onClose={() => {
            setDraftId(null);
            void draftQ.refetch();
          }}
        />
      )}

      {emailModalInvoiceId !== null && (
        <SendInvoiceEmailModal
          companyId={companyId}
          invoiceId={emailModalInvoiceId}
          onClose={() => setEmailModalInvoiceId(null)}
        />
      )}
      {avoirFor && (
        <CreditNoteModal
          companyId={companyId}
          invoice={avoirFor}
          reasonRequired={avoirGrantOnly}
          onClose={() => {
            setAvoirFor(null);
            void utilsTrpc.customerCreditNotes.invalidate();
            void utilsTrpc.customerInvoices.invalidate();
            void utilsTrpc.dossiers.invalidate();
          }}
        />
      )}
    </section>
  );
}

/**
 * F-customer-invoice-pdf — Modal de envio da fatura por email.
 * Email default = customer.billingEmail ?? customer.email (vindo de
 * `customerInvoices.getSuggestedRecipientEmail`). Operador pode override.
 */
function SendInvoiceEmailModal({
  companyId,
  invoiceId,
  onClose,
}: {
  companyId: number;
  invoiceId: number;
  onClose: () => void;
}) {
  const modals = useModals();
  const t = useT();
  const suggestionQuery = trpc.customerInvoices.getSuggestedRecipientEmail.useQuery({
    companyId,
    invoiceId,
  });
  const [email, setEmail] = useState("");
  const [edited, setEdited] = useState(false);
  // Preenche o input quando a sugestão chegar — apenas na 1ª vez.
  if (!edited && suggestionQuery.data && email === "") {
    setEmail(suggestionQuery.data.suggested);
  }
  const sendMutation = trpc.customerInvoices.sendEmail.useMutation({
    onSuccess: (result) => {
      void modals.alert({
        message: `${t("dossierDetail.invoiceSentTo")} ${result.sentTo} ✓`,
      });
      onClose();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: `${t("dossierDetail.errorSending")}: ${e.message}`,
        tone: "danger",
      }),
  });

  const customerName = suggestionQuery.data?.customerName ?? "—";
  const hasNoEmail =
    suggestionQuery.data != null &&
    !suggestionQuery.data.hasBillingEmail &&
    !suggestionQuery.data.hasEmail;

  return (
    <ModalShell title={t("dossierDetail.sendInvoiceEmailTitle")} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim()) {
            void modals.alert({ message: t("dossierDetail.invalidEmail") });
            return;
          }
          sendMutation.mutate({
            companyId,
            invoiceId,
            recipientEmail: email.trim(),
          });
        }}
        className="p-5 space-y-4"
      >
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wider text-neutral-500">
            {t("dossierDetail.recipient")}
          </label>
          <div className="text-sm text-neutral-300">{customerName}</div>
        </div>

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wider text-neutral-500" htmlFor="email">
            {t("dossierDetail.emailLabel")}
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEdited(true);
            }}
            placeholder={t("dossierDetail.emailPlaceholder")}
            className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm focus:border-lime outline-none"
          />
          {hasNoEmail && (
            <p className="text-[11px] text-amber-400">{t("dossierDetail.noCustomerEmailWarn")}</p>
          )}
          {suggestionQuery.data?.hasBillingEmail && !edited && (
            <p className="text-[11px] text-text-muted">
              {t("dossierDetail.usingBillingEmailHint")}
            </p>
          )}
        </div>

        <div className="text-xs text-text-muted bg-neutral-900 border border-neutral-800 rounded-md p-3">
          {t("dossierDetail.pdfAutoAttachHint")}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 hover:border-neutral-600 text-neutral-300 px-3 py-1.5 text-sm"
          >
            {t("dossierDetail.cancel")}
          </button>
          <button
            type="submit"
            disabled={sendMutation.isPending || !email.trim()}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
          >
            {sendMutation.isPending ? t("dossierDetail.sending") : t("dossierDetail.send")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// TAB: RECEBIMENTOS
// ============================================================================

function TabRecebimentos({
  companyId,
  dossierId,
  isAdmin,
}: {
  companyId: number;
  dossierId: number;
  isAdmin: boolean;
}) {
  const modals = useModals();
  const canEdit = useCanEdit();
  // CEO 2026-10-29 — recebimento só o escritório registra (o gerente vê).
  const { grantOnly: recebGrantOnly } = useGrantOnly();
  const tr = useT();
  const utils = trpc.useUtils();
  const invoicesQuery = trpc.customerInvoices.list.useQuery({
    companyId,
    dossierId,
    limit: 50,
    offset: 0,
  });

  const invoiceIds = (invoicesQuery.data?.rows ?? []).map((r) => r.id);
  const invoiceById = new Map((invoicesQuery.data?.rows ?? []).map((r) => [r.id, r]));

  const receiptQueries = trpc.useQueries((t) =>
    invoiceIds.map((iid) =>
      t.customerReceipts.list({
        companyId,
        customerInvoiceId: iid,
        limit: 100,
        offset: 0,
      }),
    ),
  );

  const reverseMutation = trpc.customerReceipts.reverse.useMutation({
    onSuccess: () => {
      utils.customerReceipts.invalidate();
      utils.customerInvoices.invalidate();
      utils.dossiers.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: tr("dossierDetail.errorTitle"),
        message: `${tr("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });

  const [showCreate, setShowCreate] = useState<number | null>(null);

  return (
    <section className="space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {tr("dossierDetail.receiptsByInvoiceTitle")}
      </h3>

      {invoicesQuery.isLoading && (
        <p className="text-sm text-neutral-500">{tr("dossierDetail.loading")}</p>
      )}
      {invoiceIds.length === 0 && !invoicesQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {tr("dossierDetail.issueInvoiceBeforeReceipts")}
        </p>
      )}

      {invoiceIds.map((iid, idx) => {
        const inv = invoiceById.get(iid);
        const receipts = receiptQueries[idx]?.data?.rows ?? [];
        if (!inv) return null;
        const canCreate = inv.fiscalStatus === "issued";
        return (
          <div
            key={iid}
            className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 space-y-2"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm">{inv.invoiceNumber}</span>
                <span
                  className={`inline-block rounded px-2 py-0.5 text-xs ${CUSTOMER_INVOICE_TONE[inv.fiscalStatus]}`}
                >
                  {customerInvoiceLabels(tr)[inv.fiscalStatus] ?? inv.fiscalStatus}
                </span>
                <span className="text-xs text-neutral-500 font-mono">
                  {tr("dossierDetail.total")}{" "}
                  {formatMoney(inv.totalFaturadoSnapshot, { decimals: 2 })}
                </span>
              </div>
              {canEdit && !recebGrantOnly && (
                <button
                  onClick={() => setShowCreate(iid)}
                  disabled={!canCreate}
                  className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-3 py-1 text-xs font-medium"
                >
                  {tr("dossierDetail.addReceiptBtn")}
                </button>
              )}
            </div>

            {receipts.length === 0 && (
              <p className="text-xs text-neutral-500 italic pl-2">
                {tr("dossierDetail.noReceipts")}
              </p>
            )}
            {receipts.map((r) => (
              <div key={r.id} className="flex items-center gap-3 pl-2 text-sm flex-wrap">
                <span className="text-neutral-400 text-xs">
                  {new Date(r.receivedAt).toLocaleDateString("pt-BR")}
                </span>
                <span className="font-mono">{formatMoney(r.amount, { decimals: 2 })}</span>
                <span className="text-xs text-neutral-500">{r.paymentMethod}</span>
                {r.source === "edsp_import" && (
                  <span
                    className="rounded px-1.5 py-0.5 text-xs bg-sky-500/15 text-sky-300"
                    title={tr("dossierDetail.edspReceiptTitle")}
                  >
                    {tr("dossierDetail.edspNonBank")}
                  </span>
                )}
                {r.reference && (
                  <span className="text-xs text-neutral-500 font-mono">{r.reference}</span>
                )}
                {r.reversesId && (
                  <span className="rounded px-1.5 py-0.5 text-xs bg-amber-500/15 text-amber-300">
                    {tr("dossierDetail.reversalOf")} #{r.reversesId}
                  </span>
                )}
                <div className="flex-1" />
                {isAdmin && !r.reversesId && (
                  <button
                    onClick={async () => {
                      const reason = await modals.promptReason({
                        title: tr("dossierDetail.reverseReceiptTitle"),
                        message: tr("dossierDetail.createsNegativeMirror"),
                        required: true,
                        tone: "warning",
                      });
                      if (!reason) return;
                      reverseMutation.mutate({
                        companyId,
                        receiptId: r.id,
                        reason,
                      });
                    }}
                    className="rounded-md border border-amber-500/40 hover:border-amber-500 text-amber-300 px-2 py-0.5 text-xs"
                  >
                    {tr("dossierDetail.reverseLower")}
                  </button>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {canEdit && showCreate !== null && (
        <CreateReceiptModal
          companyId={companyId}
          invoiceId={showCreate}
          onClose={() => setShowCreate(null)}
          onSaved={() => {
            setShowCreate(null);
            utils.customerReceipts.invalidate();
            utils.customerInvoices.invalidate();
            utils.dossiers.invalidate();
          }}
        />
      )}
    </section>
  );
}

function CreateReceiptModal({
  companyId,
  invoiceId,
  onClose,
  onSaved,
}: {
  companyId: number;
  invoiceId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const balanceQuery = trpc.customerReceipts.openBalance.useQuery({
    companyId,
    invoiceId,
  });
  const bankAccountsQuery = trpc.bankAccounts.list.useQuery({
    companyId,
    active: true,
    limit: 100,
    offset: 0,
  });

  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<
    "bank_transfer" | "sepa" | "check" | "cash" | "card" | "other"
  >("bank_transfer");
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [receivedAt, setReceivedAt] = useState(new Date().toISOString().slice(0, 16));
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = trpc.customerReceipts.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const amt = parseDecimal(amount);
    if (amt == null || amt <= 0) {
      setError(t("dossierDetail.amountMustBePositive"));
      return;
    }
    const payload: {
      companyId: number;
      customerInvoiceId: number;
      amount: number;
      currency: string;
      receivedAt: string;
      paymentMethod: typeof paymentMethod;
      bankAccountId?: number | null;
      reference?: string;
    } = {
      companyId,
      customerInvoiceId: invoiceId,
      amount: amt,
      currency: "EUR",
      receivedAt: new Date(receivedAt).toISOString(),
      paymentMethod,
    };
    if (bankAccountId !== null) payload.bankAccountId = bankAccountId;
    if (reference.trim()) payload.reference = reference.trim();
    createMutation.mutate(payload);
  }

  return (
    <ModalShell title={t("dossierDetail.newReceiptTitle")} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        {balanceQuery.data && (
          <div className="rounded-md bg-neutral-800/30 px-3 py-2 text-sm">
            {t("dossierDetail.openBalance")}:{" "}
            <span className="font-mono font-semibold">
              {formatMoney(balanceQuery.data.open, { decimals: 2 })}
            </span>
            <span className="text-xs text-neutral-500 ml-2">
              {t("dossierDetail.total")}{" "}
              {formatMoney(balanceQuery.data.totalFaturado, { decimals: 2 })} ·{" "}
              {t("dossierDetail.received")} {formatMoney(balanceQuery.data.paid, { decimals: 2 })}
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("dossierDetail.amountEuro")} required>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              disabled={createMutation.isPending}
              className={`${inputClass} font-mono text-right`}
            />
          </Field>
          <Field label={t("dossierDetail.method")} required>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as typeof paymentMethod)}
              disabled={createMutation.isPending}
              className={inputClass}
            >
              <option value="bank_transfer">{t("dossierDetail.methodTransfer")}</option>
              <option value="sepa">SEPA</option>
              <option value="check">{t("dossierDetail.methodCheck")}</option>
              <option value="cash">{t("dossierDetail.methodCash")}</option>
              <option value="card">{t("dossierDetail.methodCard")}</option>
              <option value="other">{t("dossierDetail.methodOther")}</option>
            </select>
          </Field>
        </div>

        <Field label={t("dossierDetail.bankAccountOptional")}>
          <select
            value={bankAccountId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setBankAccountId(v === "" ? null : Number(v));
            }}
            disabled={createMutation.isPending}
            className={inputClass}
          >
            <option value="">{t("dossierDetail.noneOption")}</option>
            {(bankAccountsQuery.data?.rows ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.label} ({b.iban ?? "—"})
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("dossierDetail.dateTime")}>
            <input
              type="datetime-local"
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
              disabled={createMutation.isPending}
              className={inputClass}
            />
          </Field>
          <Field label={t("dossierDetail.reference")}>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={200}
              disabled={createMutation.isPending}
              className={inputClass}
            />
          </Field>
        </div>

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("dossierDetail.cancel")}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {createMutation.isPending ? t("dossierDetail.saving") : t("dossierDetail.register")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// TAB: FATURAS TÉCNICO
// ============================================================================

function TabTecnicoFaturas({
  companyId,
  dossierId,
  companyById,
}: {
  companyId: number;
  dossierId: number;
  companyById: ReadonlyMap<number, { name: string }>;
}) {
  const modals = useModals();
  const canEdit = useCanEdit();
  const tr = useT();
  const utils = trpc.useUtils();
  const dossierQuery = trpc.dossiers.getById.useQuery({ companyId, dossierId });
  const techniciansQuery = trpc.technicians.list.useQuery({
    companyId,
    limit: 200,
    offset: 0,
  });
  const lines = dossierQuery.data?.lines ?? [];
  const lineIds = new Set(lines.map((l) => l.id));
  const technicianIds = Array.from(new Set(lines.map((l) => l.technicianId)));
  const technicianById = new Map((techniciansQuery.data?.rows ?? []).map((t) => [t.id, t]));

  // Lista TI da company para cada técnico envolvido neste dossier. Filtramos
  // client-side por overlap das TI.lines com lineIds do dossier (já que backend
  // não tem filtro por dossier).
  const invoicesQueries = trpc.useQueries((t) =>
    technicianIds.map((tid) =>
      t.technicianInvoices.list({
        companyId,
        technicianId: tid,
        limit: 50,
        offset: 0,
      }),
    ),
  );

  const allInvoiceRows = invoicesQueries.flatMap((q) => q.data?.rows ?? []);
  // De-dupe por id (técnico pode aparecer múltiplas vezes — mas queries diferentes não devem repetir)
  const seen = new Set<number>();
  const uniqueInvoices = allInvoiceRows.filter((inv) => {
    if (seen.has(inv.id)) return false;
    seen.add(inv.id);
    return true;
  });

  const detailQueries = trpc.useQueries((t) =>
    uniqueInvoices.map((inv) => t.technicianInvoices.getById({ companyId, invoiceId: inv.id })),
  );

  const invoicesWithLineFilter = detailQueries
    .map((q, idx) => {
      const detail = q.data;
      if (!detail) return null;
      const overlapsDossier = detail.lines.some((dl) => lineIds.has(dl.serviceLineId));
      if (!overlapsDossier) return null;
      return detail;
    })
    .filter((x): x is NonNullable<typeof x> => x != null);

  const changeStatus = trpc.technicianInvoices.changeStatus.useMutation({
    onSuccess: () => utils.technicianInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: tr("dossierDetail.errorTitle"),
        message: `${tr("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {tr("dossierDetail.technicianInvoicesTitle")}
        </h3>
        {/* F-tech-invoice-import-only — a fatura do técnico é EMITIDA pelo
            técnico e IMPORTADA: anexada à proforma ("Invoice retornada"), não
            criada pelo sistema a partir das linhas. */}
        <p className="text-xs text-neutral-500 mt-1">
          {tr("dossierDetail.technicianInvoiceImportHint")}
        </p>
      </div>

      {invoicesQueries.some((q) => q.isLoading) && (
        <p className="text-sm text-neutral-500">{tr("dossierDetail.loading")}</p>
      )}
      {invoicesWithLineFilter.length === 0 && !invoicesQueries.some((q) => q.isLoading) && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {tr("dossierDetail.noTechnicianInvoices")}
        </p>
      )}

      <div className="space-y-2">
        {invoicesWithLineFilter.map((inv) => {
          const t = technicianById.get(inv.technicianId);
          const responsible = companyById.get(inv.responsibleCompanyId);
          return (
            <div
              key={inv.id}
              className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 space-y-2"
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-sm">{inv.invoiceNumberExternal}</span>
                <span
                  className={`inline-block rounded px-2 py-0.5 text-xs ${TECHNICIAN_INVOICE_TONE[inv.docStatus]}`}
                >
                  {technicianInvoiceLabels(tr)[inv.docStatus] ?? inv.docStatus}
                </span>
                <span className="text-xs text-neutral-400">
                  {t?.name ?? `${tr("dossierDetail.technician")} #${inv.technicianId}`} ·{" "}
                  {responsible?.name ??
                    `${tr("dossierDetail.companyShort")} #${inv.responsibleCompanyId}`}
                </span>
                <div className="flex-1" />
                <span className="font-mono">{formatMoney(inv.totalAmount, { decimals: 2 })}</span>
              </div>

              {canEdit && (
                <div className="flex gap-1 flex-wrap pt-2 border-t border-neutral-800">
                  {inv.docStatus === "draft" && (
                    <button
                      onClick={() =>
                        changeStatus.mutate({
                          companyId,
                          invoiceId: inv.id,
                          toStatus: "received",
                        })
                      }
                      disabled={changeStatus.isPending}
                      className="rounded-md border border-sky-500/40 hover:border-sky-500 text-sky-300 px-2 py-0.5 text-xs"
                    >
                      {tr("dossierDetail.markReceived")}
                    </button>
                  )}
                  {inv.docStatus === "received" && (
                    <>
                      <button
                        onClick={() =>
                          changeStatus.mutate({
                            companyId,
                            invoiceId: inv.id,
                            toStatus: "approved",
                          })
                        }
                        disabled={changeStatus.isPending}
                        className="rounded-md border border-cyan-500/40 hover:border-cyan-500 text-cyan-300 px-2 py-0.5 text-xs"
                      >
                        {tr("dossierDetail.approveLower")}
                      </button>
                      <button
                        onClick={async () => {
                          const reason = await modals.promptReason({
                            title: tr("dossierDetail.rejectTechnicianInvoiceTitle"),
                            message: tr("dossierDetail.reasonRequired"),
                            required: true,
                            tone: "warning",
                          });
                          if (!reason) return;
                          changeStatus.mutate({
                            companyId,
                            invoiceId: inv.id,
                            toStatus: "rejected",
                            reason,
                          });
                        }}
                        disabled={changeStatus.isPending}
                        className="rounded-md border border-amber-500/40 hover:border-amber-500 text-amber-300 px-2 py-0.5 text-xs"
                      >
                        {tr("dossierDetail.rejectLower")}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ============================================================================
// TAB: PAGAMENTOS TÉCNICO
// ============================================================================

function TabTecnicoPagamentos({
  companyId,
  dossierId,
  isAdmin,
}: {
  companyId: number;
  dossierId: number;
  isAdmin: boolean;
}) {
  const modals = useModals();
  const canEdit = useCanEdit();
  const tr = useT();
  const utils = trpc.useUtils();
  const dossierQuery = trpc.dossiers.getById.useQuery({ companyId, dossierId });
  const lines = dossierQuery.data?.lines ?? [];
  const technicianIds = Array.from(new Set(lines.map((l) => l.technicianId)));
  const lineIds = new Set(lines.map((l) => l.id));

  const invoicesQueries = trpc.useQueries((t) =>
    technicianIds.map((tid) =>
      t.technicianInvoices.list({
        companyId,
        technicianId: tid,
        limit: 50,
        offset: 0,
      }),
    ),
  );
  const allInvoices = invoicesQueries.flatMap((q) => q.data?.rows ?? []);
  const seen = new Set<number>();
  const uniqueInvoices = allInvoices.filter((inv) => {
    if (seen.has(inv.id)) return false;
    seen.add(inv.id);
    return true;
  });

  const detailQueries = trpc.useQueries((t) =>
    uniqueInvoices.map((inv) => t.technicianInvoices.getById({ companyId, invoiceId: inv.id })),
  );

  const relevantInvoices = detailQueries
    .map((q) => q.data)
    .filter((inv): inv is NonNullable<typeof inv> => inv != null)
    .filter((inv) => inv.lines.some((dl) => lineIds.has(dl.serviceLineId)));

  const paymentsQueries = trpc.useQueries((t) =>
    relevantInvoices.map((inv) =>
      t.technicianPayments.openBalance({
        companyId,
        invoiceId: inv.id,
      }),
    ),
  );

  const [showCreate, setShowCreate] = useState<number | null>(null);
  const reverseMutation = trpc.technicianPayments.reverse.useMutation({
    onSuccess: () => {
      utils.technicianPayments.invalidate();
      utils.technicianInvoices.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: tr("dossierDetail.errorTitle"),
        message: `${tr("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });

  return (
    <section className="space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {tr("dossierDetail.technicianPaymentsTitle")}
      </h3>

      {relevantInvoices.length === 0 && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {tr("dossierDetail.createTechInvoicesFirst")}
        </p>
      )}

      {relevantInvoices.map((inv, idx) => {
        const balance = paymentsQueries[idx]?.data;
        const canCreate = inv.docStatus === "approved" || inv.docStatus === "received";
        return (
          <PaymentsByInvoiceBlock
            key={inv.id}
            companyId={companyId}
            invoice={inv}
            balance={balance ?? null}
            canCreate={canCreate}
            canEdit={canEdit}
            isAdmin={isAdmin}
            onCreate={() => setShowCreate(inv.id)}
            onReverse={async (paymentId) => {
              const reason = await modals.promptReason({
                title: tr("dossierDetail.reversePaymentTitle"),
                message: tr("dossierDetail.createsNegativeMirror"),
                required: true,
                tone: "warning",
              });
              if (!reason) return;
              reverseMutation.mutate({ companyId, paymentId, reason });
            }}
          />
        );
      })}

      {canEdit && showCreate !== null && (
        <CreateTechnicianPaymentModal
          companyId={companyId}
          technicianInvoiceId={showCreate}
          onClose={() => setShowCreate(null)}
          onSaved={() => {
            setShowCreate(null);
            utils.technicianPayments.invalidate();
            utils.technicianInvoices.invalidate();
          }}
        />
      )}
    </section>
  );
}

function PaymentsByInvoiceBlock({
  companyId,
  invoice,
  balance,
  canCreate,
  canEdit,
  isAdmin,
  onCreate,
  onReverse,
}: {
  companyId: number;
  invoice: { id: number; invoiceNumberExternal: string; docStatus: string; totalAmount: string };
  balance: { totalAmount: number; paid: number; open: number } | null;
  canCreate: boolean;
  canEdit: boolean;
  isAdmin: boolean;
  onCreate: () => void;
  onReverse: (paymentId: number) => void;
}) {
  const t = useT();
  const paymentsQuery = trpc.technicianPayments.list.useQuery({
    companyId,
    technicianInvoiceId: invoice.id,
    limit: 100,
    offset: 0,
  });
  const payments = paymentsQuery.data?.rows ?? [];
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm">{invoice.invoiceNumberExternal}</span>
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs ${TECHNICIAN_INVOICE_TONE[invoice.docStatus]}`}
          >
            {technicianInvoiceLabels(t)[invoice.docStatus] ?? invoice.docStatus}
          </span>
          {balance && (
            <span className="text-xs text-neutral-500 font-mono">
              {t("dossierDetail.balance")} {formatMoney(balance.open, { decimals: 2 })}
            </span>
          )}
        </div>
        {canEdit && (
          <button
            onClick={onCreate}
            disabled={!canCreate}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-3 py-1 text-xs font-medium"
          >
            {t("dossierDetail.addPaymentBtn")}
          </button>
        )}
      </div>
      {payments.length === 0 && (
        <p className="text-xs text-neutral-500 italic pl-2">{t("dossierDetail.noPayments")}</p>
      )}
      {payments.map((p) => (
        <div key={p.id} className="flex items-center gap-3 pl-2 text-sm flex-wrap">
          <span className="text-neutral-400 text-xs">
            {new Date(p.paidAt).toLocaleDateString("pt-BR")}
          </span>
          <span className="font-mono">{formatMoney(p.amount, { decimals: 2 })}</span>
          <span className="text-xs text-neutral-500">{p.paymentMethod}</span>
          {p.reversesId && (
            <span className="rounded px-1.5 py-0.5 text-xs bg-amber-500/15 text-amber-300">
              {t("dossierDetail.reversalOf")} #{p.reversesId}
            </span>
          )}
          <div className="flex-1" />
          {isAdmin && !p.reversesId && (
            <button
              onClick={() => onReverse(p.id)}
              className="rounded-md border border-amber-500/40 hover:border-amber-500 text-amber-300 px-2 py-0.5 text-xs"
            >
              {t("dossierDetail.reverseLower")}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function CreateTechnicianPaymentModal({
  companyId,
  technicianInvoiceId,
  onClose,
  onSaved,
}: {
  companyId: number;
  technicianInvoiceId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const balanceQuery = trpc.technicianPayments.openBalance.useQuery({
    companyId,
    invoiceId: technicianInvoiceId,
  });
  const bankAccountsQuery = trpc.bankAccounts.list.useQuery({
    companyId,
    active: true,
    limit: 100,
    offset: 0,
  });

  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<
    "bank_transfer" | "sepa" | "check" | "cash" | "card" | "other"
  >("bank_transfer");
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 16));
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = trpc.technicianPayments.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const amt = parseDecimal(amount);
    if (amt == null || amt <= 0) {
      setError(t("dossierDetail.amountPositiveShort"));
      return;
    }
    const payload: {
      companyId: number;
      technicianInvoiceId: number;
      amount: number;
      currency: string;
      paidAt: string;
      paymentMethod: typeof paymentMethod;
      bankAccountId?: number | null;
      reference?: string;
    } = {
      companyId,
      technicianInvoiceId,
      amount: amt,
      currency: "EUR",
      paidAt: new Date(paidAt).toISOString(),
      paymentMethod,
    };
    if (bankAccountId !== null) payload.bankAccountId = bankAccountId;
    if (reference.trim()) payload.reference = reference.trim();
    createMutation.mutate(payload);
  }

  return (
    <ModalShell title={t("dossierDetail.newTechnicianPaymentTitle")} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        {balanceQuery.data && (
          <div className="rounded-md bg-neutral-800/30 px-3 py-2 text-sm">
            {t("dossierDetail.openBalance")}:{" "}
            <span className="font-mono font-semibold">
              {formatMoney(balanceQuery.data.open, { decimals: 2 })}
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("dossierDetail.amountEuro")} required>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              disabled={createMutation.isPending}
              className={`${inputClass} font-mono text-right`}
            />
          </Field>
          <Field label={t("dossierDetail.method")} required>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as typeof paymentMethod)}
              disabled={createMutation.isPending}
              className={inputClass}
            >
              <option value="bank_transfer">{t("dossierDetail.methodTransfer")}</option>
              <option value="sepa">SEPA</option>
              <option value="check">{t("dossierDetail.methodCheck")}</option>
              <option value="cash">{t("dossierDetail.methodCash")}</option>
              <option value="card">{t("dossierDetail.methodCard")}</option>
              <option value="other">{t("dossierDetail.methodOther")}</option>
            </select>
          </Field>
        </div>
        <Field label={t("dossierDetail.bankAccountOptional")}>
          <select
            value={bankAccountId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setBankAccountId(v === "" ? null : Number(v));
            }}
            disabled={createMutation.isPending}
            className={inputClass}
          >
            <option value="">{t("dossierDetail.noneOption")}</option>
            {(bankAccountsQuery.data?.rows ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("dossierDetail.dateTime")}>
            <input
              type="datetime-local"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              disabled={createMutation.isPending}
              className={inputClass}
            />
          </Field>
          <Field label={t("dossierDetail.reference")}>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={200}
              disabled={createMutation.isPending}
              className={inputClass}
            />
          </Field>
        </div>
        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("dossierDetail.cancel")}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {createMutation.isPending ? t("dossierDetail.saving") : t("dossierDetail.register")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// TAB: INTERCOMPANY
// ============================================================================

function TabIntercompany({
  companyId,
  dossierId,
  billingCompanyId,
  accessibleCompanies,
  isAdmin,
}: {
  companyId: number;
  dossierId: number;
  billingCompanyId: number;
  accessibleCompanies: ReadonlyArray<{ id: number; name: string }>;
  isAdmin: boolean;
}) {
  const modals = useModals();
  const canEdit = useCanEdit();
  const t = useT();
  const utils = trpc.useUtils();
  const listQuery = trpc.intercompanyInvoices.list.useQuery({
    companyId,
    dossierId,
    limit: 50,
    offset: 0,
  });
  const rows = listQuery.data?.rows ?? [];

  const dossierQuery = trpc.dossiers.getById.useQuery({ companyId, dossierId });
  const lines = dossierQuery.data?.lines ?? [];
  const candidateIssuerIds = Array.from(
    new Set(lines.map((l) => l.responsibleCompanyId).filter((cid) => cid !== billingCompanyId)),
  );

  const issueMutation = trpc.intercompanyInvoices.issue.useMutation({
    onSuccess: () => utils.intercompanyInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: `${t("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const approveMutation = trpc.intercompanyInvoices.approve.useMutation({
    onSuccess: () => utils.intercompanyInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: `${t("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const markPaidMutation = trpc.intercompanyInvoices.markPaid.useMutation({
    onSuccess: () => utils.intercompanyInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: `${t("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const cancelMutation = trpc.intercompanyInvoices.cancel.useMutation({
    onSuccess: () => utils.intercompanyInvoices.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: `${t("dossierDetail.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });

  const [showCreate, setShowCreate] = useState(false);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("dossierDetail.intercompanyInvoicesTitle")}
        </h3>
        {canEdit && (
          <button
            onClick={() => setShowCreate(true)}
            disabled={candidateIssuerIds.length === 0}
            title={candidateIssuerIds.length === 0 ? t("dossierDetail.noIcCandidateTitle") : ""}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-3 py-1 text-xs font-medium"
          >
            {t("dossierDetail.createIcDraftBtn")}
          </button>
        )}
      </div>

      {listQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("dossierDetail.loading")}</p>
      )}
      {rows.length === 0 && !listQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("dossierDetail.noIcForDossier")}
        </p>
      )}

      <div className="space-y-2">
        {rows.map((inv) => (
          <div
            key={inv.id}
            className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 space-y-2"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-sm">{inv.invoiceNumber}</span>
              <span
                className={`inline-block rounded px-2 py-0.5 text-xs ${IC_INVOICE_TONE[inv.status]}`}
              >
                {icInvoiceLabels(t)[inv.status] ?? inv.status}
              </span>
              <span className="text-xs text-neutral-500">
                {accessibleCompanies.find((c) => c.id === inv.issuerCompanyId)?.name ??
                  `${t("dossierDetail.issuer")} #${inv.issuerCompanyId}`}{" "}
                →{" "}
                {accessibleCompanies.find((c) => c.id === inv.receiverCompanyId)?.name ??
                  `${t("dossierDetail.receiver")} #${inv.receiverCompanyId}`}
              </span>
              <div className="flex-1" />
              <span className="font-mono">{formatMoney(inv.netAmount, { decimals: 2 })}</span>
            </div>

            {canEdit && (
              <div className="flex gap-1 flex-wrap pt-2 border-t border-neutral-800">
                {inv.status === "draft" && (
                  <button
                    onClick={() => issueMutation.mutate({ companyId, invoiceId: inv.id })}
                    disabled={issueMutation.isPending}
                    className="rounded-md border border-sky-500/40 hover:border-sky-500 text-sky-300 px-2 py-0.5 text-xs"
                  >
                    {t("dossierDetail.issueLower")}
                  </button>
                )}
                {inv.status === "issued" && isAdmin && (
                  <button
                    onClick={() => approveMutation.mutate({ companyId, invoiceId: inv.id })}
                    disabled={approveMutation.isPending}
                    className="rounded-md border border-cyan-500/40 hover:border-cyan-500 text-cyan-300 px-2 py-0.5 text-xs"
                  >
                    {t("dossierDetail.approveLower")}
                  </button>
                )}
                {inv.status === "approved" && isAdmin && (
                  <button
                    onClick={() => markPaidMutation.mutate({ companyId, invoiceId: inv.id })}
                    disabled={markPaidMutation.isPending}
                    className="rounded-md border border-lime/40 hover:border-lime text-lime px-2 py-0.5 text-xs"
                  >
                    {t("dossierDetail.markPaidInvoice")}
                  </button>
                )}
                {(inv.status === "draft" || inv.status === "issued" || inv.status === "approved") &&
                  isAdmin && (
                    <button
                      onClick={async () => {
                        const reason = await modals.promptReason({
                          title: t("dossierDetail.cancelIcTitle"),
                          required: true,
                          tone: "warning",
                        });
                        if (!reason) return;
                        cancelMutation.mutate({
                          companyId,
                          invoiceId: inv.id,
                          reason,
                        });
                      }}
                      disabled={cancelMutation.isPending}
                      className="rounded-md border border-red-500/40 hover:border-red-500 text-red-300 px-2 py-0.5 text-xs"
                    >
                      {t("dossierDetail.cancelLower")}
                    </button>
                  )}
              </div>
            )}
          </div>
        ))}
      </div>

      {canEdit && showCreate && (
        <CreateIntercompanyModal
          companyId={companyId}
          dossierId={dossierId}
          billingCompanyId={billingCompanyId}
          candidateIssuerIds={candidateIssuerIds}
          accessibleCompanies={accessibleCompanies}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            utils.intercompanyInvoices.invalidate();
          }}
        />
      )}
    </section>
  );
}

function CreateIntercompanyModal({
  companyId,
  dossierId,
  billingCompanyId,
  candidateIssuerIds,
  accessibleCompanies,
  onClose,
  onSaved,
}: {
  companyId: number;
  dossierId: number;
  billingCompanyId: number;
  candidateIssuerIds: ReadonlyArray<number>;
  accessibleCompanies: ReadonlyArray<{ id: number; name: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const [issuerCompanyId, setIssuerCompanyId] = useState<number | null>(
    candidateIssuerIds[0] ?? null,
  );
  const [error, setError] = useState<string | null>(null);

  const createMutation = trpc.intercompanyInvoices.createDraft.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (issuerCompanyId == null) {
      setError(t("dossierDetail.selectIssuer"));
      return;
    }
    createMutation.mutate({
      companyId,
      dossierId,
      issuerCompanyId,
      receiverCompanyId: billingCompanyId,
    });
  }

  return (
    <ModalShell title={t("dossierDetail.createIcDraftTitle")} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <Field label={t("dossierDetail.issuerCompany")} required>
          <select
            value={issuerCompanyId ?? ""}
            onChange={(e) => setIssuerCompanyId(Number(e.target.value))}
            disabled={createMutation.isPending}
            className={inputClass}
          >
            {candidateIssuerIds.map((cid) => (
              <option key={cid} value={cid}>
                {accessibleCompanies.find((c) => c.id === cid)?.name ?? `#${cid}`}
              </option>
            ))}
          </select>
        </Field>
        <p className="text-xs text-neutral-500">
          {t("dossierDetail.receiverBillingCompany")}: #{billingCompanyId} ·{" "}
          {accessibleCompanies.find((c) => c.id === billingCompanyId)?.name ?? "—"}
        </p>
        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("dossierDetail.cancel")}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {createMutation.isPending ? t("dossierDetail.creating") : t("dossierDetail.create")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// TAB: AUDIT
// ============================================================================

function TabAudit({
  companyId,
  dossierId,
  isAdmin,
}: {
  companyId: number;
  dossierId: number;
  isAdmin: boolean;
}) {
  const t = useT();
  const auditQuery = trpc.audit.listByCompany.useQuery(
    {
      companyId,
      entityKind: "vehicle_service_job",
      entityId: dossierId,
      limit: 100,
      offset: 0,
    },
    { enabled: isAdmin },
  );

  if (!isAdmin) {
    return (
      <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
        {t("dossierDetail.auditRequiresAdmin")}
      </p>
    );
  }

  const rows = auditQuery.data?.rows ?? [];

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {t("dossierDetail.auditLogTitle")}
      </h3>
      {auditQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("dossierDetail.loading")}</p>
      )}
      {rows.length === 0 && !auditQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("dossierDetail.noEvents")}
        </p>
      )}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-neutral-950/50 text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2 w-40">{t("dossierDetail.colWhen")}</th>
              <th className="text-left px-3 py-2 w-32">{t("dossierDetail.colAction")}</th>
              <th className="text-left px-3 py-2 w-32">{t("dossierDetail.colBy")}</th>
              <th className="text-left px-3 py-2">{t("dossierDetail.colReasonDetails")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 text-neutral-400 font-mono">
                  {new Date(r.occurredAt).toLocaleString("pt-BR")}
                </td>
                <td className="px-3 py-2 font-mono">{r.action}</td>
                <td className="px-3 py-2 text-neutral-400">
                  {r.userName ?? r.userEmail ?? `#${r.userId}`}
                </td>
                <td className="px-3 py-2 text-neutral-400">
                  {r.reason && <span className="text-amber-200">{r.reason}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * F-commission-cleanup C3 — Cascade editável de comissão (modelo complementar F8).
 *
 * Inputs editáveis para os 3 pcts não-derivados (cliente em 'budget',
 * técnico, intermediário). Euro Storm é sempre o resíduo (read-only, com 🔒).
 *
 * Layout por variante:
 *   - 'budget' (orçamento): 4 linhas Cliente/Técnico/Intermediário/Euro Storm
 *     sobre Prix Brut. Cliente entra no split.
 *   - 'invoiced' (faturado): nota acima "Client a retenu X € = Y% sur Prix Brut Z€"
 *     + 3 linhas Técnico/Intermediário/Euro Storm sobre Total Facture +
 *     rodapé "Vérification brute: X + Y + Z + W = Prix Brut".
 *
 * Badge ✓/✕ visível sempre. Callback `onValidityChange` notifica o pai
 * para bloquear o submit quando soma ≠ 100%.
 *
 * Valores formatados no padrão francês (1 400,00 €).
 */
function CommissionCascadeEditor({
  commissionBase,
  prixBrut,
  totalFacture,
  clientPctInput,
  setClientPctInput,
  technicianPctInput,
  setTechnicianPctInput,
  intermediaryPctInput,
  setIntermediaryPctInput,
  technicianSelected,
  disabled,
  onValidityChange,
  companyName = "Euro Storm",
}: {
  commissionBase: "budget" | "invoiced";
  prixBrut: number | null;
  totalFacture: number | null;
  clientPctInput: string;
  setClientPctInput: (v: string) => void;
  technicianPctInput: string;
  setTechnicianPctInput: (v: string) => void;
  intermediaryPctInput: string;
  setIntermediaryPctInput: (v: string) => void;
  technicianSelected: boolean;
  disabled: boolean;
  onValidityChange: (valid: boolean) => void;
  /** F-billing-company-label — empresa faturadora (absorve o resíduo). */
  companyName?: string;
}) {
  const t = useT();
  const isBudget = commissionBase === "budget";

  // Parse seguro dos inputs (vazio = 0).
  const clientPct = clientPctInput ? Number(clientPctInput) || 0 : 0;
  const technicianPct = technicianPctInput ? Number(technicianPctInput) || 0 : 0;
  const intermediaryPct = intermediaryPctInput ? Number(intermediaryPctInput) || 0 : 0;

  // Em base 'invoiced', cliente NÃO entra no split (effectiveClientPct = 0).
  const effectiveClientPct = isBudget ? clientPct : 0;
  const eurostormPct = 100 - effectiveClientPct - technicianPct - intermediaryPct;
  const sum = effectiveClientPct + technicianPct + intermediaryPct + eurostormPct;
  const valid = Math.abs(sum - 100) < 0.01 && eurostormPct >= -0.01;

  // Notifica o pai sobre validade (controla disabled do Salvar).
  useEffect(() => {
    onValidityChange(valid && technicianSelected);
  }, [valid, technicianSelected, onValidityChange]);

  // Base do split: Prix Brut em budget; Total Facture em invoiced.
  const baseAmount = isBudget ? prixBrut : totalFacture;
  // Retenção do cliente em invoiced (Prix Brut − Total Facture).
  const clientRetentionAmount =
    !isBudget && prixBrut != null && totalFacture != null ? prixBrut - totalFacture : null;
  const clientRetentionPctOnBrut =
    clientRetentionAmount != null && prixBrut != null && prixBrut > 0
      ? (clientRetentionAmount / prixBrut) * 100
      : null;

  const fmt = (v: number | null) =>
    v == null
      ? "—"
      : `${new Intl.NumberFormat("fr-FR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(v)} €`;
  const valOf = (pct: number) => (baseAmount != null ? (baseAmount * pct) / 100 : null);

  // Verificação bruta em invoiced: soma dos 4 valores em €.
  const bruteSum =
    !isBudget && clientRetentionAmount != null
      ? clientRetentionAmount +
        (valOf(technicianPct) ?? 0) +
        (valOf(intermediaryPct) ?? 0) +
        (valOf(eurostormPct) ?? 0)
      : null;

  if (!technicianSelected) {
    return (
      <div className="rounded-md border border-neutral-800 bg-neutral-900/30 p-3 text-xs text-neutral-500">
        {t("dossierDetail.selectTechnicianForCascade")}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/30 p-4 space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-medium text-neutral-200">{t("dossierDetail.cascadeTitle")}</span>
        <span className="text-xs text-neutral-400">
          {t("dossierDetail.base")} ({isBudget ? "Prix Brut" : "Total Facture"}):{" "}
          <strong className="text-neutral-200 tabular-nums">{fmt(baseAmount)}</strong>
        </span>
      </div>

      {/* Nota acima em base invoiced: o que o cliente já reteve */}
      {!isBudget && (
        <div className="rounded border border-neutral-800/50 bg-neutral-900/40 px-3 py-2 text-xs text-neutral-400">
          {t("dossierDetail.clientRetainedPre")}{" "}
          <strong className="text-neutral-200 tabular-nums">{fmt(clientRetentionAmount)}</strong>
          {clientRetentionPctOnBrut != null && prixBrut != null ? (
            <>
              {" "}
              ={" "}
              <strong className="text-neutral-200 tabular-nums">
                {clientRetentionPctOnBrut.toFixed(2)}%
              </strong>{" "}
              {t("dossierDetail.onPrixBrut")} <span className="tabular-nums">{fmt(prixBrut)}</span>
            </>
          ) : null}
        </div>
      )}

      {/* Tabela de linhas */}
      <div className="grid grid-cols-[1fr_90px_auto] gap-x-3 gap-y-2 items-center">
        {/* Cliente — só em budget */}
        {isBudget && (
          <CascadeRow
            label={t("dossierDetail.cascadeClient")}
            pctValue={clientPctInput}
            onPctChange={setClientPctInput}
            valueEuro={valOf(clientPct)}
            disabled={disabled}
            fmt={fmt}
          />
        )}
        {/* Técnico */}
        <CascadeRow
          label={t("dossierDetail.cascadeTechnician")}
          pctValue={technicianPctInput}
          onPctChange={setTechnicianPctInput}
          valueEuro={valOf(technicianPct)}
          disabled={disabled}
          fmt={fmt}
        />
        {/* Intermediário */}
        <CascadeRow
          label={t("dossierDetail.cascadeIntermediary")}
          pctValue={intermediaryPctInput}
          onPctChange={setIntermediaryPctInput}
          valueEuro={valOf(intermediaryPct)}
          disabled={disabled}
          fmt={fmt}
        />
        {/* Empresa faturadora — read-only, com cadeado */}
        <CascadeRowReadonly
          label={companyName}
          pct={eurostormPct}
          valueEuro={valOf(eurostormPct)}
          fmt={fmt}
          warn={eurostormPct < -0.01}
        />
        {/* Total + badge ✓/✕ */}
        <div className="col-span-3 border-t border-neutral-700 pt-2 flex items-center justify-between">
          <span className="text-xs text-neutral-500">
            {isBudget ? t("dossierDetail.totalUpper") : t("dossierDetail.totalNetUpper")}
          </span>
          <div className="flex items-center gap-3">
            <span className={`tabular-nums text-sm ${valid ? "text-neutral-200" : "text-red-400"}`}>
              {sum.toFixed(2)}%
            </span>
            <span
              className={`tabular-nums text-sm font-medium ${
                valid ? "text-neutral-200" : "text-red-400"
              }`}
            >
              {fmt(baseAmount)}
            </span>
            {valid ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-lime/20 text-lime text-xs">
                ✓
              </span>
            ) : (
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-400 text-xs"
                title={`${t("dossierDetail.sumEquals")} ${sum.toFixed(2)}%, ${t("dossierDetail.expected100")}`}
              >
                ✕
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Verificação bruta em invoiced: cliente + tec + interm + euro storm = Prix Brut */}
      {!isBudget && bruteSum != null && prixBrut != null && (
        <p className="text-[11px] text-neutral-500 pt-1 border-t border-neutral-800/50">
          {t("dossierDetail.bruteCheck")}: {fmt(clientRetentionAmount)} +{" "}
          {fmt(valOf(technicianPct))} + {fmt(valOf(intermediaryPct))} + {fmt(valOf(eurostormPct))} ={" "}
          <strong
            className={`tabular-nums ${
              Math.abs(bruteSum - prixBrut) < 0.01 ? "text-lime" : "text-red-400"
            }`}
          >
            {fmt(bruteSum)}
          </strong>{" "}
          (Prix Brut {fmt(prixBrut)})
        </p>
      )}

      {eurostormPct < -0.01 && (
        <p className="text-xs text-red-400">
          {t("dossierDetail.sumExceedsPre")} {companyName} {t("dossierDetail.sumExceedsSuf")}
        </p>
      )}
    </div>
  );
}

function CascadeRow({
  label,
  pctValue,
  onPctChange,
  valueEuro,
  disabled,
  fmt,
}: {
  label: string;
  pctValue: string;
  onPctChange: (v: string) => void;
  valueEuro: number | null;
  disabled: boolean;
  fmt: (v: number | null) => string;
}) {
  return (
    <>
      <span className="text-neutral-300">{label}</span>
      <div className="relative">
        <input
          type="number"
          step="0.01"
          min={0}
          max={100}
          value={pctValue}
          onChange={(e) => onPctChange(e.target.value)}
          disabled={disabled}
          placeholder="0"
          className="w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1 text-sm text-right tabular-nums focus:border-lime outline-none disabled:opacity-50 pr-6"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-500 pointer-events-none">
          %
        </span>
      </div>
      <span className="text-neutral-200 tabular-nums text-right text-sm font-medium min-w-[110px]">
        {fmt(valueEuro)}
      </span>
    </>
  );
}

function CascadeRowReadonly({
  label,
  pct,
  valueEuro,
  fmt,
  warn,
}: {
  label: string;
  pct: number;
  valueEuro: number | null;
  fmt: (v: number | null) => string;
  warn: boolean;
}) {
  const t = useT();
  const pctColor = warn ? "text-red-400" : "text-lime";
  const valueColor = warn ? "text-red-400" : "text-lime";
  return (
    <>
      <span className="text-neutral-300 flex items-center gap-1">
        <span>{label}</span>
        <span
          aria-hidden
          className="text-[10px] text-neutral-500"
          title={t("dossierDetail.autoCalculatedTitle")}
        >
          🔒
        </span>
      </span>
      <div className="relative">
        <input
          type="text"
          value={pct.toFixed(2)}
          disabled
          readOnly
          className={`w-full rounded border border-neutral-800 bg-neutral-950/60 px-2 py-1 text-sm text-right tabular-nums cursor-not-allowed pr-6 ${pctColor}`}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-500 pointer-events-none">
          %
        </span>
      </div>
      <span className={`tabular-nums text-right text-sm font-medium min-w-[110px] ${valueColor}`}>
        {fmt(valueEuro)}
      </span>
    </>
  );
}

/**
 * F-line-status-i18n (2026-09-15, print do CEO): os chips mostravam o enum cru
 * do banco ("not_applicable", "pending") no meio da UI traduzida. Mapeia
 * valor → rótulo i18n + cor FIXA por estado (tema claro e escuro).
 */
const LINE_STATUS_LABEL: Record<string, MessageKey> = {
  not_applicable: "dossierDetail.lineStatusNotApplicable",
  pending: "dossierDetail.lineStatusPending",
  proforma_issued: "dossierDetail.lineStatusProformaIssued",
  paid: "dossierDetail.lineStatusPaid",
  received: "dossierDetail.lineStatusReceived",
  approved: "dossierDetail.lineStatusApproved",
  rejected: "dossierDetail.lineStatusRejected",
  cancelled: "dossierDetail.lineStatusCancelled",
};
const LINE_STATUS_COLOR: Record<string, string> = {
  pending: "#D97706", // âmbar — aguardando ação
  proforma_issued: "#2563EB", // azul — em trânsito
  received: "#2563EB",
  approved: "#16A34A", // verde — ok
  paid: "#16A34A",
  rejected: "#DC2626", // vermelho — problema
  cancelled: "#DC2626",
};

function StatusBadge({ label, value }: { label: string; value: string }) {
  const tr = useT();
  const key = LINE_STATUS_LABEL[value];
  return (
    <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-neutral-800 text-neutral-400">
      <span className="text-neutral-500">{label}:</span>
      <span style={LINE_STATUS_COLOR[value] ? { color: LINE_STATUS_COLOR[value] } : undefined}>
        {key ? tr(key) : value}
      </span>
    </span>
  );
}

function ValueBlock({
  label,
  value,
  suffix,
  money,
}: {
  label: string;
  value: string | null | number;
  suffix?: string;
  money?: boolean;
}) {
  const display =
    value == null
      ? "—"
      : money
        ? formatMoney(value, { decimals: 2 })
        : `${value}${suffix ? ` ${suffix}` : ""}`;
  return (
    <div>
      <div className="text-neutral-500 text-xs">{label}</div>
      <div className="font-mono">{display}</div>
    </div>
  );
}

function DlRow({
  label,
  value,
  mono,
  badge,
  badgeText,
}: {
  label: string;
  value: string;
  mono?: boolean;
  badge?: string;
  badgeText?: string;
}) {
  return (
    <div className="flex gap-2">
      {/* Celular: o rótulo fica com ~40 % e nunca encolhe; desktop: 10 rem. */}
      <dt className="w-[40%] shrink-0 text-xs text-neutral-500 sm:w-40">{label}:</dt>
      <dd className={`min-w-0 break-words ${mono ? "font-mono" : ""}`}>
        {badge ? (
          <span className={`inline-block rounded px-2 py-0.5 text-xs ${badge}`}>
            {badgeText ?? value}
          </span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function DlBlock({
  label,
  value,
  suffix,
  tone,
}: {
  label: string;
  value: string;
  suffix?: string;
  tone?: "amber";
}) {
  return (
    <div>
      <div className="text-neutral-500 text-xs">{label}</div>
      <div className={`font-mono text-base ${tone === "amber" ? "text-amber-300" : ""}`}>
        {value}
        {suffix && <span className="text-neutral-500 text-sm ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

/**
 * F-lancar-tecnico (CEO 2026-10-09, HB-244-TH do Francisco — técnico sem login
 * no portal) — o escritório lança o técnico no carro com a MESMA regra do
 * "assumir" do portal (comissão pelo par, peças/funilaria 100 % casa,
 * franquia, ordem de reparação). Carro já reparado: marca PDR finalizado e
 * desmontagem/montagem concluídas. Sem recepção, o Super Admin libera por
 * derrogação com o motivo.
 */
function LaunchTechnicianModal({
  companyId,
  dossierId,
  temPdr,
  temMontagem,
  titulo,
  onClose,
}: {
  companyId: number;
  dossierId: number;
  temPdr: boolean;
  temMontagem: boolean;
  /** Atalho do dégarnissage: título próprio (só a opção montagem aparece). */
  titulo?: string;
  onClose: () => void;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const techsQ = trpc.technicians.list.useQuery({ companyId, active: true, limit: 200, offset: 0 });
  const techs = [...(techsQ.data?.rows ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const opcoes: Array<{ v: "pdr" | "pdr_montage" | "montage"; label: string }> = [
    ...(temPdr && temMontagem
      ? [{ v: "pdr_montage" as const, label: t("dossierDetail.launchTechPdrMontage") }]
      : []),
    ...(temPdr ? [{ v: "pdr" as const, label: t("dossierDetail.launchTechPdr") }] : []),
    ...(temMontagem
      ? [{ v: "montage" as const, label: t("dossierDetail.launchTechMontage") }]
      : []),
  ];
  const [technicianId, setTechnicianId] = useState<number | null>(null);
  const [choice, setChoice] = useState<"pdr" | "pdr_montage" | "montage">(opcoes[0]?.v ?? "pdr");
  const [done, setDone] = useState(true);
  const [reason, setReason] = useState("");
  const [avisos, setAvisos] = useState<string[] | null>(null);
  const mut = trpc.technicianPortal.officeLaunch.useMutation({
    onSuccess: (r) => {
      void utils.invalidate();
      setAvisos(r.warnings ?? []);
    },
  });
  const campo = "w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-sm";
  return (
    <ModalShell title={titulo ?? t("dossierDetail.launchTechTitle")} onClose={onClose}>
      {avisos ? (
        <div className="space-y-3 p-5" data-testid="launch-tech-ok">
          <p className="text-sm text-lime">✓ {t("dossierDetail.launchTechOk")}</p>
          {avisos.length > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-xs text-amber-300">
              {avisos.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm"
          >
            {t("dossierDetail.launchTechClose")}
          </button>
        </div>
      ) : (
        <div className="space-y-3 p-5">
          <p className="text-xs text-text-muted">{t("dossierDetail.launchTechHint")}</p>
          <label className="block text-xs text-neutral-400">
            {t("dossierDetail.launchTechTech")}
            <select
              value={technicianId ?? ""}
              onChange={(e) => setTechnicianId(e.target.value ? Number(e.target.value) : null)}
              className={`${campo} mt-1`}
              data-testid="launch-tech-select"
            >
              <option value="">{t("dossierDetail.launchTechChoose")}</option>
              {techs.map((tc) => (
                <option key={tc.id} value={tc.id}>
                  {tc.name}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="space-y-1 text-sm">
            <legend className="mb-1 text-xs text-neutral-400">
              {t("dossierDetail.launchTechChoice")}
            </legend>
            {opcoes.map((o) => (
              <label key={o.v} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="launch-choice"
                  checked={choice === o.v}
                  onChange={() => setChoice(o.v)}
                />
                {o.label}
              </label>
            ))}
          </fieldset>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={done} onChange={(e) => setDone(e.target.checked)} />
            {t("dossierDetail.launchTechDone")}
          </label>
          <label className="block text-xs text-neutral-400">
            {t("dossierDetail.launchTechReason")}
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={`${campo} mt-1`}
              data-testid="launch-tech-reason"
            />
          </label>
          {mut.error && (
            <p className="rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-700">
              ✕ {mut.error.message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm"
            >
              {t("dossierDetail.launchTechCancel")}
            </button>
            <button
              type="button"
              disabled={technicianId == null || reason.trim().length < 3 || mut.isPending}
              onClick={() =>
                technicianId != null &&
                mut.mutate({
                  companyId,
                  dossierId,
                  technicianId,
                  choice,
                  done,
                  reason: reason.trim(),
                })
              }
              className="rounded-md bg-lime px-3 py-1.5 text-sm font-semibold text-on-lime disabled:opacity-40"
              data-testid="launch-tech-submit"
            >
              👷 {mut.isPending ? "…" : t("dossierDetail.launchTechSubmit")}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function ModalShell({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** F-devis-edit — tabela de peças precisa de largura. */
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`w-full ${wide ? "max-w-5xl" : "max-w-3xl"} bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto`}
      >
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950 z-10">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </span>
      {children}
    </label>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-7xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}

function parseDecimal(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

/**
 * F-derrogacao-ui (CEO 2026-09-28, EP-779-ZQ) — liberação excepcional do
 * reparo SEM recepção (a trava da franquia/checklist). Só super admin, motivo
 * obrigatório, fica no histórico. Antes só existia por script: carro de
 * atelier sem técnico de PDR não tinha quem fizesse a recepção e o montador
 * ficava travado.
 */
/**
 * F-entrega-derrogacao (CEO 2026-10-02) — "como finalizo o carro como super
 * admin?": entrega registrada sem as fotos do gerente, com motivo e auditoria.
 */
function EntregaDerrogacao({ dossierId }: { dossierId: number }) {
  const t = useT();
  const modals = useModals();
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const marcar = trpc.franchiseDerogations.markDelivered.useMutation({
    onSuccess: () => {
      void utils.dossiers.getById.invalidate();
      void utils.dossiers.invalidate();
      // CEO 2026-10-19: o card da OR seguia em "livraison en attente" até
      // recarregar — a prontidão da ordem vem de repairOrders.byDossier.
      void utils.repairOrders.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  if (!user?.isSuperAdmin) return null;
  return (
    <button
      type="button"
      disabled={marcar.isPending}
      onClick={async () => {
        const reason = await modals.promptReason({
          title: t("dossierDetail.deliveryDerogTitle"),
          message: t("dossierDetail.deliveryDerogMessage"),
          placeholder: t("dossierDetail.derogReasonPh"),
          confirmLabel: t("dossierDetail.deliveryDerogConfirm"),
          required: true,
          maxLength: 1000,
        });
        if (reason == null || reason.trim().length < 5) return;
        marcar.mutate({ dossierId, reason: reason.trim() });
      }}
      className="rounded-md border border-amber-500/40 hover:border-amber-400 text-amber-300 px-2 py-1 text-[11px] uppercase tracking-wider whitespace-nowrap disabled:opacity-40"
      title={t("dossierDetail.deliveryDerogTitle")}
    >
      🔓 {t("dossierDetail.deliveryDerogBtn")}
    </button>
  );
}

function LiberarReparo({ dossierId, temRecepcao }: { dossierId: number; temRecepcao: boolean }) {
  const t = useT();
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const isSuper = user?.isSuperAdmin === true;
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const q = trpc.franchiseDerogations.listByDossier.useQuery({ dossierId }, { enabled: isSuper });
  const criar = trpc.franchiseDerogations.create.useMutation({
    onSuccess: () => {
      setAberto(false);
      setMotivo("");
      setErro(null);
      void utils.franchiseDerogations.listByDossier.invalidate();
      void utils.dossiers.invalidate();
    },
    onError: (e) => setErro(e.message),
  });
  if (!isSuper || q.isLoading) return null;
  const rows = q.data?.rows ?? [];
  if (rows.length === 0 && temRecepcao) return null;
  return (
    <section
      className="rounded-lg border p-4 lg:col-span-2 space-y-2"
      style={{ borderColor: "var(--border)", background: "var(--surface-card)" }}
      data-testid="liberar-reparo"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {t("dossierDetail.derogTitle")}
      </h3>
      {rows.map((r) => (
        <p key={r.id} className="text-sm">
          <span className="badge b-ok mr-2">
            <span className="led" />
            {t("dossierDetail.derogReleased")}
          </span>
          {r.releasedByName} · {new Date(r.createdAt).toLocaleString("fr-FR")}
          <span className="block text-xs" style={{ color: "var(--text-muted)" }}>
            {r.reason}
          </span>
        </p>
      ))}
      {rows.length === 0 && !aberto && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="flex-1 text-sm" style={{ color: "var(--text-muted)" }}>
            {t("dossierDetail.derogNoReception")}
          </p>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setAberto(true)}
            data-testid="liberar-reparo-btn"
          >
            🔓 {t("dossierDetail.derogBtn")}
          </button>
        </div>
      )}
      {aberto && (
        <div className="space-y-2">
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={t("dossierDetail.derogReasonPh")}
            className={inputClass}
            autoFocus
          />
          {erro && <p className="text-xs text-red-500">{erro}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn--primary btn--sm disabled:opacity-40"
              disabled={criar.isPending || motivo.trim().length < 5}
              onClick={() => criar.mutate({ dossierId, reason: motivo.trim() })}
            >
              {t("dossierDetail.derogConfirm")}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setAberto(false)}
            >
              {t("dossierDetail.chantierFranchiseCancel")}
            </button>
          </div>
          <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
            {t("dossierDetail.derogHint")}
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * F-valor-fixo-dossier (CEO 2026-10-02) — "quero escolher para esse carro o
 * valor que o técnico vai receber": PDR e dégarnissage fixos no dossier,
 * antes do claim; quem assumir recebe o fixo e o % é derivado. Só a parte do
 * técnico muda; a franquia não reduz o fixo. Limpar volta ao par.
 */
/** Evento do atalho do topo → abre o formulário do card de valor fixo. */
const ABRIR_VALOR_FIXO = "eurostorm:abrir-valor-fixo";

function ValorFixoTecnico({
  companyId,
  dossierId,
  canEdit,
}: {
  companyId: number;
  dossierId: number;
  canEdit: boolean;
}) {
  const t = useT();
  const modals = useModals();
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const q = trpc.dossiers.getById.useQuery({ companyId, dossierId });
  const d = q.data;
  const isAdmin =
    user != null &&
    (user.isSuperAdmin ||
      user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin"));
  const [aberto, setAberto] = useState(false);
  const [dsp, setDsp] = useState("");
  const [mont, setMont] = useState("");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  // CEO 2026-10-26 — o botão "💶 Montant fixe" do topo pede para abrir o
  // formulário; espera os dados do dossier chegarem.
  const [pedido, setPedido] = useState(false);
  useEffect(() => {
    const ouvir = () => setPedido(true);
    window.addEventListener(ABRIR_VALOR_FIXO, ouvir);
    return () => window.removeEventListener(ABRIR_VALOR_FIXO, ouvir);
  }, []);
  useEffect(() => {
    if (!pedido || !d) return;
    setPedido(false);
    if (!canEdit) return;
    setDsp(d.technicianFixedDspHt != null ? String(Number(d.technicianFixedDspHt)) : "");
    setMont(d.technicianFixedMontageHt != null ? String(Number(d.technicianFixedMontageHt)) : "");
    setAberto(true);
    setTimeout(() => {
      document
        .getElementById("sec-valor-fixo")
        ?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 60);
  }, [pedido, d, canEdit]);
  const salvar = trpc.dossiers.setTechnicianFixed.useMutation({
    onSuccess: (r) => {
      setErro(null);
      setAberto(false);
      setMotivo("");
      void utils.dossiers.invalidate();
      void utils.serviceLines.invalidate();
      if (r.warnings.length > 0) {
        void modals.alert({
          title: t("dossierDetail.fixedTitle"),
          message: r.warnings.join(" · "),
        });
      }
    },
    onError: (e) => setErro(e.message),
  });
  // CEO 2026-10-26 — informação INTERNA: só o admin vê que o carro tem valor
  // fixo (o técnico vê só a comissão no portal, sem saber como foi calculada).
  if (!d || !isAdmin) return null;
  const bd = (d.devisBreakdown ?? null) as { totalDsp?: number; degarnissage?: number } | null;
  const devisDsp = Number(bd?.totalDsp ?? 0);
  const devisMont = Number(bd?.degarnissage ?? 0);
  const atualDsp = d.technicianFixedDspHt != null ? Number(d.technicianFixedDspHt) : null;
  const atualMont = d.technicianFixedMontageHt != null ? Number(d.technicianFixedMontageHt) : null;
  const tem = atualDsp != null || atualMont != null;
  const pct = (v: number, base: number) => (base > 0 ? Math.round((v / base) * 1000) / 10 : null);
  const abrir = () => {
    setDsp(atualDsp != null ? String(atualDsp) : "");
    setMont(atualMont != null ? String(atualMont) : "");
    setAberto(true);
  };
  const podeEditar = isAdmin && canEdit;
  const inputCls =
    "w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm font-mono";
  const toNum = (s: string) => (s.trim() === "" ? null : Number(s));
  const dspN = toNum(dsp);
  const montN = toNum(mont);
  const ok =
    motivo.trim().length >= 3 &&
    (dspN == null || (Number.isFinite(dspN) && dspN >= 0)) &&
    (montN == null || (Number.isFinite(montN) && montN >= 0));
  return (
    <div className="mb-3 space-y-2">
      <p
        className="flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-sm"
        style={
          tem
            ? { background: "#ECFCCB", border: "1px solid #65A30D", color: "#365314" }
            : { background: "var(--surface-muted)", border: "1px solid var(--border)" }
        }
      >
        {tem ? (
          <>
            {"💶"} {t("dossierDetail.fixedTitle")}:{" "}
            {atualDsp != null && (
              <span>
                PDR <strong className="font-mono">{formatMoney(atualDsp, { decimals: 2 })}</strong>
                {pct(atualDsp, devisDsp) != null && (
                  <span className="opacity-70"> ({pct(atualDsp, devisDsp)} %)</span>
                )}
              </span>
            )}
            {atualMont != null && (
              <span>
                {atualDsp != null && " · "}
                {t("dossierDetail.fixedMontageShort")}{" "}
                <strong className="font-mono">{formatMoney(atualMont, { decimals: 2 })}</strong>
                {pct(atualMont, devisMont) != null && (
                  <span className="opacity-70"> ({pct(atualMont, devisMont)} %)</span>
                )}
              </span>
            )}
            {d.technicianFixedReason && (
              <span className="opacity-70">— {d.technicianFixedReason}</span>
            )}
          </>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>{t("dossierDetail.fixedNone")}</span>
        )}
        {podeEditar && (
          <button
            type="button"
            onClick={() => (aberto ? setAberto(false) : abrir())}
            className="ml-auto rounded-md border px-2 py-0.5 text-xs"
            style={{ borderColor: "var(--border)" }}
          >
            {aberto
              ? t("dossierDetail.cancel")
              : tem
                ? t("dossierDetail.fixedEditBtn")
                : t("dossierDetail.fixedSetBtn")}
          </button>
        )}
      </p>
      {aberto && (
        <div
          className="space-y-3 rounded-md border p-3"
          style={{ borderColor: "var(--border)", background: "var(--surface-card)" }}
        >
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            {t("dossierDetail.fixedHint")}
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
              {t("dossierDetail.fixedDspLabel")}
              {devisDsp > 0 && ` · ${"Devis"} ${formatMoney(devisDsp, { decimals: 2 })}`}
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={dsp}
                onChange={(e) => setDsp(e.target.value)}
                placeholder={t("dossierDetail.fixedFollowPair")}
                className={inputCls + " mt-1"}
              />
              {dspN != null && pct(dspN, devisDsp) != null && (
                <span className="mt-1 block">→ {pct(dspN, devisDsp)} %</span>
              )}
            </label>
            <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
              {t("dossierDetail.fixedMontageLabel")}
              {devisMont > 0 && ` · ${"Devis"} ${formatMoney(devisMont, { decimals: 2 })}`}
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={mont}
                onChange={(e) => setMont(e.target.value)}
                placeholder={t("dossierDetail.fixedFollowPair")}
                className={inputCls + " mt-1"}
                disabled={devisMont <= 0}
              />
              {montN != null && pct(montN, devisMont) != null && (
                <span className="mt-1 block">→ {pct(montN, devisMont)} %</span>
              )}
            </label>
          </div>
          <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
            {t("dossierDetail.reassignReasonLabel")}
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className={inputCls.replace(" font-mono", "") + " mt-1"}
              placeholder={t("dossierDetail.derogReasonPh")}
            />
          </label>
          {erro && <p className="text-xs text-red-400">{erro}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            {tem && (
              <button
                type="button"
                disabled={salvar.isPending || motivo.trim().length < 3}
                onClick={() =>
                  salvar.mutate({
                    companyId,
                    dossierId,
                    dspHt: null,
                    montageHt: null,
                    reason: motivo.trim(),
                  })
                }
                className="rounded-md border border-amber-500/40 px-3 py-1.5 text-xs text-amber-300 disabled:opacity-40"
              >
                {t("dossierDetail.fixedClearBtn")}
              </button>
            )}
            <button
              type="button"
              disabled={salvar.isPending || !ok || (dspN == null && montN == null)}
              onClick={() =>
                salvar.mutate({
                  companyId,
                  dossierId,
                  dspHt: dspN,
                  montageHt: montN,
                  reason: motivo.trim(),
                })
              }
              className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
            >
              {t("dossierDetail.fixedSaveBtn")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * F-lancar-montagem (CEO 2026-10-02) — lança a linha de montagem do devis
 * (dégarnissage) para um técnico, já com as metades marcadas, ou como feita
 * por terceiro. Cria a linha pelo par técnico×cliente e aplica as marcas pelo
 * mesmo Réattribuer do card da linha.
 */
function LancarMontagemModal({
  companyId,
  dossierId,
  degarnissage,
  pdrTechnicianId,
  responsibleCompanyId,
  onClose,
  onSaved,
}: {
  companyId: number;
  dossierId: number;
  degarnissage: number;
  pdrTechnicianId: number | null;
  responsibleCompanyId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const techniciansQuery = trpc.technicians.list.useQuery({
    companyId,
    active: true,
    limit: 200,
    offset: 0,
  });
  const technicians = [...(techniciansQuery.data?.rows ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const [technicianId, setTechnicianId] = useState<number | null>(pdrTechnicianId);
  const [amount, setAmount] = useState(String(degarnissage));
  const [demontageDone, setDemontageDone] = useState(true);
  const [montageDone, setMontageDone] = useState(true);
  const [external, setExternal] = useState(false);
  const [reason, setReason] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const createMut = trpc.serviceLines.create.useMutation();
  const reassignMut = trpc.serviceLines.reassignTechnician.useMutation();
  const pending = createMut.isPending || reassignMut.isPending;
  const tec = technicianId ?? pdrTechnicianId;
  const valor = Number(amount);
  const ok = tec != null && Number.isFinite(valor) && valor > 0 && reason.trim().length >= 3;
  const sel = "w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm";
  async function salvar() {
    if (!ok || tec == null) return;
    setErro(null);
    try {
      const linha = await createMut.mutateAsync({
        companyId,
        dossierId,
        responsibleCompanyId,
        serviceType: "montage_demontage",
        technicianId: tec,
        orcamento: valor,
        faturado: valor,
        technicianAmountKnown: true,
        reason: reason.trim(),
      });
      await reassignMut.mutateAsync({
        companyId,
        lineId: linha.id,
        technicianId: tec,
        demontageDone: external ? true : demontageDone,
        montageDone: external ? true : montageDone,
        demontageExternal: external,
        montageExternal: external,
        reason: reason.trim(),
      });
      onSaved();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border border-neutral-700 bg-neutral-900 p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
          {t("dossierDetail.roAddMontageTitle")}
        </h3>
        <p className="text-xs text-neutral-500">{t("dossierDetail.roAddMontageHint")}</p>
        <label className="block text-xs text-neutral-400">
          {t("dossierDetail.roAddMontageTech")}
          <select
            value={tec ?? ""}
            onChange={(e) => setTechnicianId(e.target.value === "" ? null : Number(e.target.value))}
            className={sel + " mt-1"}
            disabled={external}
          >
            <option value="">—</option>
            {technicians.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-neutral-400">
          {t("dossierDetail.roAddMontageAmount")}
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={sel + " mt-1"}
          />
        </label>
        <div className="space-y-1 rounded-md border border-neutral-800 bg-neutral-950/60 p-2">
          <label className="flex items-center gap-2 text-xs text-neutral-300">
            <input
              type="checkbox"
              checked={external || demontageDone}
              disabled={external}
              onChange={(e) => setDemontageDone(e.target.checked)}
            />
            {t("dossierDetail.reassignDemontageDone")}
          </label>
          <label className="flex items-center gap-2 text-xs text-neutral-300">
            <input
              type="checkbox"
              checked={external || montageDone}
              disabled={external}
              onChange={(e) => setMontageDone(e.target.checked)}
            />
            {t("dossierDetail.reassignMontageDone")}
          </label>
          <label className="flex items-center gap-2 text-xs text-amber-300">
            <input
              type="checkbox"
              checked={external}
              onChange={(e) => setExternal(e.target.checked)}
            />
            {t("dossierDetail.roAddMontageExternal")}
          </label>
        </div>
        <label className="block text-xs text-neutral-400">
          {t("dossierDetail.reassignReasonLabel")}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className={sel + " mt-1"}
          />
        </label>
        {erro && <p className="text-xs text-red-400">{erro}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs hover:border-neutral-500"
          >
            {t("dossierDetail.cancel")}
          </button>
          <button
            type="button"
            disabled={!ok || pending}
            onClick={() => void salvar()}
            className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
          >
            {t("dossierDetail.roAddMontageConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** F-ordre-reparation fase 3 — o que falta para o carro estar completo. */
function roMissingKey(m: string): MessageKey {
  switch (m) {
    case "dsp":
      return "dossierDetail.roMissingDsp";
    case "montage":
      return "dossierDetail.roMissingMontage";
    case "montage_phases":
      return "dossierDetail.roMissingMontagePhases";
    default:
      return "dossierDetail.roMissingDelivery";
  }
}

/**
 * F-ordre-reparation (CEO 2026-09-20) — o documento do MEIO do fluxo do eDSP:
 * devis → ORDRE DE RÉPARATION → facture. Mostra o nº, o valor e deixa o
 * escritório reprecificar ANTES de faturar; a diferença vira remise.
 */
function RepairOrderCard({
  companyId,
  dossierId,
  canEdit,
}: {
  companyId: number;
  dossierId: number;
  canEdit: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const modals = useModals();
  const q = trpc.repairOrders.byDossier.useQuery({ companyId, dossierId });
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");
  const [po, setPo] = useState("");
  const [comment, setComment] = useState("");
  const invalidate = () => utils.repairOrders.byDossier.invalidate({ companyId, dossierId });
  const createMut = trpc.repairOrders.createForDossier.useMutation({ onSuccess: invalidate });
  const updateMut = trpc.repairOrders.update.useMutation({
    onSuccess: () => {
      setEditing(false);
      void invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.roSectionTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const abaPdf = useAbaPendente();
  const pdfMut = trpc.repairOrders.pdf.useMutation({
    onSuccess: (r) => abaPdf.ir(r.url),
    onError: () => abaPdf.fechar(),
  });
  // F-degarnissage-embutido — a linha PDR engloba o dégarnissage e o próprio
  // técnico o fez: divide em PDR + montagem concluída (valores não mudam).
  const splitMut = trpc.serviceLines.splitDegarnissage.useMutation({
    onSuccess: () => {
      void invalidate();
      void utils.dossiers.getById.invalidate();
      void utils.dossiers.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossierDetail.roSplitDegTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const embedded = q.data?.degarnissageEmbedded ?? null;
  // F-lancar-montagem — devis com dégarnissage e sem linha de montagem.
  const montageMissing = q.data?.montageMissing ?? null;
  const [lancarAberto, setLancarAberto] = useState(false);
  async function dividirDegarnissage() {
    if (!embedded) return;
    const reason = await modals.promptReason({
      title: t("dossierDetail.roSplitDegTitle"),
      message: `${t("dossierDetail.roSplitDegMsgPre")} ${embedded.technicianName ?? "—"} (${formatMoney(embedded.degarnissage, { decimals: 2 })}). ${t("dossierDetail.roSplitDegMsgSuf")}`,
      placeholder: t("dossierDetail.derogReasonPh"),
      confirmLabel: t("dossierDetail.roSplitDegConfirm"),
      required: true,
      maxLength: 500,
    });
    if (reason == null || reason.trim().length < 3) return;
    splitMut.mutate({ companyId, dossierId, markDspDone: true, reason: reason.trim() });
  }

  const order = q.data?.order ?? null;
  const invoiced = q.data?.invoiced ?? false;
  if (q.isLoading) return null;

  if (!order) {
    return (
      <section className="lg:col-span-2 rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
            {t("dossierDetail.roSectionTitle")}
          </h3>
          {canEdit && !invoiced && (
            <button
              type="button"
              disabled={createMut.isPending}
              onClick={() => createMut.mutate({ companyId, dossierId })}
              className="rounded-md border border-lime/50 px-3 py-1.5 text-xs text-lime hover:bg-lime/10 disabled:opacity-40"
            >
              + {t("dossierDetail.roCreateBtn")}
            </button>
          )}
        </div>
        <p className="pt-2 text-xs text-neutral-500">
          {createMut.error ? createMut.error.message : t("dossierDetail.roEmpty")}
        </p>
      </section>
    );
  }

  const remise = Math.round((order.amountHtInitial - order.amountHt) * 100) / 100;
  return (
    <section className="lg:col-span-2 rounded-lg border border-neutral-800 bg-neutral-900 p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("dossierDetail.roSectionTitle")}
          <span className="font-mono text-neutral-300">{order.number}</span>
          {/* F-ordre-reparation fase 3 — o carro está completo? Quem abriu a
              ordem pode não ser quem termina o serviço. */}
          {q.data?.ready ? (
            <span className="rounded bg-lime/15 px-2 py-0.5 text-[10px] font-semibold text-lime">
              ✓ {t("dossierDetail.roReady")}
            </span>
          ) : (
            <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
              ⏳ {(q.data?.missing ?? []).map((m) => t(roMissingKey(m))).join(" · ")}
            </span>
          )}
          {canEdit && embedded && (
            <button
              type="button"
              disabled={splitMut.isPending}
              onClick={() => void dividirDegarnissage()}
              title={t("dossierDetail.roSplitDegTitle")}
              className="rounded-md border border-amber-500/40 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-300 hover:border-amber-400 disabled:opacity-40"
            >
              🔧 {t("dossierDetail.roSplitDegBtn")}
            </button>
          )}
          {canEdit && montageMissing && (
            <button
              type="button"
              onClick={() => setLancarAberto(true)}
              title={t("dossierDetail.roAddMontageTitle")}
              className="rounded-md border border-amber-500/40 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-300 hover:border-amber-400"
            >
              ➕ {t("dossierDetail.roAddMontageBtn")}
            </button>
          )}
        </h3>
        {lancarAberto && montageMissing && (
          <LancarMontagemModal
            companyId={companyId}
            dossierId={dossierId}
            degarnissage={montageMissing.degarnissage}
            pdrTechnicianId={montageMissing.pdrTechnicianId}
            responsibleCompanyId={montageMissing.responsibleCompanyId}
            onClose={() => setLancarAberto(false)}
            onSaved={() => {
              setLancarAberto(false);
              void invalidate();
              void utils.serviceLines.invalidate();
              void utils.dossiers.invalidate();
            }}
          />
        )}
        <span className="flex items-center gap-2 text-xs text-neutral-500">
          {order.repairDate ?? "—"}
          {order.technicianName && ` · ${order.technicianName}`}
          <button
            type="button"
            disabled={pdfMut.isPending}
            onClick={() => {
              abaPdf.abrir();
              pdfMut.mutate({ companyId, dossierId });
            }}
            className="rounded-md border border-neutral-700 px-2 py-1 text-xs text-neutral-300 hover:border-neutral-500 disabled:opacity-40"
          >
            📄 PDF
          </button>
          {canEdit && !invoiced && !editing && (
            <button
              type="button"
              onClick={() => {
                setAmount(String(order.amountHt));
                setPo(order.clientPoNumber ?? "");
                setComment(order.comment ?? "");
                setEditing(true);
              }}
              className="rounded-md border border-neutral-700 px-2 py-1 text-xs text-neutral-300 hover:border-neutral-500"
            >
              ✎ {t("dossierDetail.roEditBtn")}
            </button>
          )}
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm md:grid-cols-4">
        {order.devisAmountHt != null && (
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-neutral-500">
              {t("dossierDetail.roDevisAmount")}
            </dt>
            <dd className="font-mono tabular-nums text-neutral-300">
              {formatMoney(order.devisAmountHt, { decimals: 2 })}
            </dd>
          </div>
        )}
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-neutral-500">
            {t("dossierDetail.roInitialAmount")}
          </dt>
          <dd className="font-mono tabular-nums text-neutral-300">
            {formatMoney(order.amountHtInitial, { decimals: 2 })}
          </dd>
        </div>
        {remise > 0.009 && (
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-neutral-500">
              {t("dossierDetail.roRemise")}
            </dt>
            <dd className="font-mono tabular-nums text-amber-300">
              − {formatMoney(remise, { decimals: 2 })}
            </dd>
          </div>
        )}
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-neutral-500">
            {t("dossierDetail.roAmount")}
          </dt>
          <dd className="font-mono tabular-nums font-semibold text-lime">
            {formatMoney(order.amountHt, { decimals: 2 })}
          </dd>
        </div>
      </dl>

      {(order.clientPoNumber || order.comment) && !editing && (
        <p className="pt-2 text-xs text-neutral-500">
          {order.clientPoNumber && `${t("dossierDetail.roClientPo")}: ${order.clientPoNumber}`}
          {order.clientPoNumber && order.comment && " · "}
          {order.comment}
        </p>
      )}

      {invoiced && (
        <p className="pt-2 text-xs text-neutral-500">{t("dossierDetail.roInvoicedLocked")}</p>
      )}

      {editing && (
        <div className="mt-3 space-y-2 rounded-md border border-neutral-800 bg-neutral-950 p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            <label className="text-xs text-neutral-400">
              {t("dossierDetail.roAmount")} (HT)
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(",", "."))}
                inputMode="decimal"
                className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 font-mono text-sm"
              />
            </label>
            <label className="text-xs text-neutral-400">
              {t("dossierDetail.roClientPo")}
              <input
                value={po}
                onChange={(e) => setPo(e.target.value)}
                className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm"
              />
            </label>
            <label className="text-xs text-neutral-400">
              {t("dossierDetail.roComment")}
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm"
              />
            </label>
          </div>
          <p className="text-[11px] text-neutral-500">{t("dossierDetail.roEditHint")}</p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={updateMut.isPending || !Number.isFinite(Number(amount))}
              onClick={() =>
                updateMut.mutate({
                  companyId,
                  dossierId,
                  amountHt: Number(amount),
                  clientPoNumber: po,
                  comment,
                })
              }
              className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
            >
              {t("common.save")}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs text-neutral-300"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * F-franquia-erp (CEO 2026-09-21) — franquia da recepção, com correção pelo
 * escritório. Ela sai da fatura do cliente, então até hoje só quem recebia o
 * carro podia informá-la (gerente do chantier ou técnico PDR, cada um no seu
 * portal). Quando ia errada e ninguém estava mais no pátio, não havia conserto
 * pela tela. Aqui o ADMIN corrige, com motivo obrigatório no log.
 */
function FranquiaRecepcao({
  companyId,
  dossierId,
  franchise,
  amountTtc,
  canEdit,
  semRecepcao = false,
}: {
  companyId: number;
  dossierId: number;
  franchise: boolean;
  amountTtc: number | null;
  canEdit: boolean;
  /** Carro sem recepção registrada: o texto não diz "recepção sem franquia". */
  semRecepcao?: boolean;
}) {
  const t = useT();
  const { user } = useAuth();
  const utils = trpc.useUtils();
  // CEO 2026-10-27 — particular: a fatura sai cheia (franquia só reduz a base
  // das comissões); profissional: fatura e comissões reduzem.
  const dossierKindQ = trpc.dossiers.getById.useQuery({ companyId, dossierId });
  const particular = dossierKindQ.data?.customerKind === "individual";
  // F-franquia-excecao (CEO 2026-10-30, EP-405-PS): por carro, "só nas
  // comissões" — Profissional com fatura cheia, como no Particular.
  const excecao = dossierKindQ.data?.franchiseCommissionsOnly === true;
  const soComissoes = particular || excecao;
  const [excecaoForm, setExcecaoForm] = useState(false);
  useEffect(() => setExcecaoForm(excecao), [excecao]);
  const [aberto, setAberto] = useState(false);
  const [tem, setTem] = useState(franchise);
  const [valor, setValor] = useState(amountTtc != null ? String(amountTtc) : "");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const modals = useModals();

  const isAdmin =
    user != null &&
    (user.isSuperAdmin ||
      user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin"));

  const salvar = trpc.dossiers.setReceptionFranchise.useMutation({
    onSuccess: (r) => {
      setErro(null);
      setAberto(false);
      setMotivo("");
      void utils.dossiers.invalidate();
      void utils.serviceLines.invalidate();
      void utils.customerInvoices.invalidate();
      // F-fatura-segue-franquia — a fatura emitida foi atualizada (ou não): dizer.
      const avisos = (r.warnings ?? []).filter((w) => !/^[a-z_]+$/.test(w));
      if (avisos.length > 0) {
        void modals.alert({
          title: t("dossierDetail.chantierFranchiseEdit"),
          message: avisos.join(" · "),
        });
      }
    },
    onError: (e) => setErro(e.message),
  });

  const podeEditar = isAdmin && canEdit;

  return (
    <div className="mb-3 space-y-2">
      <p
        className="flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-sm"
        style={
          franchise
            ? { background: "#FEF9C3", border: "1px solid #EAB308", color: "#854D0E" }
            : { background: "var(--surface-muted)", border: "1px solid var(--border)" }
        }
      >
        {franchise ? (
          <>
            {"⚠"}{" "}
            {soComissoes
              ? t("dossierDetail.chantierFranchisePreParticular")
              : t("dossierDetail.chantierFranchisePre")}{" "}
            <strong className="font-mono">
              {amountTtc != null ? formatMoney(amountTtc, { decimals: 2 }) : "—"}
            </strong>{" "}
            {soComissoes
              ? t("dossierDetail.chantierFranchiseSufParticular")
              : t("dossierDetail.chantierFranchiseSuf")}
            {excecao && !particular && (
              <span className="badge b-warn" data-testid="franchise-exception-badge">
                {t("dossierDetail.franchiseCommissionsOnlyBadge")}
              </span>
            )}
          </>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>
            {semRecepcao
              ? t("dossierDetail.franchiseNoReception")
              : t("dossierDetail.chantierNoFranchise")}
          </span>
        )}
        {podeEditar && (
          <button
            type="button"
            onClick={() => setAberto((o) => !o)}
            className="ml-auto rounded-md border px-2 py-0.5 text-xs"
            style={{ borderColor: "var(--border)" }}
          >
            {aberto
              ? t("dossierDetail.chantierFranchiseCancel")
              : semRecepcao
                ? t("dossierDetail.franchiseAddBtn")
                : t("dossierDetail.chantierFranchiseEdit")}
          </button>
        )}
      </p>

      {aberto && podeEditar && (
        <div className="space-y-2 rounded-md border p-3" style={{ borderColor: "var(--border)" }}>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={tem} onChange={(e) => setTem(e.target.checked)} />
            {t("dossierDetail.chantierFranchiseHas")}
          </label>
          {tem && (
            <input
              type="number"
              min="0"
              step="0.01"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder={t("dossierDetail.chantierFranchiseAmountPh")}
              className={inputClass + " font-mono"}
            />
          )}
          {tem && !particular && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={excecaoForm}
                onChange={(e) => setExcecaoForm(e.target.checked)}
                data-testid="franchise-commissions-only"
              />
              <span>
                {t("dossierDetail.franchiseCommissionsOnlyLabel")}
                <span className="block text-[11px]" style={{ color: "var(--text-muted)" }}>
                  {t("dossierDetail.franchiseCommissionsOnlyHint")}
                </span>
              </span>
            </label>
          )}
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={t("dossierDetail.chantierFranchiseReasonPh")}
            className={inputClass}
          />
          {erro && <p className="text-xs text-red-500">{erro}</p>}
          <button
            type="button"
            disabled={
              salvar.isPending ||
              motivo.trim().length < 3 ||
              (tem && (valor.trim() === "" || Number(valor) <= 0))
            }
            onClick={() =>
              salvar.mutate({
                companyId,
                dossierId,
                franchise: tem,
                franchiseAmountTtc: tem ? Number(valor) : null,
                commissionsOnly: tem && !particular ? excecaoForm : false,
                reason: motivo.trim(),
              })
            }
            className="btn btn--primary btn--sm disabled:opacity-40"
          >
            {salvar.isPending
              ? t("dossierDetail.chantierFranchiseSaving")
              : t("dossierDetail.chantierFranchiseSave")}
          </button>
          <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
            {t("dossierDetail.chantierFranchiseHint")}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Cartão do gerente do chantier no dossier (comissão derivada). CEO
 * 2026-10-31: o Super Admin muda quanto o gerente recebe NESTE carro —
 * percentual próprio ou valor fixo (€ HT), com motivo; "Padrão" volta à regra.
 */
function CartaoGerente({
  m,
  companyId,
  dossierId,
  podeEditar,
}: {
  m: {
    userId: number;
    managerName: string;
    chantierName: string;
    particular: boolean;
    pct: number;
    baseHt: number;
    amount: number;
    deliveredAt: string | null;
    defaultPct: number;
    override: {
      mode: "pct" | "fixed";
      value: number;
      reason: string | null;
      setAt: string | null;
    } | null;
  };
  companyId: number;
  dossierId: number;
  podeEditar: boolean;
}) {
  const tr = useT();
  const utils = trpc.useUtils();
  const modals = useModals();
  const [editando, setEditando] = useState(false);
  const [modo, setModo] = useState<"default" | "pct" | "fixed">(m.override?.mode ?? "default");
  const [valor, setValor] = useState(m.override ? String(m.override.value).replace(".", ",") : "");
  const [motivo, setMotivo] = useState(m.override?.reason ?? "");
  const mut = trpc.dossiers.setManagerCommission.useMutation({
    onSuccess: () => {
      void utils.dossiers.managerCommission.invalidate();
      setEditando(false);
    },
    onError: (e) =>
      void modals.alert({
        title: tr("dossierDetail.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const num = Number(valor.replace(",", "."));
  const valido = modo === "default" || (valor.trim() !== "" && Number.isFinite(num) && num >= 0);
  const previa =
    modo === "default"
      ? (m.baseHt * m.defaultPct) / 100
      : modo === "pct"
        ? (m.baseHt * (Number.isFinite(num) ? num : 0)) / 100
        : Number.isFinite(num)
          ? num
          : 0;
  const rotuloPct =
    m.override?.mode === "pct"
      ? tr("dossierDetail.managerPctOverride")
      : m.override?.mode === "fixed"
        ? tr("dossierDetail.managerPctEquiv")
        : m.particular
          ? tr("dossierDetail.managerPctParticular")
          : tr("dossierDetail.managerPctPro");
  const abrir = () => {
    setModo(m.override?.mode ?? "default");
    setValor(m.override ? String(m.override.value).replace(".", ",") : "");
    setMotivo(m.override?.reason ?? "");
    setEditando(true);
  };
  return (
    <div
      className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-2"
      data-testid="manager-commission"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-neutral-500">
          {tr("dossierDetail.managerCardTitle")}
        </span>
        {podeEditar && !editando && (
          <button
            type="button"
            onClick={abrir}
            className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-semibold hover:border-lime"
            data-testid="manager-edit"
          >
            ✎ {tr("dossierDetail.managerEditBtn")}
          </button>
        )}
      </div>
      <h4 className="text-sm font-semibold">
        {m.managerName} <span className="text-neutral-500">· {m.chantierName}</span>
      </h4>
      <div className="grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
        <ValueBlock label={tr("dossierDetail.managerBase")} value={m.baseHt} money />
        <ValueBlock label={rotuloPct} value={m.pct} suffix="%" />
        <ValueBlock label={tr("dossierDetail.managerAmount")} value={m.amount} money />
      </div>
      {m.override && (
        <p
          className="text-[11px]"
          style={{ color: "var(--warning-500)" }}
          data-testid="manager-override"
        >
          ✎ {tr("dossierDetail.managerOverrideLabel")}:{" "}
          {m.override.mode === "fixed"
            ? `${formatMoney(m.override.value, { decimals: 2 })} ${tr("dossierDetail.managerFixedWord")}`
            : `${m.override.value} %`}{" "}
          · {tr("dossierDetail.managerDefaultWas")} {m.defaultPct} %
          {m.override.reason ? ` — ${m.override.reason}` : ""}
        </p>
      )}
      {editando && (
        <div
          className="space-y-3 rounded-md border p-3"
          style={{ borderColor: "var(--border-strong)", background: "var(--surface-muted)" }}
          data-testid="manager-editor"
        >
          <div className="flex flex-wrap gap-4 text-sm">
            {(
              [
                ["default", `${tr("dossierDetail.managerModeDefault")} (${m.defaultPct} %)`],
                ["pct", tr("dossierDetail.managerModePct")],
                ["fixed", tr("dossierDetail.managerModeFixed")],
              ] as const
            ).map(([v, label]) => (
              <label key={v} className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="radio"
                  name={`gerente-modo-${m.userId}`}
                  checked={modo === v}
                  onChange={() => setModo(v)}
                  className="accent-lime"
                  data-testid={`manager-mode-${v}`}
                />
                {label}
              </label>
            ))}
          </div>
          {modo !== "default" && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                inputMode="decimal"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder={modo === "pct" ? "10" : "150,00"}
                className="h-9 w-32 rounded-lg border border-[var(--border-strong)] bg-[var(--surface-page)] px-3 text-sm text-[var(--text-strong)] outline-none focus:border-lime"
                data-testid="manager-value"
              />
              <span className="text-sm" style={{ color: "var(--text-muted)" }}>
                {modo === "pct" ? "%" : "€ HT"}
              </span>
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder={tr("dossierDetail.managerReasonPh")}
                className="h-9 min-w-[220px] flex-1 rounded-lg border border-[var(--border-strong)] bg-[var(--surface-page)] px-3 text-sm text-[var(--text-strong)] outline-none focus:border-lime"
                data-testid="manager-reason"
              />
            </div>
          )}
          <p className="text-xs" style={{ color: "var(--text-body)" }}>
            {tr("dossierDetail.managerPreview")}:{" "}
            <b style={{ color: "var(--text-strong)" }}>{formatMoney(previa, { decimals: 2 })}</b>
            <span style={{ color: "var(--text-muted)" }}>
              {" "}
              · {tr("dossierDetail.managerEditHint")}
            </span>
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditando(false)}
              className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs"
            >
              {tr("dossierDetail.managerCancelBtn")}
            </button>
            <button
              type="button"
              disabled={!valido || mut.isPending}
              onClick={() =>
                mut.mutate({
                  companyId,
                  dossierId,
                  mode: modo,
                  value: modo === "default" ? undefined : num,
                  reason: modo === "default" ? undefined : motivo.trim(),
                })
              }
              className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
              data-testid="manager-save"
            >
              {mut.isPending ? "…" : tr("dossierDetail.managerSaveBtn")}
            </button>
          </div>
        </div>
      )}
      <p className="text-[11px] text-neutral-500">{tr("dossierDetail.managerHint")}</p>
      {/* F-gerente-entrega — conta no mês do registro da entrega. */}
      {m.deliveredAt ? (
        <p className="text-[11px] text-lime">
          ✓ {tr("dossierDetail.managerDeliveredPre")} {m.deliveredAt.split("-").reverse().join("/")}
        </p>
      ) : (
        <p className="text-[11px] text-amber-300">⚠ {tr("dossierDetail.managerNotDelivered")}</p>
      )}
    </div>
  );
}
